import React, { FormEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, type Appearance, type CustomFontSource } from '@stripe/stripe-js';
import {
  ApiError,
  SAFE_REQUEST_ERROR_MESSAGE,
  cancelCustomerReturnPickup,
  cancelReturnPickupWithManagementToken,
  confirmCheckout,
  getCheckoutStatus,
  getCustomerReturn,
  getCustomerReturns,
  getPickupQuote,
  getPickupQuoteById,
  getReturnWithManagementToken,
  getSupportReturn,
  getSupportReturns,
  sendSupportMessage,
  startCheckout,
} from './api';
import { trackEvent, trackPage, trackRouteView } from './analytics';
import { useReturnemAuth } from './auth';
import { TurnstileWidget } from './TurnstileWidget';
import {
  Address,
  BookingState,
  CarrierType,
  CheckoutQuote,
  CheckoutQuoteLineItem,
  CustomerReturnSummary,
  PickupQuoteRequest,
  PickupQuoteResponse,
  PickupQuoteSnapshotResponse,
  CheckoutStartRequest,
  CheckoutStartResponse,
  CheckoutStatusResponse,
  PackageInfo,
  SupportReturnSummary,
} from './types';
import poppins400Latin from './assets/fonts/poppins/poppins-400-latin.woff2';
import poppins500Latin from './assets/fonts/poppins/poppins-500-latin.woff2';
import poppins600Latin from './assets/fonts/poppins/poppins-600-latin.woff2';
import poppins700Latin from './assets/fonts/poppins/poppins-700-latin.woff2';
import './App.css';

const DEFAULT_READY_TIME = '09:00';
const DEFAULT_CLOSE_TIME = '17:00';
const DEFAULT_SUPPORT_EMAIL = 'support@returnem.com';
const SUPPORT_RETURNS_LIMIT = 100;
const DEFAULT_PRIVACY_EMAIL = 'privacy@returnem.com';
const DEFAULT_SUPPORT_HOURS = 'Support hours: Monday-Friday, 9:00 AM-5:00 PM ET';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY ?? '';
const CONFIRMATION_SNAPSHOT_STORAGE_KEY_PREFIX = 'returnem-confirmation-snapshot:';
const INTAKE_PROGRESS_STORAGE_KEY = 'returnem-intake-progress';
const ACTIVE_QUOTE_ID_STORAGE_KEY = 'returnem-active-quote-id';
const PENDING_RETURN_DETAILS_STORAGE_KEY = 'returnem-pending-return-details';
const CONFIRMATION_WAIT_STARTED_STORAGE_KEY_PREFIX = 'returnem-confirmation-wait-started:';
const RETURN_MANAGEMENT_TOKEN_STORAGE_KEY_PREFIX = 'returnem-return-management-token:';
const CHECKOUT_FLOW_HISTORY_STATE_KEY = 'returnemCheckoutStep';
const GUEST_PICKUP_ADDRESS_STORAGE_KEY = 'returnem-guest-pickup-address';
export const USPS_WEEKEND_PICKUP_WARNING =
  'USPS pickups are not available on weekends. Choose a weekday pickup date.';
const CARRIERS: CarrierType[] = ['FEDEX', 'UPS', 'USPS'];
const ACTIVE_CONFIRMATION_WAIT_MS = 60_000;
const CONFIRMATION_INITIAL_POLL_MS = 1_250;
const CONFIRMATION_STANDARD_POLL_MS = 2_500;
const CONFIRMATION_DELAYED_POLL_MS = 5_000;
const CONFIRMATION_HIDDEN_POLL_MS = 15_000;
const CONFIRMATION_MAX_ERROR_POLL_MS = 15_000;

const CARRIER_DISPLAY_LABEL: Record<CarrierType, string> = {
  UPS: 'UPS',
  USPS: 'USPS',
  FEDEX: 'FedEx',
};

function getCarrierSummaryLabel(carriers: Array<CarrierType | undefined>): string {
  const uniqueCarriers = Array.from(new Set(carriers.filter((carrier): carrier is CarrierType => Boolean(carrier))));

  if (uniqueCarriers.length === 0) {
    return '';
  }

  if (uniqueCarriers.length === 1) {
    return CARRIER_DISPLAY_LABEL[uniqueCarriers[0]];
  }

  return 'Multiple carriers';
}

function getQuoteCarrierSummaryLabel(quote: CheckoutQuote | null | undefined, fallbackCarrier: CarrierType): string {
  return getCarrierSummaryLabel(quote?.lineItems?.map((lineItem) => lineItem.carrier) ?? []) || CARRIER_DISPLAY_LABEL[quote?.carrier ?? fallbackCarrier];
}

function getReturnCarrierSummaryLabel(returnSummary: {
  carrier: CarrierType;
  carrierPickups?: Array<{ carrier: CarrierType }>;
}): string {
  return getCarrierSummaryLabel(returnSummary.carrierPickups?.map((pickup) => pickup.carrier) ?? []) || CARRIER_DISPLAY_LABEL[returnSummary.carrier];
}

export function getReviewShippingMethodLabel(
  hasPrepaidLabel: boolean,
  carrier: CarrierType,
  shippingLabelChargeLabel?: string,
): string {
  if (hasPrepaidLabel) {
    return CARRIER_DISPLAY_LABEL[carrier];
  }

  return shippingLabelChargeLabel?.replace(/\s+label$/i, '') || CARRIER_DISPLAY_LABEL[carrier];
}

export function isWeekendPickupDate(pickupDate: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(pickupDate);
  if (!match) {
    return false;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsedDate = new Date(Date.UTC(year, month - 1, day));
  const isValidDate =
    parsedDate.getUTCFullYear() === year &&
    parsedDate.getUTCMonth() === month - 1 &&
    parsedDate.getUTCDate() === day;

  if (!isValidDate) {
    return false;
  }

  return parsedDate.getUTCDay() === 0 || parsedDate.getUTCDay() === 6;
}

export function getUspsWeekendPickupWarning(
  form: Pick<FormState, 'pickupDate' | 'packageCards'>,
  quote?: CheckoutQuote | null,
): string {
  if (!isWeekendPickupDate(form.pickupDate)) {
    return '';
  }

  const hasSelectedUspsPackage = form.packageCards.some(
    (packageCard) => packageCard.hasPrepaidLabel && packageCard.carrier === 'USPS',
  );
  const quoteUsesUsps =
    quote?.carrier === 'USPS' || quote?.lineItems?.some((lineItem) => lineItem.carrier === 'USPS') === true;

  return hasSelectedUspsPackage || quoteUsesUsps ? USPS_WEEKEND_PICKUP_WARNING : '';
}

const CARRIER_STYLE_CLASS: Record<CarrierType, string> = {
  FEDEX: 'carrier-fedex',
  UPS: 'carrier-ups',
  USPS: 'carrier-usps',
};

const CHECKOUT_STEPS = ['Pickup', 'Review & Pay', 'Confirmation'];
const CONFIRMATION_GENERIC_MESSAGES = [
  'Gathering your pickup details...',
  'Checking availability with your carrier...',
  'Putting the final touches on your booking...',
  'Almost done - thanks for hanging tight.',
];
const BOOKING_STATE_SUMMARY: Record<BookingState, string> = {
  QUOTED: 'The quote exists, but the carrier booking has not been completed yet.',
  PAYMENT_AUTH_PENDING: 'Payment is still being confirmed before the pickup can be booked.',
  PAYMENT_AUTHORIZED: 'Payment is approved and the pickup request is moving through booking.',
  SCHEDULING_IN_PROGRESS: 'The carrier is confirming the pickup behind the scenes.',
  SCHEDULED: 'The pickup is booked with the carrier and ready for the scheduled day.',
  CAPTURED: 'The pickup is booked and payment has been captured successfully.',
  FAILED_PAYMENT: 'Payment did not complete, so the pickup was not booked.',
  FAILED_SCHEDULING: 'The carrier could not confirm this pickup request.',
  QUOTE_EXPIRED: 'The quote expired before this booking could be completed.',
  CANCELLED_AUTH: 'The payment authorization was canceled before the pickup was booked.',
  CANCELLED_PICKUP: 'The scheduled pickup was canceled successfully.',
};

function normalizePublishableKey(rawValue: string | undefined): string | null {
  if (!rawValue) {
    return null;
  }

  const normalized = rawValue.trim().replace(/^['"]+|['"]+$/g, '');
  return normalized || null;
}

const stripePublishableKey = normalizePublishableKey(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const googleMapsApiKey = normalizePublishableKey(process.env.REACT_APP_GOOGLE_MAPS_API_KEY);

const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;
const STRIPE_ELEMENTS_FONT_FAMILY = 'Poppins, "Segoe UI", Tahoma, Geneva, Verdana, sans-serif';
const stripeElementsFonts: CustomFontSource[] = [
  { family: 'Poppins', src: `url(${poppins400Latin}) format("woff2")`, weight: '400', display: 'swap' },
  { family: 'Poppins', src: `url(${poppins500Latin}) format("woff2")`, weight: '500', display: 'swap' },
  { family: 'Poppins', src: `url(${poppins600Latin}) format("woff2")`, weight: '600', display: 'swap' },
  { family: 'Poppins', src: `url(${poppins700Latin}) format("woff2")`, weight: '700', display: 'swap' },
];
const stripeElementsAppearance: Appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#ff6900',
    borderRadius: '16px',
    colorText: '#101828',
    fontFamily: STRIPE_ELEMENTS_FONT_FAMILY,
  },
};
let googlePlacesScriptPromise: Promise<void> | null = null;
let formEntityCounter = 0;
const reservedFormEntityIds = new Set<string>();

type CheckoutStep = 'form' | 'review';
type IntakeStep = 1 | 2;
type CheckoutFlowHistoryStep = 'pickup' | 'packages' | 'review';
type SignInStage = 'email' | 'code';
type AccountReturnSummary = CustomerReturnSummary | SupportReturnSummary;

type AppRoute =
  | { kind: 'checkout' }
  | { kind: 'managePickup' }
  | { kind: 'signIn' }
  | { kind: 'accountReturns' }
  | { kind: 'accountReturn'; returnId: string }
  | { kind: 'sellDonate' }
  | { kind: 'howItWorks' }
  | { kind: 'help' }
  | { kind: 'refundPolicy' }
  | { kind: 'privacyPolicy' }
  | { kind: 'confirmation'; returnId: string };

interface PackageCardState {
  id: string;
  packageNote: string;
  carrier: CarrierType;
  weightLbs: string;
  lengthIn: string;
  widthIn: string;
  heightIn: string;
  hasPrepaidLabel: boolean;
  destinationAddressSearch: string;
  destinationLine1: string;
  destinationLine2: string;
  destinationCity: string;
  destinationState: string;
  destinationPostalCode: string;
}

interface FormState {
  addressSearch: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  readyTime: string;
  closeTime: string;
  carrier: CarrierType;
  pickupDate: string;
  email: string;
  phone: string;
  residential: boolean;
  packageCards: PackageCardState[];
}

interface GuestPickupInfoSnapshot {
  addressSearch: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  email: string;
  phone: string;
  residential: boolean;
}

interface IntakeProgressSnapshot {
  form: FormState;
  intakeStep: IntakeStep;
  expandedPackageId: string;
}

interface PaymentCardFormProps {
  email: string;
  phone: string;
  returnUrl: string;
  onPaymentConfirmed: () => void;
  showTopRow?: boolean;
  submitLabel?: string;
  showFieldLabel?: boolean;
  footnoteText?: string | null;
  disabled?: boolean;
}

type PaymentConfirmationResult = { status: 'confirmed' } | { status: 'failed'; message: string };

interface StepProgressProps {
  currentStep: number;
}

interface SkeletonLinesProps {
  rows?: number;
}

type HomeFeatureKey =
  | 'wardrobe'
  | 'holiday'
  | 'value'
  | 'convenient'
  | 'sustainable'
  | 'economical'
  | 'easy';
type HomeBannerItemKey = 'package' | 'shoppingBag' | 'shirt' | 'hanger' | 'smartphone' | 'bookOpen' | 'watch';
type SellDonateOptionKey = 'donate' | 'resell';
type HelpTopicKey = 'pickup' | 'tracking' | 'payment' | 'reschedule' | 'issues';

interface HelpFormState {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const HELP_SUPPORT_HOURS = '9:00 AM - 5:00 PM ET';

const HELP_FAQS: Array<{
  id: string;
  topic: HelpTopicKey;
  question: string;
  answer: string;
}> = [
  {
    id: 'label-help',
    topic: 'pickup',
    question: "What if I don't already have a return label?",
    answer:
      'Choose "No, I need a label" in Package details. Enter the return destination, and we will show any eligible label charge before you pay.',
  },
  {
    id: 'pickup-window',
    topic: 'reschedule',
    question: 'When can my pickup happen?',
    answer:
      'Pickups are scheduled for the next available day or later, and every pickup window is between 9 AM and 5 PM local time.',
  },
  {
    id: 'bundle-pricing',
    topic: 'payment',
    question: 'How is pricing calculated for multiple packages?',
    answer:
      'Carrier pickup charges depend on the carriers in your order. ReturnEm adds one flat order fee, and any label costs are shown separately for each package on the review screen.',
  },
  {
    id: 'manage-return',
    topic: 'tracking',
    question: 'How do I check status or cancel a booked return?',
    answer:
      'Sign in to My Returns to see the latest status. Eligible pickups can also be canceled from the details screen.',
  },
  {
    id: 'package-ready',
    topic: 'pickup',
    question: 'Does my package need to be ready before pickup?',
    answer:
      'Yes. Packages should be sealed and ready for the carrier. If you already have a prepaid label, attach it before pickup. If you request a label during checkout, make sure the package is packed and accessible.',
  },
  {
    id: 'carrier-issue',
    topic: 'issues',
    question: 'What if the carrier misses the pickup or there is an issue?',
    answer:
      'Contact support with your Return ID. We will review the pickup and any eligible unused-label refund.',
  },
];

const HELP_SUBJECT_OPTIONS = [
  'Scheduling a pickup',
  'Return status',
  'Payment & refunds',
  'Canceling a pickup',
  'Common issues',
];

function createFormEntityId(prefix: string): string {
  let candidate = '';

  do {
    formEntityCounter += 1;
    candidate = `${prefix}-${formEntityCounter}`;
  } while (reservedFormEntityIds.has(candidate));

  reservedFormEntityIds.add(candidate);
  return candidate;
}

function createPackageCardState(packageNumber = 1, carrier: CarrierType = 'FEDEX'): PackageCardState {
  return {
    id: createFormEntityId('package'),
    packageNote: `Package ${packageNumber}`,
    carrier,
    weightLbs: '',
    lengthIn: '',
    widthIn: '',
    heightIn: '',
    hasPrepaidLabel: true,
    destinationAddressSearch: '',
    destinationLine1: '',
    destinationLine2: '',
    destinationCity: '',
    destinationState: '',
    destinationPostalCode: '',
  };
}

function createSystemCheckoutEmail(): string {
  return `pickup-${Date.now()}-${Math.random().toString(36).slice(2, 8)}@returnem.invalid`;
}

export function createInitialFormState(): FormState {
  return {
    addressSearch: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
    readyTime: DEFAULT_READY_TIME,
    closeTime: DEFAULT_CLOSE_TIME,
    carrier: 'FEDEX',
    pickupDate: getNextEligiblePickupDate(),
    email: '',
    phone: '',
    residential: true,
    packageCards: [createPackageCardState(1)],
  };
}

function sanitizeGuestPickupInfoSnapshot(rawValue: unknown): GuestPickupInfoSnapshot | null {
  const rawAddress = rawValue && typeof rawValue === 'object' ? (rawValue as Record<string, unknown>) : null;

  if (!rawAddress) {
    return null;
  }

  const snapshot = {
    addressSearch: typeof rawAddress.addressSearch === 'string' ? rawAddress.addressSearch.trim() : '',
    line1: typeof rawAddress.line1 === 'string' ? rawAddress.line1.trim() : '',
    line2: typeof rawAddress.line2 === 'string' ? rawAddress.line2.trim() : '',
    city: typeof rawAddress.city === 'string' ? rawAddress.city.trim() : '',
    state: typeof rawAddress.state === 'string' ? rawAddress.state.trim().toUpperCase() : '',
    postalCode: typeof rawAddress.postalCode === 'string' ? rawAddress.postalCode.trim() : '',
    email: typeof rawAddress.email === 'string' ? rawAddress.email.trim() : '',
    phone: typeof rawAddress.phone === 'string' ? rawAddress.phone.trim() : '',
    residential: rawAddress.residential !== false,
  };

  if (!snapshot.addressSearch || !snapshot.line1 || !snapshot.city || !snapshot.state || !snapshot.postalCode) {
    return null;
  }

  return snapshot;
}

export function readGuestPickupInfoSnapshot(): GuestPickupInfoSnapshot | null {
  try {
    const rawValue = window.localStorage.getItem(GUEST_PICKUP_ADDRESS_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    return sanitizeGuestPickupInfoSnapshot(JSON.parse(rawValue));
  } catch {
    return null;
  }
}

function applyGuestPickupInfoSnapshot(form: FormState, snapshot: GuestPickupInfoSnapshot | null): FormState {
  if (!snapshot) {
    return form;
  }

  return {
    ...form,
    addressSearch: snapshot.addressSearch,
    line1: snapshot.line1,
    line2: snapshot.line2,
    city: snapshot.city,
    state: snapshot.state,
    postalCode: snapshot.postalCode,
    email: snapshot.email,
    phone: snapshot.phone,
    residential: snapshot.residential,
  };
}

function createGuestPickupInfoSnapshot(form: FormState): GuestPickupInfoSnapshot | null {
  return sanitizeGuestPickupInfoSnapshot({
    addressSearch: form.addressSearch,
    line1: form.line1,
    line2: form.line2,
    city: form.city,
    state: form.state,
    postalCode: form.postalCode,
    email: form.email,
    phone: form.phone,
    residential: form.residential,
  });
}

export function saveGuestPickupInfoSnapshot(form: FormState): void {
  const snapshot = createGuestPickupInfoSnapshot(form);

  if (!snapshot) {
    return;
  }

  try {
    window.localStorage.setItem(GUEST_PICKUP_ADDRESS_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Ignore storage failures and keep the form usable.
  }
}

function clearGuestPickupInfoSnapshot(): void {
  try {
    window.localStorage.removeItem(GUEST_PICKUP_ADDRESS_STORAGE_KEY);
  } catch {
    // Ignore storage failures and keep the form usable.
  }
}

function resolveReturnId(value: { returnId?: string; bookingId?: string }): string {
  return value.returnId ?? value.bookingId ?? '';
}

export function readActiveQuoteId(): string {
  try {
    return window.sessionStorage.getItem(ACTIVE_QUOTE_ID_STORAGE_KEY)?.trim() ?? '';
  } catch {
    return '';
  }
}

export function saveActiveQuoteId(quoteId: string): void {
  try {
    window.sessionStorage.setItem(ACTIVE_QUOTE_ID_STORAGE_KEY, quoteId);
  } catch {
    // Ignore storage failures; the current in-memory checkout remains usable.
  }
}

export function clearActiveQuoteId(): void {
  try {
    window.sessionStorage.removeItem(ACTIVE_QUOTE_ID_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function getConfirmationWaitStartedStorageKey(returnId: string): string {
  return `${CONFIRMATION_WAIT_STARTED_STORAGE_KEY_PREFIX}${returnId}`;
}

function readOrCreateConfirmationWaitStartedAt(returnId: string): number {
  const now = Date.now();
  try {
    const key = getConfirmationWaitStartedStorageKey(returnId);
    const stored = Number(window.sessionStorage.getItem(key));
    if (Number.isFinite(stored) && stored > 0 && stored <= now) {
      return stored;
    }
    window.sessionStorage.setItem(key, String(now));
  } catch {
    // Use the current page-load time when session storage is unavailable.
  }
  return now;
}

function clearConfirmationWaitStartedAt(returnId: string): void {
  try {
    window.sessionStorage.removeItem(getConfirmationWaitStartedStorageKey(returnId));
  } catch {
    // Ignore storage failures.
  }
}

function readPendingReturnDetailsId(): string {
  try {
    return window.sessionStorage.getItem(PENDING_RETURN_DETAILS_STORAGE_KEY)?.trim() ?? '';
  } catch {
    return '';
  }
}

function savePendingReturnDetailsId(returnId: string): void {
  try {
    window.sessionStorage.setItem(PENDING_RETURN_DETAILS_STORAGE_KEY, returnId);
  } catch {
    // Ignore storage failures; signed-in customers can still open the route directly.
  }
}

function clearPendingReturnDetailsId(): void {
  try {
    window.sessionStorage.removeItem(PENDING_RETURN_DETAILS_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function getReturnManagementTokenStorageKey(returnId: string): string {
  return `${RETURN_MANAGEMENT_TOKEN_STORAGE_KEY_PREFIX}${returnId}`;
}

function readStoredReturnManagementToken(returnId: string): string {
  try {
    return window.sessionStorage.getItem(getReturnManagementTokenStorageKey(returnId))?.trim() ?? '';
  } catch {
    return '';
  }
}

function saveReturnManagementToken(returnId: string, token: string): void {
  try {
    window.sessionStorage.setItem(getReturnManagementTokenStorageKey(returnId), token);
  } catch {
    // Ignore storage failures; the current page can still use the in-memory credential.
  }
}

function clearReturnManagementToken(returnId: string): void {
  try {
    window.sessionStorage.removeItem(getReturnManagementTokenStorageKey(returnId));
  } catch {
    // Ignore storage failures.
  }
}

function consumeReturnManagementToken(returnId: string): string {
  const hash = window.location.hash.replace(/^#/, '');
  const hashParams = new URLSearchParams(hash);
  const tokenFromLink = hashParams.get('manage')?.trim() ?? '';

  if (!tokenFromLink) {
    return readStoredReturnManagementToken(returnId);
  }

  saveReturnManagementToken(returnId, tokenFromLink);
  hashParams.delete('manage');
  const nextHash = hashParams.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${window.location.search}${nextHash ? `#${nextHash}` : ''}`,
  );
  return tokenFromLink;
}

function getConfirmationSnapshotStorageKey(returnId: string): string {
  return `${CONFIRMATION_SNAPSHOT_STORAGE_KEY_PREFIX}${returnId}`;
}

function sanitizePackageCardSnapshot(rawValue: unknown, fallbackCarrier: CarrierType = 'FEDEX'): PackageCardState {
  const rawPackageCard = rawValue && typeof rawValue === 'object' ? (rawValue as Record<string, unknown>) : {};
  const restoredId = typeof rawPackageCard.id === 'string' ? rawPackageCard.id : createFormEntityId('package');

  reservedFormEntityIds.add(restoredId);

  return {
    id: restoredId,
    packageNote: typeof rawPackageCard.packageNote === 'string' ? rawPackageCard.packageNote : '',
    carrier:
      rawPackageCard.carrier === 'UPS' || rawPackageCard.carrier === 'USPS' || rawPackageCard.carrier === 'FEDEX'
        ? rawPackageCard.carrier
        : fallbackCarrier,
    weightLbs: typeof rawPackageCard.weightLbs === 'string' ? rawPackageCard.weightLbs : '',
    lengthIn: typeof rawPackageCard.lengthIn === 'string' ? rawPackageCard.lengthIn : '',
    widthIn: typeof rawPackageCard.widthIn === 'string' ? rawPackageCard.widthIn : '',
    heightIn: typeof rawPackageCard.heightIn === 'string' ? rawPackageCard.heightIn : '',
    hasPrepaidLabel: rawPackageCard.hasPrepaidLabel !== false,
    destinationAddressSearch:
      typeof rawPackageCard.destinationAddressSearch === 'string' ? rawPackageCard.destinationAddressSearch : '',
    destinationLine1: typeof rawPackageCard.destinationLine1 === 'string' ? rawPackageCard.destinationLine1 : '',
    destinationLine2: typeof rawPackageCard.destinationLine2 === 'string' ? rawPackageCard.destinationLine2 : '',
    destinationCity: typeof rawPackageCard.destinationCity === 'string' ? rawPackageCard.destinationCity : '',
    destinationState: typeof rawPackageCard.destinationState === 'string' ? rawPackageCard.destinationState : '',
    destinationPostalCode:
      typeof rawPackageCard.destinationPostalCode === 'string' ? rawPackageCard.destinationPostalCode : '',
  };
}

function sanitizeFormSnapshot(rawValue: unknown): FormState | null {
  const rawForm = rawValue && typeof rawValue === 'object' ? (rawValue as Record<string, unknown>) : null;

  if (!rawForm || !Array.isArray(rawForm.packageCards)) {
    return null;
  }

  return {
    addressSearch: typeof rawForm.addressSearch === 'string' ? rawForm.addressSearch : '',
    line1: typeof rawForm.line1 === 'string' ? rawForm.line1 : '',
    line2: typeof rawForm.line2 === 'string' ? rawForm.line2 : '',
    city: typeof rawForm.city === 'string' ? rawForm.city : '',
    state: typeof rawForm.state === 'string' ? rawForm.state : '',
    postalCode: typeof rawForm.postalCode === 'string' ? rawForm.postalCode : '',
    readyTime: typeof rawForm.readyTime === 'string' ? rawForm.readyTime : DEFAULT_READY_TIME,
    closeTime: typeof rawForm.closeTime === 'string' ? rawForm.closeTime : DEFAULT_CLOSE_TIME,
    carrier: rawForm.carrier === 'UPS' || rawForm.carrier === 'USPS' || rawForm.carrier === 'FEDEX' ? rawForm.carrier : 'FEDEX',
    pickupDate: typeof rawForm.pickupDate === 'string' ? rawForm.pickupDate : getNextEligiblePickupDate(),
    email: typeof rawForm.email === 'string' ? rawForm.email : createSystemCheckoutEmail(),
    phone: typeof rawForm.phone === 'string' ? rawForm.phone : '',
    residential: Boolean(rawForm.residential),
    packageCards: rawForm.packageCards.map((packageCard) => sanitizePackageCardSnapshot(packageCard, rawForm.carrier === 'UPS' || rawForm.carrier === 'USPS' || rawForm.carrier === 'FEDEX' ? rawForm.carrier : 'FEDEX')),
  };
}

function sanitizeIntakeProgressSnapshot(rawValue: unknown): IntakeProgressSnapshot | null {
  const rawSnapshot = rawValue && typeof rawValue === 'object' ? (rawValue as Record<string, unknown>) : null;
  const form = rawSnapshot ? sanitizeFormSnapshot(rawSnapshot.form) : null;

  if (!rawSnapshot || !form) {
    return null;
  }

  const savedExpandedPackageId =
    typeof rawSnapshot.expandedPackageId === 'string' ? rawSnapshot.expandedPackageId : null;
  const expandedPackageId =
    savedExpandedPackageId === '' ||
    (savedExpandedPackageId !== null &&
      form.packageCards.some((packageCard) => packageCard.id === savedExpandedPackageId))
      ? savedExpandedPackageId
      : form.packageCards[form.packageCards.length - 1]?.id ?? '';

  return {
    form,
    intakeStep: rawSnapshot.intakeStep === 2 ? 2 : 1,
    expandedPackageId,
  };
}

export function saveIntakeProgressSnapshot(
  form: FormState,
  intakeStep: IntakeStep,
  expandedPackageId: string,
): void {
  try {
    window.sessionStorage.setItem(
      INTAKE_PROGRESS_STORAGE_KEY,
      JSON.stringify({
        form,
        intakeStep,
        expandedPackageId,
      }),
    );
  } catch {
    // Ignore storage failures and keep the form usable.
  }
}

export function readIntakeProgressSnapshot(): IntakeProgressSnapshot | null {
  try {
    const rawValue = window.sessionStorage.getItem(INTAKE_PROGRESS_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    return sanitizeIntakeProgressSnapshot(JSON.parse(rawValue));
  } catch {
    return null;
  }
}

function clearIntakeProgressSnapshot(): void {
  try {
    window.sessionStorage.removeItem(INTAKE_PROGRESS_STORAGE_KEY);
  } catch {
    // Ignore storage failures and keep the flow usable.
  }
}

function readCheckoutFlowHistoryStep(state: unknown = window.history.state): CheckoutFlowHistoryStep | null {
  const historyState = state && typeof state === 'object' ? (state as Record<string, unknown>) : null;
  const step = historyState?.[CHECKOUT_FLOW_HISTORY_STATE_KEY];

  return step === 'pickup' || step === 'packages' || step === 'review' ? step : null;
}

function createCheckoutFlowHistoryState(step: CheckoutFlowHistoryStep): Record<string, unknown> {
  const currentState =
    window.history.state && typeof window.history.state === 'object'
      ? (window.history.state as Record<string, unknown>)
      : {};

  return {
    ...currentState,
    [CHECKOUT_FLOW_HISTORY_STATE_KEY]: step,
  };
}

function replaceCheckoutFlowHistoryStep(step: CheckoutFlowHistoryStep): void {
  window.history.replaceState(createCheckoutFlowHistoryState(step), '', window.location.href);
}

function pushCheckoutFlowHistoryStep(step: CheckoutFlowHistoryStep): void {
  if (readCheckoutFlowHistoryStep() === step) {
    return;
  }

  window.history.pushState(createCheckoutFlowHistoryState(step), '', window.location.href);
}

function saveConfirmationSnapshot(returnId: string, form: FormState): void {
  try {
    window.sessionStorage.setItem(getConfirmationSnapshotStorageKey(returnId), JSON.stringify(form));
  } catch {
    // Ignore storage failures and fall back to in-memory state.
  }
}

function readConfirmationSnapshot(returnId: string): FormState | null {
  try {
    const rawValue = window.sessionStorage.getItem(getConfirmationSnapshotStorageKey(returnId));
    if (!rawValue) {
      return null;
    }

    return sanitizeFormSnapshot(JSON.parse(rawValue));
  } catch {
    return null;
  }
}

function getTodayDate(): string {
  const now = new Date();
  const tzAdjusted = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return tzAdjusted.toISOString().split('T')[0];
}

function getNextEligiblePickupDate(): string {
  const now = new Date();
  now.setDate(now.getDate() + 1);
  const tzAdjusted = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return tzAdjusted.toISOString().split('T')[0];
}

function normalizeEmailAddress(email: string): string {
  return email.trim().toLowerCase();
}

function isSupportAccountEmail(email: string): boolean {
  return normalizeEmailAddress(email) === DEFAULT_SUPPORT_EMAIL;
}

function isSupportReturnSummary(returnSummary: AccountReturnSummary): returnSummary is SupportReturnSummary {
  return 'customerEmail' in returnSummary;
}

function parseRoute(pathname: string): AppRoute {
  if (pathname === '/manage-return' || pathname === '/manage-pickup') {
    return { kind: 'managePickup' };
  }

  if (pathname === '/sign-in') {
    return { kind: 'signIn' };
  }

  if (pathname === '/returns') {
    return { kind: 'accountReturns' };
  }

  const accountReturnMatch = pathname.match(/^\/returns\/([^/]+)$/);
  if (accountReturnMatch) {
    return { kind: 'accountReturn', returnId: decodeURIComponent(accountReturnMatch[1]) };
  }

  if (pathname === '/sell-or-donate') {
    return { kind: 'checkout' };
  }

  if (pathname === '/how-it-works') {
    return { kind: 'howItWorks' };
  }

  if (pathname === '/help') {
    return { kind: 'help' };
  }

   if (pathname === '/refund-policy') {
    return { kind: 'refundPolicy' };
  }

  if (pathname === '/privacy' || pathname === '/privacy-policy') {
    return { kind: 'privacyPolicy' };
  }

  if (/^\/receipt\/[^/]+$/.test(pathname)) {
    return { kind: 'signIn' };
  }

  const confirmationMatch = pathname.match(/^\/confirmation\/([^/]+)$/);
  if (confirmationMatch) {
    return { kind: 'confirmation', returnId: decodeURIComponent(confirmationMatch[1]) };
  }

  return { kind: 'checkout' };
}

function getAnalyticsPath(route: AppRoute): string {
  switch (route.kind) {
    case 'managePickup':
      return '/manage-return';
    case 'signIn':
      return '/sign-in';
    case 'accountReturns':
      return '/returns';
    case 'accountReturn':
      return '/returns/:returnId';
    case 'sellDonate':
      return '/sell-or-donate';
    case 'howItWorks':
      return '/how-it-works';
    case 'help':
      return '/help';
    case 'refundPolicy':
      return '/refund-policy';
    case 'privacyPolicy':
      return '/privacy';
    case 'confirmation':
      return '/confirmation/:returnId';
    case 'checkout':
    default:
      return '/';
  }
}

function isTerminalState(state: BookingState): boolean {
  return (
    state === 'CAPTURED' ||
    state === 'SCHEDULED' ||
    state === 'FAILED_PAYMENT' ||
    state === 'FAILED_SCHEDULING' ||
    state === 'QUOTE_EXPIRED' ||
    state === 'CANCELLED_AUTH' ||
    state === 'CANCELLED_PICKUP'
  );
}

function isCustomerReturnsListState(state: BookingState): boolean {
  return state === 'CAPTURED' || state === 'SCHEDULED' || state === 'CANCELLED_PICKUP';
}

function isAccountReturnProblemState(state: BookingState): boolean {
  return (
    state === 'CANCELLED_AUTH' ||
    state === 'CANCELLED_PICKUP' ||
    state === 'FAILED_PAYMENT' ||
    state === 'FAILED_SCHEDULING' ||
    state === 'QUOTE_EXPIRED'
  );
}

function isSuccessState(state: BookingState): boolean {
  return state === 'CAPTURED' || state === 'SCHEDULED';
}

function canRequestCancellation(status: CheckoutStatusResponse): boolean {
  return isSuccessState(status.state) && status.pickupDate > getTodayDate();
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatDate(dateString: string): string {
  const date = new Date(`${dateString}T00:00:00`);
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
}

function formatLongDate(dateString: string): string {
  const date = new Date(`${dateString}T00:00:00`);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function formatClockTime(timeValue: string): string {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(timeValue);
  if (!match) {
    return timeValue;
  }

  const date = new Date(`1970-01-01T${timeValue}:00`);
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

function formatCityStatePostal(city: string, state: string, postalCode: string): string {
  const trimmedCity = city.trim();
  const trimmedState = state.trim().toUpperCase();
  const trimmedPostalCode = postalCode.trim();
  const cityState = [trimmedCity, trimmedState].filter(Boolean).join(', ');
  return [cityState, trimmedPostalCode].filter(Boolean).join(' ');
}

function formatAddressLines(address?: Address | null): string[] {
  if (!address) {
    return [];
  }

  return [
    address.line1.trim(),
    address.line2?.trim() ?? '',
    formatCityStatePostal(address.city, address.state, address.postalCode),
  ].filter((line): line is string => Boolean(line));
}

function formatMeasurement(value: number): string {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(value);
}

function isSystemCheckoutEmail(email: string): boolean {
  return email.trim().endsWith('@returnem.invalid');
}

function toMinutes(timeValue: string): number | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(timeValue);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours * 60 + minutes;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.payload?.correlationId) {
      return `${error.message} (Ref: ${error.payload.correlationId})`;
    }
    return error.message;
  }

  return SAFE_REQUEST_ERROR_MESSAGE;
}

export function waitForGooglePlaces(timeoutMs = 10_000, pollIntervalMs = 50): Promise<void> {
  const startedAt = Date.now();

  return new Promise<void>((resolve, reject) => {
    const checkReady = (): void => {
      if ((window as any).google?.maps?.places?.Autocomplete) {
        resolve();
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error('Google Places did not become ready.'));
        return;
      }

      window.setTimeout(checkReady, pollIntervalMs);
    };

    checkReady();
  });
}

export function loadGooglePlacesScript(apiKey: string): Promise<void> {
  if ((window as any).google?.maps?.places?.Autocomplete) {
    return Promise.resolve();
  }

  if (googlePlacesScriptPromise) {
    return googlePlacesScriptPromise;
  }

  googlePlacesScriptPromise = new Promise<void>((resolve, reject) => {
    const scriptId = 'google-maps-places-script';
    const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null;
    let readinessCheckStarted = false;

    const resolveWhenReady = (): void => {
      if (readinessCheckStarted) {
        return;
      }

      readinessCheckStarted = true;
      void waitForGooglePlaces().then(resolve, reject);
    };

    if (existingScript) {
      resolveWhenReady();
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps script.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = resolveWhenReady;
    script.onerror = () => reject(new Error('Failed to load Google Maps script.'));
    document.head.appendChild(script);
  }).catch((error) => {
    googlePlacesScriptPromise = null;
    throw error;
  });

  return googlePlacesScriptPromise;
}

function parseGooglePlaceAddress(place: any): {
  formattedAddress: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
} {
  const components = place?.address_components ?? [];
  const byType = (type: string): any => components.find((component: any) => component.types?.includes(type));

  const streetNumber = byType('street_number')?.long_name ?? '';
  const route = byType('route')?.long_name ?? '';
  const premise = byType('premise')?.long_name ?? '';
  const subpremise = byType('subpremise')?.long_name ?? '';
  const locality = byType('locality')?.long_name ?? '';
  const sublocality = byType('sublocality_level_1')?.long_name ?? '';
  const postalTown = byType('postal_town')?.long_name ?? '';
  const adminAreaLevel2 = byType('administrative_area_level_2')?.long_name ?? '';

  const line1 = [streetNumber, route].filter(Boolean).join(' ').trim() || premise || place?.name || '';
  const city = locality || sublocality || postalTown || adminAreaLevel2;
  const state = byType('administrative_area_level_1')?.short_name ?? '';
  const postalCode = byType('postal_code')?.long_name ?? '';

  return {
    formattedAddress: place?.formatted_address ?? '',
    line1,
    line2: subpremise ? `Unit ${subpremise}` : '',
    city,
    state,
    postalCode,
  };
}

function getPackageFieldErrorKey(packageId: string, field: string): string {
  return `package:${packageId}:${field}`;
}

function getPackageInfoFromCard(packageCard: PackageCardState): PackageInfo {
  return {
    weightLbs: Number(packageCard.weightLbs),
    lengthIn: Number(packageCard.lengthIn),
    widthIn: Number(packageCard.widthIn),
    heightIn: Number(packageCard.heightIn),
  };
}

function getPackageNote(packageCard: PackageCardState, index: number): string {
  return packageCard.packageNote.trim() || `Package ${index + 1}`;
}

function getPackageNotePlaceholder(index: number): string {
  return `Package ${index + 1} (e.g. Blue shoes)`;
}

function hasCompletePackageInfo(packageCard: PackageCardState): boolean {
  const packageInfo = getPackageInfoFromCard(packageCard);
  return (
    Boolean(packageCard.weightLbs && packageCard.lengthIn && packageCard.widthIn && packageCard.heightIn) &&
    packageInfo.weightLbs >= 0.1 &&
    packageInfo.lengthIn >= 1 &&
    packageInfo.widthIn >= 1 &&
    packageInfo.heightIn >= 1
  );
}

function getPackageDetailSummary(packageCard: PackageCardState): string {
  if (hasCompletePackageInfo(packageCard)) {
    return `${packageCard.weightLbs} lbs - ${packageCard.lengthIn} x ${packageCard.widthIn} x ${packageCard.heightIn} in`;
  }

  return packageCard.hasPrepaidLabel ? 'Prepaid label' : 'Size and weight needed';
}

export function buildPickupQuoteRequest(form: FormState, turnstileToken: string): PickupQuoteRequest {
  return {
    carrier: form.carrier,
    shipperAddress: {
      line1: form.line1.trim(),
      line2: form.line2.trim() || undefined,
      city: form.city.trim(),
      state: form.state.trim(),
      postalCode: form.postalCode.trim(),
      countryCode: 'US' as const,
    },
    pickupDate: form.pickupDate,
    email: form.email.trim() || createSystemCheckoutEmail(),
    residential: form.residential,
    turnstileToken,
    lineItems: form.packageCards.flatMap((packageCard, index) => {
      const packageLineItem = {
        type: 'PACKAGE' as const,
        note: getPackageNote(packageCard, index),
        ...(packageCard.hasPrepaidLabel ? { carrier: packageCard.carrier } : {}),
        ...(!packageCard.hasPrepaidLabel ? { packageInfo: getPackageInfoFromCard(packageCard) } : {}),
      };
      const lineItems = [packageLineItem];

      if (packageCard.hasPrepaidLabel) {
        return lineItems;
      }

      return [
        ...lineItems,
        {
          type: 'SHIPPING_LABEL' as const,
          destinationAddress: {
            line1: packageCard.destinationLine1.trim(),
            line2: packageCard.destinationLine2.trim() || undefined,
            city: packageCard.destinationCity.trim(),
            state: packageCard.destinationState.trim(),
            postalCode: packageCard.destinationPostalCode.trim(),
            countryCode: 'US' as const,
          },
        },
      ];
    }),
  };
}

export function isQuoteExpired(quote: PickupQuoteResponse, now = Date.now()): boolean {
  if (!quote.expiresAt) {
    return false;
  }
  const expiresAt = Date.parse(quote.expiresAt);
  return Number.isFinite(expiresAt) && now >= expiresAt;
}

function formatAddressSearch(address: Address): string {
  return [address.line1, address.line2, `${address.city}, ${address.state} ${address.postalCode}`]
    .filter(Boolean)
    .join(', ');
}

function formFromStoredQuote(snapshot: PickupQuoteSnapshotResponse, previous: FormState): FormState {
  const request = snapshot.request;
  const packageCards: PackageCardState[] = [];

  request.lineItems.forEach((lineItem, index) => {
    if (lineItem.type !== 'PACKAGE') {
      return;
    }
    const shippingLabel = request.lineItems[index + 1]?.type === 'SHIPPING_LABEL'
      ? request.lineItems[index + 1]
      : null;
    const destination = shippingLabel?.type === 'SHIPPING_LABEL' ? shippingLabel.destinationAddress : undefined;
    const priorCard = previous.packageCards[packageCards.length];
    const packageInfo = lineItem.packageInfo;
    const id = priorCard?.id ?? createFormEntityId('package');
    reservedFormEntityIds.add(id);
    packageCards.push({
      id,
      packageNote: lineItem.note ?? `Package ${packageCards.length + 1}`,
      carrier: lineItem.carrier ?? request.carrier,
      weightLbs: packageInfo ? String(packageInfo.weightLbs) : '',
      lengthIn: packageInfo ? String(packageInfo.lengthIn) : '',
      widthIn: packageInfo ? String(packageInfo.widthIn) : '',
      heightIn: packageInfo ? String(packageInfo.heightIn) : '',
      hasPrepaidLabel: !destination,
      destinationAddressSearch: destination ? formatAddressSearch(destination) : '',
      destinationLine1: destination?.line1 ?? '',
      destinationLine2: destination?.line2 ?? '',
      destinationCity: destination?.city ?? '',
      destinationState: destination?.state ?? '',
      destinationPostalCode: destination?.postalCode ?? '',
    });
  });

  return {
    ...previous,
    addressSearch: formatAddressSearch(request.shipperAddress),
    line1: request.shipperAddress.line1,
    line2: request.shipperAddress.line2 ?? '',
    city: request.shipperAddress.city,
    state: request.shipperAddress.state,
    postalCode: request.shipperAddress.postalCode,
    carrier: request.carrier,
    pickupDate: request.pickupDate,
    email: request.email,
    residential: request.residential ?? false,
    packageCards: packageCards.length > 0 ? packageCards : previous.packageCards,
  };
}

export function buildCheckoutStartRequest(quoteId: string): CheckoutStartRequest {
  return { quoteId };
}

export function getQuoteTotalAmount(quote: CheckoutQuote): number {
  return quote.totalAmount ?? quote.amount;
}

function getQuoteLineItemTotal(lineItem: CheckoutQuoteLineItem): number {
  return lineItem.amount;
}

function getItemizedChargeRows(quote: CheckoutQuote): Array<{
  key: string;
  label: string;
  amount: number;
  currency: string;
}> {
  const lineItems = quote.lineItems ?? [];
  const pickupAmount = lineItems
    .filter((lineItem) => lineItem.type === 'PACKAGE')
    .reduce((total, lineItem) => total + getQuoteLineItemTotal(lineItem), 0);
  const labelAmount = lineItems
    .filter((lineItem) => lineItem.type === 'SHIPPING_LABEL')
    .reduce((total, lineItem) => total + getQuoteLineItemTotal(lineItem), 0);
  const itemizedAmount = pickupAmount + labelAmount;
  const resolvedPickupAmount = itemizedAmount > 0 || lineItems.length > 0 ? pickupAmount : getQuoteTotalAmount(quote);
  const rows = [
    {
      key: 'pickup',
      label: 'Pickup fee',
      amount: resolvedPickupAmount,
      currency: quote.currency,
    },
  ];

  if (labelAmount > 0) {
    rows.push({
      key: 'label',
      label: 'Shipping label',
      amount: labelAmount,
      currency: quote.currency,
    });
  }

  return rows;
}

function groupReviewLineItemsByPackage(
  packageCards: PackageCardState[],
  lineItems: CheckoutQuoteLineItem[] | undefined,
): CheckoutQuoteLineItem[][] {
  const reviewLineItems = lineItems ?? [];

  if (!reviewLineItems.length) {
    return packageCards.map(() => []);
  }

  if (reviewLineItems.some((lineItem) => typeof lineItem.orderIndex === 'number')) {
    return packageCards.map((_, index) => reviewLineItems.filter((lineItem) => lineItem.orderIndex === index));
  }

  let nextLineItemCursor = 0;

  return packageCards.map((packageCard) => {
    const groupedLineItems: CheckoutQuoteLineItem[] = [];
    const expectedTypes: Array<'PACKAGE' | 'SHIPPING_LABEL'> = ['PACKAGE'];

    if (!packageCard.hasPrepaidLabel) {
      expectedTypes.push('SHIPPING_LABEL');
    }

    expectedTypes.forEach((expectedType) => {
      const currentLineItem = reviewLineItems[nextLineItemCursor];

      if (currentLineItem?.type === expectedType) {
        groupedLineItems.push(currentLineItem);
        nextLineItemCursor += 1;
        return;
      }

      const fallbackLineItemIndex = reviewLineItems.findIndex(
        (lineItem, index) => index >= nextLineItemCursor && lineItem.type === expectedType,
      );

      if (fallbackLineItemIndex >= nextLineItemCursor) {
        groupedLineItems.push(reviewLineItems[fallbackLineItemIndex]);
        nextLineItemCursor = fallbackLineItemIndex + 1;
      }
    });

    return groupedLineItems;
  });
}

export function validateForm(form: FormState): Record<string, string> {
  const errors: Record<string, string> = {};
  const openTimeMinutes = toMinutes(DEFAULT_READY_TIME) ?? 540;
  const closeTimeMinutes = toMinutes(DEFAULT_CLOSE_TIME) ?? 1020;
  const trimmedEmail = form.email.trim();
  const nextEligiblePickupDate = new Date(`${getNextEligiblePickupDate()}T00:00:00`);

  const missingAddressParts = !form.line1.trim() || !form.city.trim() || !form.state.trim() || !form.postalCode.trim();
  if (!form.addressSearch.trim()) {
    errors.addressSearch = 'Pickup address is required.';
  } else if (missingAddressParts) {
    errors.addressSearch = 'Please choose your full address from the list.';
  }
  if (!form.pickupDate) {
    errors.pickupDate = 'Pickup date is required.';
  }
  const pickupDate = new Date(`${form.pickupDate}T00:00:00`);
  if (Number.isNaN(pickupDate.getTime()) || pickupDate < nextEligiblePickupDate) {
    errors.pickupDate = 'Same-day pickup is not available. Choose tomorrow or a later date.';
  }
  if (!errors.pickupDate && getUspsWeekendPickupWarning(form)) {
    errors.pickupDate = USPS_WEEKEND_PICKUP_WARNING;
  }

  const readyTime = toMinutes(form.readyTime);
  const closeTime = toMinutes(form.closeTime);
  if (readyTime === null) {
    errors.readyTime = 'Pickup start time is required.';
  } else if (readyTime < openTimeMinutes || readyTime > closeTimeMinutes) {
    errors.readyTime = `Start time must be between ${DEFAULT_READY_TIME} and ${DEFAULT_CLOSE_TIME} local time.`;
  }

  if (closeTime === null) {
    errors.closeTime = 'Pickup end time is required.';
  } else if (closeTime < openTimeMinutes || closeTime > closeTimeMinutes) {
    errors.closeTime = `End time must be between ${DEFAULT_READY_TIME} and ${DEFAULT_CLOSE_TIME} local time.`;
  }

  if (readyTime !== null && closeTime !== null && readyTime >= closeTime) {
    errors.closeTime = 'End time must be later than start time.';
  }

  if (!trimmedEmail || isSystemCheckoutEmail(trimmedEmail)) {
    errors.email = 'Email is required.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
    errors.email = 'Enter a valid email address.';
  }

  if (form.packageCards.length === 0) {
    errors.packageCards = 'Add at least one package.';
  }

  form.packageCards.forEach((packageCard, index) => {
    const packageInfo = getPackageInfoFromCard(packageCard);

    if (!packageCard.hasPrepaidLabel) {
      if (!packageCard.weightLbs || Number.isNaN(packageInfo.weightLbs) || packageInfo.weightLbs < 0.1) {
        errors[getPackageFieldErrorKey(packageCard.id, 'weightLbs')] = 'Weight must be at least 0.1 lbs.';
      }
      if (!packageCard.lengthIn || Number.isNaN(packageInfo.lengthIn) || packageInfo.lengthIn < 1) {
        errors[getPackageFieldErrorKey(packageCard.id, 'lengthIn')] = 'Length must be at least 1 inch.';
      }
      if (!packageCard.widthIn || Number.isNaN(packageInfo.widthIn) || packageInfo.widthIn < 1) {
        errors[getPackageFieldErrorKey(packageCard.id, 'widthIn')] = 'Width must be at least 1 inch.';
      }
      if (!packageCard.heightIn || Number.isNaN(packageInfo.heightIn) || packageInfo.heightIn < 1) {
        errors[getPackageFieldErrorKey(packageCard.id, 'heightIn')] = 'Height must be at least 1 inch.';
      }
      if (!packageCard.destinationLine1.trim()) {
        errors[getPackageFieldErrorKey(packageCard.id, 'destinationLine1')] = `Package ${index + 1}: destination street address is required.`;
      }
      if (!packageCard.destinationCity.trim()) {
        errors[getPackageFieldErrorKey(packageCard.id, 'destinationCity')] = `Package ${index + 1}: destination city is required.`;
      }
      if (!packageCard.destinationState.trim()) {
        errors[getPackageFieldErrorKey(packageCard.id, 'destinationState')] = `Package ${index + 1}: destination state is required.`;
      }
      if (!packageCard.destinationPostalCode.trim()) {
        errors[getPackageFieldErrorKey(packageCard.id, 'destinationPostalCode')] = `Package ${index + 1}: destination ZIP code is required.`;
      }
    }
  });

  return errors;
}

function validatePickupBasics(form: FormState): Record<string, string> {
  const pickupBasicKeys = new Set(['addressSearch', 'pickupDate', 'readyTime', 'closeTime', 'email']);
  return Object.fromEntries(
    Object.entries(validateForm(form)).filter(([key]) => pickupBasicKeys.has(key)),
  );
}

function StepProgress({ currentStep }: StepProgressProps): React.ReactElement {
  return (
    <nav className="progress-nav" aria-label="Checkout progress">
      <ol className="progress-steps">
        {CHECKOUT_STEPS.map((step, index) => {
          const stepNumber = index + 1;
          const stateClass = stepNumber < currentStep ? 'complete' : stepNumber === currentStep ? 'active' : 'upcoming';

          return (
            <li key={step} className={`progress-step ${stateClass}`}>
              <span className="progress-dot" aria-hidden="true">
                {stepNumber}
              </span>
              <span className="progress-label">{step}</span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ReviewStageProgress({ currentStep }: { currentStep: number }): React.ReactElement {
  return (
    <nav className="review-stage-progress" aria-label="Checkout progress">
      {CHECKOUT_STEPS.map((step, index) => {
        const stepNumber = index + 1;
        const stateClass = stepNumber < currentStep ? 'complete' : stepNumber === currentStep ? 'active' : 'upcoming';

        return (
          <React.Fragment key={step}>
            <div className={`review-stage-progress-item is-${stateClass}`}>
              <span className="review-stage-progress-dot" aria-hidden="true">
                {stateClass === 'complete' ? '✓' : stepNumber}
              </span>
              <span className="review-stage-progress-label">{step}</span>
            </div>
            {index < CHECKOUT_STEPS.length - 1 && (
              <span
                className={`review-stage-progress-line is-${stepNumber < currentStep ? 'complete' : 'upcoming'}`}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

function ConfirmationStageProgress(): React.ReactElement {
  return (
    <nav className="confirmation-stage-progress" aria-label="Checkout progress">
      {CHECKOUT_STEPS.map((step, index) => (
        <React.Fragment key={step}>
          <div className="confirmation-stage-progress-item is-complete">
            <span className="confirmation-stage-progress-dot" aria-hidden="true">
              ✓
            </span>
            <span className="confirmation-stage-progress-label">{step}</span>
          </div>
          {index < CHECKOUT_STEPS.length - 1 && (
            <span className="confirmation-stage-progress-line" aria-hidden="true" />
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

function ReviewPackageIllustration({ showLabel = true }: { showLabel?: boolean }): React.ReactElement {
  return (
    <svg
      viewBox="0 0 200 200"
      className="review-package-illustration-svg"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
      aria-hidden="true"
    >
      <ellipse cx="100" cy="162" rx="38" ry="13" fill="rgba(84, 62, 41, 0.12)" />
      <polygon
        points="100,180 30,140 30,60 100,100"
        fill="#dca77b"
        stroke="#b5835a"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <polygon
        points="100,180 170,140 170,60 100,100"
        fill="#eebc8f"
        stroke="#b5835a"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <polygon
        points="100,100 30,60 100,20 170,60"
        fill="#fbe0c3"
        stroke="#b5835a"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <polygon points="90,95 25,58 35,52 100,89" fill="#ffffff" opacity="0.3" />
      <polygon points="110,95 175,58 165,52 100,89" fill="#ffffff" opacity="0.1" />
      {showLabel && (
        <g filter="drop-shadow(0 3px 4px rgba(15, 23, 42, 0.14))">
          <polygon
            points="100,52 121,64 100,76 79,64"
            fill="#ffffff"
            stroke="#94a3b8"
            strokeWidth="1"
            strokeLinejoin="round"
          />
          <polygon points="86,60 107,72 100,76 79,64" fill="#facc15" opacity="0.8" />
          <line x1="88" y1="57" x2="102" y2="65" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" />
          <line x1="92" y1="55" x2="106" y2="63" stroke="#334155" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="96" y1="53" x2="110" y2="61" stroke="#64748b" strokeWidth="1" strokeLinecap="round" />
        </g>
      )}
    </svg>
  );
}

function PackagePreviewIllustration(): React.ReactElement {
  return (
    <svg
      viewBox="0 0 200 200"
      className="package-preview-svg package-preview-box-svg"
      aria-hidden="true"
    >
      <polygon
        points="100,180 30,140 30,60 100,100"
        fill="#dca77b"
        stroke="#b5835a"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <polygon
        points="100,180 170,140 170,60 100,100"
        fill="#eebc8f"
        stroke="#b5835a"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <polygon
        points="100,100 30,60 100,20 170,60"
        fill="#fbe0c3"
        stroke="#b5835a"
        strokeWidth="2"
        strokeLinejoin="round"
      />

      <g filter="drop-shadow(0 3px 4px rgba(15, 23, 42, 0.14))">
        <polygon
          points="100,52 121,64 100,76 79,64"
          fill="#ffffff"
          stroke="#94a3b8"
          strokeWidth="1"
          strokeLinejoin="round"
        />
        <polygon points="86,60 107,72 100,76 79,64" fill="#facc15" opacity="0.8" />
        <line x1="93" y1="60" x2="107" y2="68" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="96.5" y1="58" x2="110.5" y2="66" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="100" y1="56" x2="114" y2="64" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" />
      </g>
    </svg>
  );
}

function ReviewDestinationIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 20 20" className="review-package-detail-icon-svg" aria-hidden="true">
      <path
        d="M10 17.2s5-4.64 5-8.52A5 5 0 0 0 5 8.68c0 3.88 5 8.52 5 8.52Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
      <circle cx="10" cy="8.5" r="1.8" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function ReviewShippingMethodIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 20 20" className="review-package-detail-icon-svg" aria-hidden="true">
      <path
        d="M6.2 2.9h5.72l4.18 4.18V15.8a1.3 1.3 0 0 1-1.3 1.3H6.2a1.3 1.3 0 0 1-1.3-1.3V4.2a1.3 1.3 0 0 1 1.3-1.3Z"
        fill="none"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
      <path d="M11.92 2.9v4.18h4.18" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.6" />
      <path d="M7.5 10h5M7.5 12.9h5" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.6" />
    </svg>
  );
}

function ArrowRightIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" className="button-inline-icon" aria-hidden="true">
      <path
        d="M3.5 8h8.1M9.4 4.8 12.6 8l-3.2 3.2"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.7"
      />
    </svg>
  );
}

function ArrowLeftIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" className="button-inline-icon" aria-hidden="true">
      <path
        d="M12.5 8H4.4M6.6 4.8 3.4 8l3.2 3.2"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.7"
      />
    </svg>
  );
}

function HomeFeatureIcon({ icon }: { icon: HomeFeatureKey }): React.ReactElement {
  if (icon === 'wardrobe') {
    return <PackageCubeIcon className="home-feature-svg" />;
  }

  if (icon === 'holiday') {
    return (
      <svg viewBox="0 0 20 20" className="home-feature-svg" aria-hidden="true">
        <path
          d="M2.9 10.8h8.9v4.4H2.9zM11.8 12.1h2.6l1.8 1.7v1.4h-4.4m-6.5 0a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4Zm8 0a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4ZM5 10.8V8.9h4.2v1.9"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.6"
        />
      </svg>
    );
  }

  if (icon === 'value') {
    return (
      <svg viewBox="0 0 20 20" className="home-feature-svg" aria-hidden="true">
        <path
          d="M4.4 6.7h9.8a1.9 1.9 0 0 1 1.9 1.9v5.2a1.9 1.9 0 0 1-1.9 1.9H4.4V6.7Zm0 0V5.6a1.8 1.8 0 0 1 1.8-1.8h6.1"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.55"
        />
        <path d="M4.7 9.7h11.2" fill="none" stroke="currentColor" strokeWidth="1.55" />
        <circle cx="12.9" cy="11.7" r="1.15" fill="none" stroke="currentColor" strokeWidth="1.55" />
      </svg>
    );
  }

  return <PackageCubeIcon className="home-feature-svg" />;
}

function HomeBannerItemIcon({ icon }: { icon: HomeBannerItemKey }): React.ReactElement {
  if (icon === 'package') {
    return (
      <svg viewBox="0 0 24 24" className="home-banner-item-svg" aria-hidden="true">
        <path
          d="M12 3.2 19 6.8v10.4L12 20.8 5 17.2V6.8L12 3.2Zm0 0V10m7-3.2L12 10 5 6.8"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </svg>
    );
  }

  if (icon === 'shoppingBag') {
    return (
      <svg viewBox="0 0 24 24" className="home-banner-item-svg" aria-hidden="true">
        <path
          d="M6.5 9.2h11l-.8 9.2H7.3L6.5 9.2Zm2.7 0V7.8a2.8 2.8 0 0 1 5.6 0v1.4"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.55"
        />
      </svg>
    );
  }

  if (icon === 'shirt') {
    return (
      <svg viewBox="0 0 24 24" className="home-banner-item-svg" aria-hidden="true">
        <path
          d="M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23Z"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </svg>
    );
  }

  if (icon === 'hanger') {
    return (
      <svg viewBox="4 2 16 14" className="home-banner-item-svg" aria-hidden="true">
        <path
          d="M12 6.1a1.7 1.7 0 1 0-1.6-1.7m1.6 1.7v1.4l5.2 4.6a.9.9 0 0 1-.6 1.6H7.4a.9.9 0 0 1-.6-1.6L12 7.5"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.35"
        />
      </svg>
    );
  }

  if (icon === 'smartphone') {
    return (
      <svg viewBox="0 0 24 24" className="home-banner-item-svg" aria-hidden="true">
        <rect
          x="7.2"
          y="2.8"
          width="9.6"
          height="18.4"
          rx="2.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M10.2 6.2h3.6M10.4 17.8h3.2"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="1.5"
        />
      </svg>
    );
  }

  if (icon === 'bookOpen') {
    return (
      <svg viewBox="0 0 24 24" className="home-banner-item-svg" aria-hidden="true">
        <path
          d="M12 6.1c-1.7-1.1-3.8-1.7-6.4-1.7v13.2c2.6 0 4.7.6 6.4 1.8m0-13.3c1.7-1.1 3.8-1.7 6.4-1.7v13.2c-2.6 0-4.7.6-6.4 1.8m0-13.3v13.3"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </svg>
    );
  }

  if (icon === 'watch') {
    return (
      <svg viewBox="0 0 24 24" className="home-banner-item-svg" aria-hidden="true">
        <path
          d="M9.4 2.8h5.2l-.7 2.9h-3.8l-.7-2.9Zm0 18.4h5.2l-.7-2.9h-3.8l-.7 2.9Z"
          fill="none"
          stroke="currentColor"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
        <circle cx="12" cy="12" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M12 9.4v3.1l1.9 1.2"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className="home-banner-item-svg" aria-hidden="true">
      <path
        d="M6.5 9.2h11l-.8 9.2H7.3L6.5 9.2Zm2.7 0V7.8a2.8 2.8 0 0 1 5.6 0v1.4"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.55"
      />
    </svg>
  );
}

function PackageCubeIcon({ className = 'package-cube-icon' }: { className?: string }): React.ReactElement {
  return (
    <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
      <path
        d="M10 2.9 16 6.1v7.8L10 17.1l-6-3.2V6.1L10 2.9Zm0 0V10m6-3.9L10 10 4 6.1"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.55"
      />
    </svg>
  );
}

function EnvelopeIcon({ className = 'auth-field-icon' }: { className?: string }): React.ReactElement {
  return (
    <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
      <path
        d="M3.4 5.2h13.2v9.6H3.4V5.2Zm.6.5 6 5.1 6-5.1"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.45"
      />
    </svg>
  );
}

function AccountIcon({ className = 'site-sign-in-icon' }: { className?: string }): React.ReactElement {
  return (
    <svg viewBox="0 0 18 18" className={className} aria-hidden="true">
      <path
        d="M14.25 15.75V14.25C14.25 13.4544 13.9339 12.6913 13.3713 12.1287C12.8087 11.5661 12.0456 11.25 11.25 11.25H6.75C5.95435 11.25 5.19129 11.5661 4.62868 12.1287C4.06607 12.6913 3.75 13.4544 3.75 14.25V15.75"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      <path
        d="M9 8.25C10.6569 8.25 12 6.90685 12 5.25C12 3.59315 10.6569 2.25 9 2.25C7.34315 2.25 6 3.59315 6 5.25C6 6.90685 7.34315 8.25 9 8.25Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function AccountReturnsIcon({ className = 'account-menu-icon' }: { className?: string }): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" className={className} aria-hidden="true">
      <path
        d="M4.25 2.75h5.3L11.75 5v8.25h-7.5V2.75Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.35"
      />
      <path
        d="M9.5 2.9v2.35h2.15M5.9 7.65h4.2M5.9 10.1h3"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.35"
      />
    </svg>
  );
}

function AccountSignOutIcon({ className = 'account-menu-icon' }: { className?: string }): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" className={className} aria-hidden="true">
      <path
        d="M6.75 3.25h-2.5v9.5h2.5M9.25 5.25 12 8l-2.75 2.75M11.65 8H6.5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.35"
      />
    </svg>
  );
}

interface SignInPanelProps {
  stage: SignInStage;
  email: string;
  code: string;
  sentEmail: string;
  error: string;
  busy: boolean;
  authLoading: boolean;
  onEmailChange: (value: string) => void;
  onCodeChange: (value: string) => void;
  onBackToEmail: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

function SignInPanel({
  stage,
  email,
  code,
  sentEmail,
  error,
  busy,
  authLoading,
  onEmailChange,
  onCodeChange,
  onBackToEmail,
  onSubmit,
}: SignInPanelProps): React.ReactElement {
  const isCodeStage = stage === 'code';
  const isDisabled = busy || authLoading;

  return (
    <section className="auth-page" aria-labelledby="auth-title">
      <div className="auth-card">
        <span className="auth-icon-shell" aria-hidden="true">
          <PackageCubeIcon className="auth-icon" />
        </span>
        <h1 id="auth-title">{isCodeStage ? 'Enter your code' : 'Welcome back'}</h1>
        <p>{isCodeStage ? `We sent a code to ${sentEmail}.` : 'Sign in to view your return history.'}</p>

        <form className="auth-form" onSubmit={onSubmit}>
          {isCodeStage ? (
            <>
              <label className="auth-field">
                <span>Code</span>
                <input
                  type="text"
                  value={code}
                  onChange={(event) => onCodeChange(event.target.value)}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  placeholder="123456"
                  disabled={isDisabled}
                />
              </label>

              <div className="auth-code-actions">
                <button type="submit" className="button primary auth-submit-button" disabled={isDisabled}>
                  <span>{isDisabled ? 'Checking...' : 'Continue'}</span>
                  <ArrowRightIcon />
                </button>
                <button type="button" className="auth-secondary-action" onClick={onBackToEmail} disabled={isDisabled}>
                  Use a different email
                </button>
              </div>
            </>
          ) : (
            <>
              <label className="auth-field">
                <span>Email Address</span>
                <span className="auth-input-shell">
                  <EnvelopeIcon />
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => onEmailChange(event.target.value)}
                    autoComplete="email"
                    placeholder="you@example.com"
                    disabled={isDisabled}
                  />
                </span>
              </label>

              <button type="submit" className="button primary auth-submit-button" disabled={isDisabled}>
                <span>{isDisabled ? 'Sending...' : 'Send code'}</span>
                <ArrowRightIcon />
              </button>
            </>
          )}
        </form>

        {error && (
          <p className="alert error auth-alert" role="alert">
            {error}
          </p>
        )}

        <div className="auth-footer">
          <span>
            {isCodeStage
              ? 'Use the latest code we sent.'
              : 'Use the email address you entered when scheduling your pickup.'}
          </span>
        </div>
      </div>
    </section>
  );
}

interface ReturnMagicLinkPanelProps {
  busy: boolean;
  error: string;
  onSignIn: () => void;
}

function ReturnMagicLinkPanel({ busy, error, onSignIn }: ReturnMagicLinkPanelProps): React.ReactElement {
  return (
    <section className="auth-page" aria-labelledby="return-magic-title">
      <div className="auth-card">
        <span className="auth-icon-shell" aria-hidden="true">
          <PackageCubeIcon className="auth-icon" />
        </span>
        <h1 id="return-magic-title">{busy ? 'Opening your return' : "We couldn't open this secure link"}</h1>
        <p>{busy ? 'Checking the secure link from your email.' : error}</p>
        {!busy && (
          <button type="button" className="button primary auth-submit-button" onClick={onSignIn}>
            <span>Sign in with email</span>
            <ArrowRightIcon />
          </button>
        )}
      </div>
    </section>
  );
}

interface AccountReturnsPageProps {
  returns: AccountReturnSummary[];
  loading: boolean;
  error: string;
  userEmail: string;
  detailsBusyId: string;
  isSupportView?: boolean;
  onDetails?: (returnSummary: AccountReturnSummary) => void;
  onNewReturn?: () => void;
}

function getAccountReturnStatusLabel(state: BookingState, pickupDate: string): string {
  if (state === 'CANCELLED_AUTH') {
    return 'Booking Issue';
  }

  if (state === 'CANCELLED_PICKUP') {
    return 'Cancelled';
  }

  if (state === 'CAPTURED' || state === 'SCHEDULED') {
    return pickupDate < getTodayDate() ? 'Completed' : 'Pickup scheduled';
  }

  if (state === 'FAILED_PAYMENT' || state === 'FAILED_SCHEDULING') {
    return 'Action needed';
  }

  return 'In progress';
}

function AccountReturnsPage({
  returns,
  loading,
  error,
  userEmail,
  detailsBusyId,
  isSupportView = false,
  onDetails,
  onNewReturn,
}: AccountReturnsPageProps): React.ReactElement {
  return (
    <section className="account-returns-page" aria-labelledby="account-returns-title">
      <div className="account-returns-head">
        <div>
          <h1 id="account-returns-title">{isSupportView ? 'Returns' : 'My Returns'}</h1>
          <p>{isSupportView ? 'View recent pickup orders.' : 'View and manage upcoming and past pickups.'}</p>
          {userEmail && <span className="account-returns-email">{userEmail}</span>}
        </div>
        {onNewReturn && (
          <button type="button" className="button primary account-new-return-button" onClick={onNewReturn}>
            New Return
          </button>
        )}
      </div>

      <section className="account-returns-card" aria-live="polite">
        {loading && <SkeletonLines rows={5} />}

        {!loading && error && (
          <p className="alert error account-returns-alert" role="alert">
            {error}
          </p>
        )}

        {!loading && !error && returns.length === 0 && (
          <div className="account-returns-empty">
            <span className="account-return-icon is-empty" aria-hidden="true">
              <PackageCubeIcon className="account-return-icon-svg" />
            </span>
            <strong>No pickups yet</strong>
            <p>Scheduled pickups will appear here.</p>
          </div>
        )}

        {!loading &&
          !error &&
          returns.map((returnSummary) => {
            const isBusy = detailsBusyId === returnSummary.returnId;
            const carrierSummaryLabel = getReturnCarrierSummaryLabel(returnSummary);
            const pickupTitle =
              carrierSummaryLabel === 'Multiple carriers' ? 'Multi-carrier pickup' : `${carrierSummaryLabel} pickup`;
            const statusIconTone = isAccountReturnProblemState(returnSummary.state) ? 'error' : 'success';
            return (
              <article
                className={`account-return-row${onDetails ? '' : ' has-no-action'}`}
                key={returnSummary.returnId}
              >
                <span className={`account-return-icon is-${statusIconTone}`} aria-hidden="true">
                  {statusIconTone === 'error' ? (
                    <svg viewBox="0 0 20 20" className="account-return-icon-svg" aria-hidden="true">
                      <path
                        d="M6.2 6.2 13.8 13.8M13.8 6.2 6.2 13.8"
                        fill="none"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 20 20" className="account-return-icon-svg" aria-hidden="true">
                      <path
                        d="m5.6 10.2 2.8 2.9 6-6.2"
                        fill="none"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      />
                    </svg>
                  )}
                </span>
                <div className="account-return-main">
                  <div className="account-return-title-row">
                    <h2>{pickupTitle}</h2>
                    <span className="account-return-status">
                      {getAccountReturnStatusLabel(returnSummary.state, returnSummary.pickupDate)}
                    </span>
                  </div>
                  <p>
                    {returnSummary.returnId} - {formatDate(returnSummary.pickupDate)}
                  </p>
                  {isSupportView && isSupportReturnSummary(returnSummary) && <p>{returnSummary.customerEmail}</p>}
                </div>
                <div className="account-return-meta">
                  <span>
                    {carrierSummaryLabel} - {formatCurrency(returnSummary.totalAmount, returnSummary.currency)}
                  </span>
                  <span>
                    {formatClockTime(returnSummary.window.readyTime)} - {formatClockTime(returnSummary.window.closeTime)}
                  </span>
                </div>
                {onDetails && (
                  <button
                    type="button"
                    className="account-return-details-button"
                    onClick={() => onDetails(returnSummary)}
                    disabled={isBusy}
                  >
                    {isBusy ? 'Opening...' : 'Details'}
                  </button>
                )}
              </article>
            );
          })}
      </section>
    </section>
  );
}

function SellDonateSparkIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 32 32" className="sell-donate-spark-icon" aria-hidden="true">
      <path
        d="m16 4 2.2 6.1L24.5 12l-6.3 1.9L16 20l-2.2-6.1L7.5 12l6.3-1.9L16 4Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
      <path
        d="m24.2 6.2.7 2 .2.6 2 .6-2 .6-.9 2.6-.9-2.6-2-.6 2-.6.2-.6.7-2Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </svg>
  );
}

function SellDonateOptionIcon({ option }: { option: SellDonateOptionKey }): React.ReactElement {
  if (option === 'resell') {
    return (
      <svg viewBox="0 0 32 32" className="sell-donate-option-icon-svg" aria-hidden="true">
        <path
          d="M16 5v22m6-18H13.8a3.8 3.8 0 1 0 0 7.6h4.4a3.8 3.8 0 1 1 0 7.6H10"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 32 32" className="sell-donate-option-icon-svg" aria-hidden="true">
      <path
        d="M16 27.2 6.8 18c-2.4-2.4-2.4-6.3 0-8.7 2.1-2.1 5.4-2.4 7.9-.7l1.3.9 1.3-.9c2.5-1.7 5.8-1.4 7.9.7 2.4 2.4 2.4 6.3 0 8.7L16 27.2Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

function HelpHeroIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 32 32" className="help-hero-icon-svg" aria-hidden="true">
      <path
        d="M10 23.5 7 26v-5.3a7.5 7.5 0 1 1 3 2.8Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

function HelpSupportCardIcon({ kind }: { kind: 'email' | 'hours' }): React.ReactElement {
  if (kind === 'hours') {
    return (
      <svg viewBox="0 0 28 28" className="help-support-icon-svg" aria-hidden="true">
        <circle cx="14" cy="14" r="8.75" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M14 9v5l3 2" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 28 28" className="help-support-icon-svg" aria-hidden="true">
      <path
        d="M6 9.5h16v9a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2v-9Zm0 0 8 5 8-5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

function HelpTopicIcon({ topic }: { topic: HelpTopicKey }): React.ReactElement {
  if (topic === 'tracking') {
    return (
      <svg viewBox="0 0 20 20" className="help-topic-icon-svg" aria-hidden="true">
        <path
          d="M10 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.7"
        />
        <path d="M10 17c2.6-2.8 3.9-5 3.9-6.8A3.9 3.9 0 1 0 6.1 10c0 1.8 1.3 4 3.9 7Z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" />
        <circle cx="10" cy="10" r="1.3" />
      </svg>
    );
  }

  if (topic === 'payment') {
    return (
      <svg viewBox="0 0 20 20" className="help-topic-icon-svg" aria-hidden="true">
        <rect x="3.5" y="5.5" width="13" height="9" rx="1.8" fill="none" stroke="currentColor" strokeWidth="1.7" />
        <path d="M3.5 8.5h13" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" />
      </svg>
    );
  }

  if (topic === 'reschedule') {
    return (
      <svg viewBox="0 0 20 20" className="help-topic-icon-svg" aria-hidden="true">
        <rect x="4" y="5" width="12" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="1.7" />
        <path d="M6.8 3.8v2.4m6.4-2.4v2.4M4 8h12" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" />
      </svg>
    );
  }

  if (topic === 'issues') {
    return (
      <svg viewBox="0 0 20 20" className="help-topic-icon-svg" aria-hidden="true">
        <circle cx="10" cy="10" r="7" fill="none" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8.4 8.1a1.9 1.9 0 1 1 3 1.5c-.9.6-1.2 1-1.2 1.9m-.2 2.7h.1" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 20 20" className="help-topic-icon-svg" aria-hidden="true">
      <path
        d="M10 3.2 15.6 6v8L10 16.8 4.4 14V6L10 3.2Zm0 0V9m5.6-3L10 9 4.4 6"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </svg>
  );
}

function HelpSendIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" className="help-send-icon" aria-hidden="true">
      <path
        d="m2.4 7.6 10.5-4.3c.5-.2 1 .3.8.8L9.4 14.6c-.2.5-1 .4-1.1-.1l-.7-3.3-3.3-.7c-.5-.1-.6-.9-.1-1.1Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.4"
      />
      <path d="m7.6 11.2 3.9-6" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.4" />
    </svg>
  );
}

function HelpChevronIcon({ isOpen = false }: { isOpen?: boolean }): React.ReactElement {
  return (
    <svg viewBox="0 0 20 20" className={`help-faq-chevron ${isOpen ? 'is-open' : ''}`} aria-hidden="true">
      <path d="m6 8 4 4 4-4" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
    </svg>
  );
}

function ManageStatusClockIcon({ className }: { className?: string }): React.ReactElement {
  return (
    <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
      <circle cx="10" cy="10" r="7.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M10 6.6v3.8l2.4 1.6" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
    </svg>
  );
}

function BrandLockup(): React.ReactElement {
  return (
    <span className="brand-mark" aria-hidden="true">
      <span className="brand-mark-icon-shell">
        <PackageCubeIcon className="brand-mark-icon" />
      </span>
      <span className="brand-mark-copy">
        <span className="brand-mark-word">
          <span className="brand-mark-word-return">Return</span>
          <span className="brand-mark-word-em">Em</span>
        </span>
        <span className="brand-mark-tagline">Returns without the hassle.</span>
      </span>
    </span>
  );
}

function CheckoutLockIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 20 20" className="review-checkout-icon-svg" aria-hidden="true">
      <path
        d="M6.2 8.5V6.7a3.8 3.8 0 1 1 7.6 0v1.8M5.2 8.5h9.6v7.1H5.2z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </svg>
  );
}

function ConfirmationClockIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 32 32" className="confirmation-clock-icon" aria-hidden="true">
      <circle cx="16" cy="16" r="9.5" />
      <path d="M16 10.9v5.8l3.9 2.3" />
    </svg>
  );
}

function ConfirmationSuccessIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 40 40" className="confirmation-success-icon" aria-hidden="true">
      <circle cx="20" cy="20" r="15" />
      <path d="m13.8 20.2 4.2 4.3 8.2-9.4" />
    </svg>
  );
}

type ConfirmationGlyph =
  | 'mail'
  | 'print'
  | 'package'
  | 'track'
  | 'calendar'
  | 'pin'
  | 'contact'
  | 'printer'
  | 'download';

function ConfirmationGlyphIcon({
  glyph,
  className = 'confirmation-glyph-icon',
}: {
  glyph: ConfirmationGlyph;
  className?: string;
}): React.ReactElement {
  switch (glyph) {
    case 'mail':
      return (
        <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
          <path d="M3.5 5.5h13v9h-13zM4 6l6 4.5L16 6" />
        </svg>
      );
    case 'print':
      return (
        <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
          <path d="M6 6V3.5h8V6M5 14H3.5V8.5h13V14H15M6 11h8v5H6zM14.2 9.8h.01" />
        </svg>
      );
    case 'package':
      return (
        <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
          <path d="M10 2.9 16 6.1v7.8L10 17.1l-6-3.2V6.1L10 2.9Zm0 0V10m6-3.9L10 10 4 6.1" />
        </svg>
      );
    case 'track':
      return (
        <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
          <path d="M3.5 12.5h8.5l2.2-2.8h2.3v2.8H17m-11.5 0v2.2m9 0v-2.2M6 14.7a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0Zm9 0a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0ZM12 6h3.5M12 8.5h2.5M3.5 6H9" />
        </svg>
      );
    case 'calendar':
      return (
        <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
          <path d="M5.5 3.8v2.4M14.5 3.8v2.4M4 6.2h12v10H4zM4 9.2h12" />
        </svg>
      );
    case 'pin':
      return (
        <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
          <path d="M10 16.4s4.2-4.5 4.2-7.4A4.2 4.2 0 1 0 5.8 9c0 2.9 4.2 7.4 4.2 7.4Zm0-5.6a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6Z" />
        </svg>
      );
    case 'contact':
      return (
        <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
          <path d="M10 10.1a2.9 2.9 0 1 0 0-5.8 2.9 2.9 0 0 0 0 5.8Zm-5 5.6a5.7 5.7 0 0 1 10 0" />
        </svg>
      );
    case 'printer':
      return (
        <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
          <path d="M6 6V3.5h8V6M5 14H3.5V8.5h13V14H15M6 11h8v5H6zM14.2 9.8h.01" />
        </svg>
      );
    case 'download':
      return (
        <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
          <path d="M10 4.2v7.2m0 0 3-3m-3 3-3-3M4.2 15.8h11.6" />
        </svg>
      );
  }
}

function ChevronUpSmallIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" className="package-toggle-icon" aria-hidden="true">
      <path
        d="m4.2 9.6 3.8-3.8 3.8 3.8"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.7"
      />
    </svg>
  );
}

function IntakeStageHeader({
  intakeStep,
  title,
  subtitle,
}: {
  intakeStep: IntakeStep;
  title: string;
  subtitle?: string;
}): React.ReactElement {
  return (
    <div className="intake-stage-header">
      <div className="intake-progress" aria-hidden="true">
        <span className={`intake-progress-segment ${intakeStep >= 1 ? 'is-active' : ''}`} />
        <span className={`intake-progress-segment ${intakeStep >= 2 ? 'is-active' : ''}`} />
      </div>
      <p className="intake-step-label">Step {intakeStep} of 2</p>
      <h2 className="intake-stage-title">{title}</h2>
      {subtitle && <p className="intake-stage-subtitle">{subtitle}</p>}
    </div>
  );
}

function HomeScheduleHero(): React.ReactElement {
  const bannerItems: Array<{
    icon: HomeBannerItemKey;
    top: string;
    left: string;
    size: number;
    color: string;
    delay: string;
    duration: string;
    rotate: number;
  }> = [
    { icon: 'package', top: '-8%', left: '5%', size: 28, color: '#f97316', delay: '0s', duration: '7s', rotate: 15 },
    { icon: 'shirt', top: '-5%', left: '18%', size: 24, color: '#34C5FA', delay: '1.2s', duration: '8.5s', rotate: -20 },
    { icon: 'hanger', top: '-10%', left: '32%', size: 25, color: '#a78bfa', delay: '0.5s', duration: '6.8s', rotate: 10 },
    { icon: 'smartphone', top: '-6%', left: '48%', size: 20, color: '#34d399', delay: '2s', duration: '9s', rotate: -30 },
    { icon: 'bookOpen', top: '-9%', left: '62%', size: 23, color: '#fbbf24', delay: '0.8s', duration: '7.5s', rotate: 25 },
    { icon: 'watch', top: '-7%', left: '75%', size: 20, color: '#f472b6', delay: '1.6s', duration: '8s', rotate: -15 },
    { icon: 'shoppingBag', top: '-8%', left: '93%', size: 26, color: '#f97316', delay: '2.4s', duration: '9.5s', rotate: -10 },
    { icon: 'shirt', top: '-6%', left: '55%', size: 18, color: '#fb923c', delay: '3s', duration: '8.2s', rotate: 35 },
    { icon: 'package', top: '-9%', left: '40%', size: 20, color: '#60a5fa', delay: '1.8s', duration: '7.8s', rotate: -22 },
    { icon: 'hanger', top: '-5%', left: '70%', size: 21, color: '#f97316', delay: '3.5s', duration: '6.5s', rotate: 12 },
    { icon: 'smartphone', top: '-7%', left: '10%', size: 21, color: '#a3e635', delay: '2.8s', duration: '9.2s', rotate: -8 },
  ];

  return (
    <section className="schedule-intro schedule-home-intro" aria-labelledby="schedule-stage-title">
      <div className="home-hero-banner">
        <div className="home-floating-items" aria-hidden="true">
          <div className="home-hero-grid" />
          {bannerItems.map((item, index) => (
            <span
              key={`${item.icon}-${item.left}-${index}`}
              className="home-floating-item"
              style={
                {
                  top: item.top,
                  left: item.left,
                  width: item.size + 16,
                  height: item.size + 16,
                  color: item.color,
                  backgroundColor: `${item.color}18`,
                  border: `1px solid ${item.color}30`,
                  '--home-banner-left': item.left,
                  '--home-banner-duration': item.duration,
                  '--home-banner-delay': item.delay,
                  '--home-banner-rotate-start': `${item.rotate}deg`,
                  '--home-banner-rotate-end': `${item.rotate + 180}deg`,
                } as React.CSSProperties
              }
            >
              <span className="home-floating-item-inner" style={{ width: item.size, height: item.size }}>
                <HomeBannerItemIcon icon={item.icon} />
              </span>
            </span>
          ))}
          <span className="home-floating-fade" />
        </div>
        <div className="schedule-home-intro-copy">
          <h1 id="schedule-stage-title">
            <span>Returns, </span>
            <span className="home-hero-accent">without leaving home.</span>
          </h1>
          <p className="schedule-lede">
            No drop-offs, no lines—just leave your package out and we'll return it for you.
          </p>
        </div>
      </div>
    </section>
  );
}

function HomeValueSection(): React.ReactElement {
  const features: Array<{ icon: HomeFeatureKey; title: string; description: string; tone: 'orange' | 'blue' | 'green' }> = [
    {
      icon: 'wardrobe',
      title: 'Wardrobe Hauls',
      tone: 'orange',
      description: "Trying on five different wedding guest dresses? Return the ones you don't want at no additional cost per mail carrier.",
    },
    {
      icon: 'holiday',
      title: 'Post-holiday returns',
      tone: 'blue',
      description:
        "Returning unwanted gifts or swapping sizes? Add them to one order and we'll schedule the required carrier pickups.",
    },
    {
      icon: 'value',
      title: 'Clear pricing',
      tone: 'green',
      description:
        'Group packages into one order with one flat ReturnEm fee. Carrier and label charges are always shown before checkout.',
    },
  ];

  return (
    <section className="home-value-section" aria-labelledby="home-value-title">
      <div className="home-value-head">
        <span className="home-value-heading-icon" aria-hidden="true">
          <HomeFeatureIcon icon="value" />
        </span>
        <h2 id="home-value-title">Return more, save more.</h2>
        <p>Schedule all your packages in one order, with one pickup charge for each carrier you use.</p>
      </div>
      <div className="home-value-grid">
        {features.map((feature) => (
          <article className="home-value-card" key={feature.title}>
            <span className={`home-value-icon-shell tone-${feature.tone}`} aria-hidden="true">
              <HomeFeatureIcon icon={feature.icon} />
            </span>
            <strong>{feature.title}</strong>
            <p>{feature.description}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function HowItWorksIcon({
  kind,
  className = 'how-it-works-icon-svg',
}: {
  kind: 'package' | 'calendar' | 'check' | 'print' | 'clock' | 'truck' | 'pin';
  className?: string;
}): React.ReactElement {
  switch (kind) {
    case 'calendar':
      return (
        <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
          <path d="M5.5 3.8v2.4M14.5 3.8v2.4M4 6.2h12v10H4zM4 9.2h12" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
        </svg>
      );
    case 'check':
      return (
        <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
          <circle cx="10" cy="10" r="7" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="m6.8 10.3 2.1 2.2 4.5-5" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
        </svg>
      );
    case 'print':
      return (
        <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
          <path d="M6 6V3.5h8V6M5 14H3.5V8.5h13V14H15M6 11h8v5H6zM14.2 9.8h.01" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" />
        </svg>
      );
    case 'clock':
      return (
        <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
          <circle cx="10" cy="10" r="7.1" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M10 6.4v4l2.6 1.7" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
        </svg>
      );
    case 'truck':
      return (
        <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
          <path d="M3.8 6.5h7.1v6H3.8zM10.9 8.5h2.5l2.2 2.3v1.7h-4.7z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" />
          <circle cx="6.3" cy="14.2" r="1.2" fill="none" stroke="currentColor" strokeWidth="1.7" />
          <circle cx="13.7" cy="14.2" r="1.2" fill="none" stroke="currentColor" strokeWidth="1.7" />
        </svg>
      );
    case 'pin':
      return (
        <svg viewBox="0 0 20 20" className={className} aria-hidden="true">
          <path d="M10 16.4s4.2-4.5 4.2-7.4A4.2 4.2 0 1 0 5.8 9c0 2.9 4.2 7.4 4.2 7.4Zm0-5.6a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6Z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" />
        </svg>
      );
    case 'package':
    default:
      return <PackageCubeIcon className={className} />;
  }
}

function HowItWorksPage({ onScheduleClick }: { onScheduleClick: () => void }): React.ReactElement {
  const processSteps = [
    {
      number: '1',
      title: 'Enter pickup details',
      description: 'Tell us where and when to pick up your return.',
      tone: 'orange',
      icon: 'calendar' as const,
      listTitle: "You'll need:",
      items: ['Your pickup location', 'Contact information', 'Pickup date', 'Residential or commercial'],
    },
    {
      number: '2',
      title: 'Add package details',
      description: 'Tell us whether each package already has a prepaid label and which carrier is on it.',
      tone: 'blue',
      icon: 'package' as const,
      listTitle: "You'll need:",
      items: ['Carrier for existing labels', 'Package details when you need a label', 'Return destination when you need a label', 'Multiple packages supported'],
      note: "Need a label? Enter the return address and we'll email it with your confirmation.",
    },
    {
      number: '3',
      title: 'Confirm & pay',
      description:
        "Review your details and price, then authorize payment. If you requested a label, we'll attach it to your confirmation email after scheduling succeeds.",
      tone: 'green',
      icon: 'check' as const,
      listTitle: 'What you get:',
      items: ['Email confirmation', 'Return ID and pickup details', 'Requested labels when applicable', 'Order history after sign-in'],
    },
  ];
  const pickupDaySteps = [
    {
      title: 'Attach your label',
      description: 'Make sure each package has its shipping label attached',
      tone: 'purple',
      icon: 'print' as const,
    },
    {
      title: 'Get ready',
      description: 'Have your package ready during the pickup window',
      tone: 'orange',
      icon: 'clock' as const,
    },
    {
      title: 'Carrier arrives',
      description: 'The carrier collects your packages from the pickup address',
      tone: 'blue',
      icon: 'truck' as const,
    },
  ];
  return (
    <section className="how-it-works-page">
      <section className="how-it-works-hero">
        <span className="how-it-works-hero-icon-shell" aria-hidden="true">
          <HowItWorksIcon kind="package" />
        </span>
        <h1>How ReturnEm Works</h1>
        <p>
          Schedule a carrier pickup for your returns in minutes. No need to visit a drop-off location. We bring the
          service to you.
        </p>
      </section>

      <section className="how-it-works-intro">
        <span className="how-it-works-kicker">Simple process</span>
        <h2>Three easy steps</h2>
        <p>From booking to pickup, we&apos;ve made returns effortless</p>
      </section>

      <div className="how-it-works-steps-grid">
        {processSteps.map((step) => (
          <article className={`how-it-works-step-card is-${step.tone}`} key={step.number}>
            <span className="how-it-works-step-number">{step.number}</span>
            <span className={`how-it-works-step-icon-shell is-${step.tone}`} aria-hidden="true">
              <HowItWorksIcon kind={step.icon} />
            </span>
            <h3>{step.title}</h3>
            <p className="how-it-works-step-copy">{step.description}</p>
            <div className="how-it-works-step-list-box">
              <strong>{step.listTitle}</strong>
              <ul>
                {step.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            {step.note && (
              <div className="how-it-works-step-note">
                <span className="how-it-works-step-note-dot" aria-hidden="true" />
                <p>{step.note}</p>
              </div>
            )}
          </article>
        ))}
      </div>

      <section className="how-it-works-pickup-card">
        <div className="how-it-works-section-head">
          <h2>What happens on pickup day?</h2>
          <p>Here&apos;s what to expect during your pickup window</p>
        </div>
        <div className="how-it-works-pickup-grid">
          {pickupDaySteps.map((step) => (
            <article className="how-it-works-pickup-step" key={step.title}>
              <span className={`how-it-works-mini-icon-shell is-${step.tone}`} aria-hidden="true">
                <HowItWorksIcon kind={step.icon} />
              </span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="how-it-works-cta">
        <h2>Ready to schedule your pickup?</h2>
        <p>Book your return pickup in minutes and say goodbye to drop-off hassles.</p>
        <button type="button" className="button how-it-works-cta-button" onClick={onScheduleClick}>
          <span>Schedule a pickup now</span>
          <ArrowRightIcon />
        </button>
      </section>
    </section>
  );
}

type LegalPolicySection = {
  heading: string;
  intro?: React.ReactNode;
  paragraphs?: React.ReactNode[];
  listItems?: React.ReactNode[];
  outro?: React.ReactNode;
};

const REFUND_POLICY_SECTIONS: LegalPolicySection[] = [
  {
    heading: '1. Cancellations & Refunds',
    paragraphs: [
      <>
        We understand that plans change. You may cancel your pickup request for a full refund any time before the
        scheduled pickup date. Cancellations are not available on the pickup day.
      </>,
    ],
  },
  {
    heading: '2. Missed Pickups',
    paragraphs: [
      <>
        If the package is not ready, accessible, or available during the scheduled window, the pickup fee is not
        refundable. We do not offer a discounted rebooking rate for missed pickups. If a missed pickup leaves a
        ReturnEm-purchased label unused, contact support to request a label refund review.
      </>,
    ],
  },
  {
    heading: '3. Carrier Delays or Issues',
    paragraphs: [
      <>
        Refund eligibility for carrier issues depends on the pickup status, label status, and the carrier&apos;s
        current policy. If a pickup is not completed or a ReturnEm-purchased label remains unused, contact support and
        we will review the request.
      </>,
    ],
  },
  {
    heading: '4. Shipping Label Refunds',
    paragraphs: [
      <>
        If you purchased a shipping label through ReturnEm and did not use it, you must request any label refund
        within <strong>14 days</strong> of purchase. To be eligible, the label must not have been scanned, used, or
        previously refunded. Approved label refunds can take up to <strong>7 days</strong> to process, although your
        bank or card issuer may take additional time to post the credit.
      </>,
    ],
  },
  {
    heading: '5. How to Request a Refund',
    paragraphs: [
      <>
        To request a refund, please reply to your confirmation email or contact our support team directly at{' '}
        <a href={`mailto:${DEFAULT_SUPPORT_EMAIL}`}>{DEFAULT_SUPPORT_EMAIL}</a> with your Return ID. Please include
        the reason for the request and whether the label has been used. Our team will respond within 24 business
        hours.
      </>,
    ],
  },
];

const PRIVACY_POLICY_SECTIONS: LegalPolicySection[] = [
  {
    heading: '1. Scope of This Policy',
    paragraphs: [
      <>
        This Privacy Policy describes how ReturnEm handles personal information when you use our website to request a
        quote, schedule or manage a pickup, view return history, or contact us through the support form or by email.
      </>,
    ],
  },
  {
    heading: '2. Information We Collect',
    listItems: [
      <>
        <strong>Pickup and return details:</strong> We collect the pickup address, pickup date and time window,
        carrier selection, package weight and dimensions, and destination address details when a shipping label is
        needed.
      </>,
      <>
        <strong>Contact details:</strong> We collect your email address to start checkout and to look up a return. You
        may also choose to provide a phone number during checkout.
      </>,
      <>
        <strong>Payment details:</strong> If payment is required, payment information is collected and processed by
        Stripe. We do not store full card numbers or card security codes on our website.
      </>,
      <>
        <strong>Return status details:</strong> We receive and display information tied to your return, such as your
        Return ID, booking status, and carrier confirmation number.
      </>,
      <>
        <strong>Support communications:</strong> If you contact us through the support form or by email, we collect
        the name, email address, topic, message, and any other information you include.
      </>,
      <>
        <strong>Anti-abuse signals:</strong> When you request a pickup quote or use the support form, Cloudflare
        Turnstile processes browser,
        device, network, and interaction signals needed to distinguish people from automated traffic. It does not
        receive the pickup or support-form fields you enter.
      </>,
    ],
  },
  {
    heading: '3. Sources of Information',
    paragraphs: [
      <>
        Most information comes directly from you. We also receive information from service providers you use through
        the site, including Google Places for address autocomplete, Stripe for payment processing, Cloudflare
        Turnstile for pickup-request and support-form abuse prevention, and our booking or carrier systems for quote, scheduling, and
        status information.
      </>,
    ],
  },
  {
    heading: '4. How We Use Your Information',
    intro: <>We use personal information to operate, support, and protect the service, including to:</>,
    listItems: [
      <>Create quotes, schedule pickups, request carrier labels, and maintain return records.</>,
      <>Process payments and display order and pickup status information.</>,
      <>Send transactional communications related to your return, such as confirmations, requested labels, and support replies.</>,
      <>Troubleshoot issues, measure site activity, and protect the service against misuse.</>,
    ],
  },
  {
    heading: '5. How We Share Information',
    intro: <>We share information only as needed to provide the service and comply with law, including with:</>,
    listItems: [
      <>
        <strong>Carriers and logistics partners:</strong> We share pickup, package, and destination information with
        partners such as FedEx, UPS, and USPS when needed to quote, label, schedule, or complete a pickup.
      </>,
      <>
        <strong>Payment providers:</strong> We share transaction and billing details with Stripe to process payments.
      </>,
      <>
        <strong>Address autocomplete provider:</strong> When you use address search, your browser connects to Google
        Places so it can return address suggestions and selected address details.
      </>,
      <>
        <strong>Analytics providers:</strong> We use Google Analytics and similar tools to understand how visitors use
        the site. These providers may receive information such as pages viewed, approximate location, browser and
        device information, and interactions with the site.
      </>,
      <>
        <strong>Security provider:</strong> Pickup quote requests and the support form connect to Cloudflare Turnstile
        so it can evaluate the browser and network signals needed to detect automated abuse. Pickup and support-form
        entries are sent only to ReturnEm, not to Turnstile.
      </>,
      <>
        <strong>Service providers and legal disclosures:</strong> We may share information with vendors that host or
        secure the site, and when required by law or to protect rights and safety.
      </>,
    ],
    outro: <>We do not sell or rent your personal information to third parties for marketing purposes.</>,
  },
  {
    heading: '6. Browser Storage and Site Tools',
    paragraphs: [
      <>
        If you select <strong>Remember my info on this device</strong>, we store your pickup address, email address,
        and optional phone number in this browser&apos;s local storage to prefill a future pickup form. Unchecking the
        option removes that saved information from this browser.
      </>,
      <>
        We use temporary browser storage to restore an in-progress pickup form and open the correct return from a
        secure email link. Information used by a secure email link remains only in that browser tab and is removed
        when the session ends. Payment card details are not stored there.
      </>,
      <>
        We use Google Analytics and similar analytics tools to measure traffic and understand how visitors use the site.
        These tools may use cookies and similar technologies to collect information such as pages viewed, approximate
        location, browser and device information, and interactions with the site. We use this information
        to improve the site and troubleshoot issues. We do not use our website to serve personalized ads or set our
        own advertising cookies.
      </>,
    ],
  },
  {
    heading: '7. Data Retention',
    paragraphs: [
      <>
        Information stored in your browser session remains there until the tab or browser session ends, unless you
        clear it sooner. Information saved through <strong>Remember my info on this device</strong> remains in local
        storage until you uncheck that option or clear this site&apos;s browser data. Information processed through
        ReturnEm, payment providers, and carriers may retain information as needed to complete returns, keep business
        and tax records, resolve disputes, prevent fraud, and comply with legal obligations.
      </>,
    ],
  },
  {
    heading: '8. Data Security',
    paragraphs: [
      <>
        We use administrative, technical, and physical safeguards designed to protect personal information. No method
        of transmission or storage is completely secure, so we cannot guarantee absolute security.
      </>,
    ],
  },
  {
    heading: '9. Your Choices and Rights',
    listItems: [
      <>You can choose whether to provide a phone number during checkout, but an email address is required to schedule or look up a return.</>,
      <>You can opt in to remembering your pickup and contact information on a device and remove it by unchecking the option.</>,
      <>You can clear browser local and session storage through your browser settings.</>,
      <>
        Depending on where you live, you may have privacy rights such as requesting access to, correction of, or
        deletion of certain personal information, subject to exceptions and verification requirements.
      </>,
    ],
  },
  {
    heading: "10. Children's Privacy",
    paragraphs: [
      <>
        Our services are not directed to children under 13, and we do not knowingly collect personal information
        directly from children under 13.
      </>,
    ],
  },
  {
    heading: '11. Changes to This Policy',
    paragraphs: [
      <>
        We may update this Privacy Policy from time to time. When we do, we will revise the Last updated date and post
        the revised version on this page.
      </>,
    ],
  },
  {
    heading: '12. Contact Us',
    paragraphs: [
      <>
        If you have questions about this Privacy Policy or want to submit a privacy request, please email{' '}
        <a href={`mailto:${DEFAULT_PRIVACY_EMAIL}`}>{DEFAULT_PRIVACY_EMAIL}</a> or contact support at{' '}
        <a href={`mailto:${DEFAULT_SUPPORT_EMAIL}`}>{DEFAULT_SUPPORT_EMAIL}</a>.
      </>,
    ],
  },
];

function LegalPolicyPage({
  title,
  updatedAt,
  sections,
}: {
  title: string;
  updatedAt: string;
  sections: LegalPolicySection[];
}): React.ReactElement {
  return (
    <section className="legal-policy-page">
      <div className="legal-policy-head">
        <h1>{title}</h1>
        <p className="legal-policy-updated">Last updated: {updatedAt}</p>
      </div>

      <section className="legal-policy-card">
        {sections.map((section, index) => (
          <article className={`legal-policy-section ${index === 0 ? 'is-first' : ''}`} key={section.heading}>
            <h2>{section.heading}</h2>
            {section.intro && <p className="legal-policy-copy">{section.intro}</p>}
            {section.paragraphs?.map((paragraph, paragraphIndex) => (
              <p className="legal-policy-copy" key={`${section.heading}-paragraph-${paragraphIndex + 1}`}>
                {paragraph}
              </p>
            ))}
            {section.listItems && (
              <div className="legal-policy-list" role="list">
                {section.listItems.map((item, itemIndex) => (
                  <p className="legal-policy-copy legal-policy-list-item" key={`${section.heading}-item-${itemIndex + 1}`} role="listitem">
                    {item}
                  </p>
                ))}
              </div>
            )}
            {section.outro && <p className="legal-policy-copy">{section.outro}</p>}
          </article>
        ))}
      </section>
    </section>
  );
}

function SkeletonLines({ rows = 4 }: SkeletonLinesProps): React.ReactElement {
  const widths = ['100%', '92%', '84%', '76%', '88%'];

  return (
    <div className="skeleton-stack" aria-hidden="true">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={`skeleton-${index + 1}`} className="skeleton-line" style={{ width: widths[index % widths.length] }} />
      ))}
    </div>
  );
}

function ErrorNavigationManager(): null {
  useEffect(() => {
    const root = document.getElementById('root') ?? document.body;
    const pendingAlerts = new Set<HTMLElement>();
    let pendingTimer: number | null = null;

    const queueAlerts = (alerts: ArrayLike<HTMLElement>): void => {
      Array.from(alerts).forEach((alert) => pendingAlerts.add(alert));

      if (pendingTimer !== null) {
        return;
      }

      pendingTimer = window.setTimeout(() => {
        pendingTimer = null;
        const target = Array.from(root.querySelectorAll<HTMLElement>('[role="alert"]')).find(
          (alert) => pendingAlerts.has(alert) && alert.isConnected,
        );
        pendingAlerts.clear();

        if (!target) {
          return;
        }

        if (!target.hasAttribute('tabindex')) {
          target.setAttribute('tabindex', '-1');
        }

        try {
          target.focus({ preventScroll: true });
        } catch {
          target.focus();
        }

        target.scrollIntoView?.({
          behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'center',
        });
      }, 0);
    };

    const getAlerts = (node: Node): HTMLElement[] => {
      const element = node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement;
      if (!element) {
        return [];
      }

      const alerts = Array.from(element.querySelectorAll<HTMLElement>('[role="alert"]'));
      if (element.matches('[role="alert"]')) {
        alerts.unshift(element);
      }
      return alerts;
    };

    const observer = new MutationObserver((mutations) => {
      const changedAlerts = mutations.flatMap((mutation) => {
        if (mutation.type === 'characterData') {
          const alert = mutation.target.parentElement?.closest<HTMLElement>('[role="alert"]');
          return alert ? [alert] : [];
        }

        return Array.from(mutation.addedNodes).flatMap(getAlerts);
      });

      if (changedAlerts.length > 0) {
        queueAlerts(changedAlerts);
      }
    });

    observer.observe(root, { childList: true, subtree: true, characterData: true });
    queueAlerts(root.querySelectorAll<HTMLElement>('[role="alert"]'));

    return () => {
      observer.disconnect();
      if (pendingTimer !== null) {
        window.clearTimeout(pendingTimer);
      }
    };
  }, []);

  return null;
}

function scrollCheckoutStageToTop(): void {
  window.setTimeout(() => {
    window.scrollTo({
      top: 0,
      behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }, 0);
}

function PaymentCardForm({
  email,
  phone,
  returnUrl,
  onPaymentConfirmed,
  showTopRow = true,
  submitLabel = 'Confirm & Schedule Pickup',
  showFieldLabel = true,
  footnoteText = '*Available methods depend on your location and device.',
  disabled = false,
}: PaymentCardFormProps): React.ReactElement {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const paymentElementOptions = useMemo(
    () => ({
      layout: 'tabs' as const,
      wallets: {
        applePay: 'auto' as const,
        googlePay: 'never' as const,
        link: 'never' as const,
      },
      defaultValues: {
        billingDetails: {
          email,
          phone,
        },
      },
      fields: {
        billingDetails: {
          email: 'auto' as const,
          phone: 'auto' as const,
        },
      },
    }),
    [email, phone],
  );

  const getPaymentErrorMessage = (message?: string | null): string =>
    message ?? "We couldn't process that payment. Please try another payment method.";

  const confirmSelectedPaymentMethod = async (): Promise<PaymentConfirmationResult> => {
    if (!stripe || !elements) {
      return { status: 'failed', message: getPaymentErrorMessage() };
    }

    const submitResult = await elements.submit();
    if (submitResult.error) {
      return { status: 'failed', message: getPaymentErrorMessage(submitResult.error.message) };
    }

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl,
        payment_method_data: {
          billing_details: {
            email,
            phone,
          },
        },
      },
      redirect: 'if_required',
    });

    if (result.error) {
      return { status: 'failed', message: getPaymentErrorMessage(result.error.message) };
    }

    const paymentIntentStatus = result.paymentIntent?.status;
    if (
      paymentIntentStatus === 'succeeded' ||
      paymentIntentStatus === 'requires_capture' ||
      paymentIntentStatus === 'processing'
    ) {
      trackEvent('payment_confirmed');
      return { status: 'confirmed' };
    }

    return { status: 'failed', message: getPaymentErrorMessage() };
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    if (!stripe || !elements || isSubmitting || disabled) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    const result = await confirmSelectedPaymentMethod();
    setIsSubmitting(false);

    if (result.status === 'confirmed') {
      onPaymentConfirmed();
      return;
    }

    setErrorMessage(result.message);
  };

  return (
    <form onSubmit={handleSubmit} className="stack payment-inline-form">
      {showTopRow && (
        <div className="payment-top-row" aria-hidden="true">
          <span className="payment-secure">Secure checkout</span>
          <div className="payment-method-chips">
            <span>Cards</span>
            <span>Wallets*</span>
          </div>
        </div>
      )}
      {showFieldLabel ? (
        <label className="field">
          <span>Payment method</span>
          <div className="payment-element-wrap">
            <PaymentElement options={paymentElementOptions} />
          </div>
        </label>
      ) : (
        <div className="payment-field-shell">
          <div className="payment-element-wrap">
            <PaymentElement options={paymentElementOptions} />
          </div>
        </div>
      )}
      {footnoteText && <p className="muted payment-footnote">{footnoteText}</p>}

      {errorMessage && (
        <p className="alert error" role="alert">
          {errorMessage}
        </p>
      )}

      <div className="button-row">
        <button type="submit" className="button primary" disabled={!stripe || isSubmitting || disabled}>
          {isSubmitting ? 'Confirming...' : submitLabel}
        </button>
      </div>
    </form>
  );
}

function App(): React.ReactElement {
  const addressInputRef = useRef<HTMLInputElement | null>(null);
  const addressAutocompleteRef = useRef<any>(null);
  const packageDestinationInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const packageDestinationInputCallbackRefs = useRef<Record<string, (input: HTMLInputElement | null) => void>>({});
  const packageDestinationAutocompleteRefs = useRef<Record<string, any>>({});
  const ignoreNextAddressInputChangeRef = useRef(false);
  const ignoreNextPackageDestinationInputChangeRef = useRef<Record<string, boolean>>({});
  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const auth = useReturnemAuth();
  const initialIntakeProgressRef = useRef<IntakeProgressSnapshot | null>(readIntakeProgressSnapshot());
  const initialCheckoutFlowHistoryStepRef = useRef<CheckoutFlowHistoryStep | null>(
    readCheckoutFlowHistoryStep(),
  );
  const initialActiveQuoteIdRef = useRef(readActiveQuoteId());
  const initialGuestPickupInfoRef = useRef<GuestPickupInfoSnapshot | null>(
    !auth.isLoading ? readGuestPickupInfoSnapshot() : null,
  );
  const hasCheckedSavedGuestPickupInfoRef = useRef(!auth.isLoading);
  const [route, setRoute] = useState<AppRoute>(() => parseRoute(window.location.pathname));
  const [returnManagementToken, setReturnManagementToken] = useState(() =>
    route.kind === 'accountReturn' ? consumeReturnManagementToken(route.returnId) : '',
  );
  const [signInStage, setSignInStage] = useState<SignInStage>('email');
  const [signInEmail, setSignInEmail] = useState('');
  const [signInCode, setSignInCode] = useState('');
  const [signInSentEmail, setSignInSentEmail] = useState('');
  const [signInError, setSignInError] = useState('');
  const [signInBusy, setSignInBusy] = useState(false);
  const [accountReturns, setAccountReturns] = useState<AccountReturnSummary[]>([]);
  const [accountReturnsLoading, setAccountReturnsLoading] = useState(false);
  const [accountReturnsError, setAccountReturnsError] = useState('');
  const [accountReturnDetailsBusyId, setAccountReturnDetailsBusyId] = useState('');
  const [isAccountMenuOpen, setAccountMenuOpen] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState<CheckoutStep>('form');
  const [intakeStep, setIntakeStep] = useState<IntakeStep>(() => {
    if (initialCheckoutFlowHistoryStepRef.current === 'pickup') {
      return 1;
    }

    return initialIntakeProgressRef.current?.intakeStep ?? 1;
  });
  const showingPickupBasics = intakeStep === 1;
  const [form, setForm] = useState<FormState>(() =>
    initialIntakeProgressRef.current?.form ??
    applyGuestPickupInfoSnapshot(createInitialFormState(), initialGuestPickupInfoRef.current),
  );
  const [rememberPickupInfo, setRememberPickupInfo] = useState(() =>
    Boolean(initialGuestPickupInfoRef.current),
  );
  const [confirmationSnapshot, setConfirmationSnapshot] = useState<FormState | null>(null);
  const anonymousCheckoutEmailRef = useRef(createSystemCheckoutEmail());
  const [expandedPackageId, setExpandedPackageId] = useState(
    initialIntakeProgressRef.current?.expandedPackageId ??
      form.packageCards[form.packageCards.length - 1]?.id ??
      '',
  );
  const hasInitializedCheckoutHistoryRef = useRef(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [addressAutocompleteError, setAddressAutocompleteError] = useState('');
  const [packageDestinationAutocompleteErrors, setPackageDestinationAutocompleteErrors] = useState<Record<string, string>>({});
  const [checkoutStartData, setCheckoutStartData] = useState<CheckoutStartResponse | null>(null);
  const [zeroDollarQuote, setZeroDollarQuote] = useState<PickupQuoteResponse | null>(null);
  const [restoredPaidQuote, setRestoredPaidQuote] = useState<PickupQuoteResponse | null>(null);
  const [checkoutStatus, setCheckoutStatus] = useState<CheckoutStatusResponse | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [pickupTurnstileActive, setPickupTurnstileActive] = useState(false);
  const pickupQuoteAttemptCounterRef = useRef(0);
  const activePickupQuoteAttemptRef = useRef<number | null>(null);
  const pendingPickupQuoteFormRef = useRef<{ form: FormState; attemptId: number } | null>(null);
  const [globalError, setGlobalError] = useState('');
  const [confirmationTick, setConfirmationTick] = useState(0);
  const [confirmationConnectionIssue, setConfirmationConnectionIssue] = useState(false);
  const [confirmationDelayed, setConfirmationDelayed] = useState(false);
  const [quoteClock, setQuoteClock] = useState(() => Date.now());
  const [manageStatus, setManageStatus] = useState<CheckoutStatusResponse | null>(null);
  const [manageSnapshot, setManageSnapshot] = useState<FormState | null>(null);
  const [manageCancelBusy, setManageCancelBusy] = useState(false);
  const [manageCancelError, setManageCancelError] = useState('');
  const [manageCancelNotice, setManageCancelNotice] = useState('');
  const [manageCancelConfirmOpen, setManageCancelConfirmOpen] = useState(false);
  const [manageSupportReadOnly, setManageSupportReadOnly] = useState(false);
  const [helpForm, setHelpForm] = useState<HelpFormState>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [helpNotice, setHelpNotice] = useState('');
  const [helpError, setHelpError] = useState('');
  const [helpSubmitting, setHelpSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetSignal, setTurnstileResetSignal] = useState(0);
  const [expandedHelpFaqId, setExpandedHelpFaqId] = useState<string | null>(null);

  const supportEmail = DEFAULT_SUPPORT_EMAIL;
  const checkoutEmail = form.email.trim() || anonymousCheckoutEmailRef.current;
  const uspsWeekendPickupWarning = getUspsWeekendPickupWarning(form);
  const isSupportReturnsView = auth.isAuthenticated && isSupportAccountEmail(auth.userEmail);
  const canRememberGuestPickupInfo = !auth.isLoading;

  useLayoutEffect(() => {
    if (route.kind !== 'checkout' || checkoutStep !== 'review') {
      return;
    }

    // Review replaces a much taller Package Details screen. Reposition after React
    // commits the new stage so the browser cannot preserve the old scroll offset.
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [checkoutStep, route.kind]);

  const setPackageDestinationAutocompleteError = (packageId: string, message: string): void => {
    setPackageDestinationAutocompleteErrors((previous) => {
      if (!message) {
        if (!(packageId in previous)) {
          return previous;
        }

        const next = { ...previous };
        delete next[packageId];
        return next;
      }

      if (previous[packageId] === message) {
        return previous;
      }

      return {
        ...previous,
        [packageId]: message,
      };
    });
  };

  const clearPackageDestinationAutocomplete = (packageId: string): void => {
    const googleMaps = (window as any).google?.maps;
    const autocomplete = packageDestinationAutocompleteRefs.current[packageId];

    if (autocomplete && googleMaps?.event?.clearInstanceListeners) {
      googleMaps.event.clearInstanceListeners(autocomplete);
    }

    delete packageDestinationAutocompleteRefs.current[packageId];
    delete ignoreNextPackageDestinationInputChangeRef.current[packageId];
  };

  const initializePackageDestinationAutocomplete = (packageId: string, inputElement: HTMLInputElement): void => {
    if (packageDestinationAutocompleteRefs.current[packageId]) {
      return;
    }

    if (!googleMapsApiKey) {
      setPackageDestinationAutocompleteError(packageId, "Address search isn't available right now.");
      return;
    }

    setPackageDestinationAutocompleteError(packageId, '');

    void loadGooglePlacesScript(googleMapsApiKey)
      .then(() => {
        if (packageDestinationInputRefs.current[packageId] !== inputElement || packageDestinationAutocompleteRefs.current[packageId]) {
          return;
        }

        const googleMaps = (window as any).google?.maps;
        if (!googleMaps?.places?.Autocomplete) {
          setPackageDestinationAutocompleteError(packageId, "Address search isn't available right now.");
          return;
        }

        const autocomplete = new googleMaps.places.Autocomplete(inputElement, {
          componentRestrictions: { country: 'us' },
          types: ['address'],
          fields: ['address_components', 'formatted_address'],
        });

        packageDestinationAutocompleteRefs.current[packageId] = autocomplete;

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace?.();
          const parsed = parseGooglePlaceAddress(place);

          if (!parsed.line1 || !parsed.city || !parsed.state || !parsed.postalCode) {
            setPackageDestinationAutocompleteError(packageId, 'Please choose the full address from the list.');
            return;
          }

          setPackageDestinationAutocompleteError(packageId, '');
          ignoreNextPackageDestinationInputChangeRef.current[packageId] = true;
          setForm((previous) => ({
            ...previous,
            packageCards: previous.packageCards.map((packageCard) =>
              packageCard.id === packageId
                ? {
                    ...packageCard,
                    destinationAddressSearch: parsed.formattedAddress || packageCard.destinationAddressSearch,
                    destinationLine1: parsed.line1,
                    destinationLine2: '',
                    destinationCity: parsed.city,
                    destinationState: parsed.state,
                    destinationPostalCode: parsed.postalCode,
                  }
                : packageCard,
            ),
          }));
          setFormErrors((previous) => {
            const next = { ...previous };
            delete next[getPackageFieldErrorKey(packageId, 'destinationLine1')];
            delete next[getPackageFieldErrorKey(packageId, 'destinationCity')];
            delete next[getPackageFieldErrorKey(packageId, 'destinationState')];
            delete next[getPackageFieldErrorKey(packageId, 'destinationPostalCode')];
            return next;
          });
        });
      })
      .catch(() => {
        if (packageDestinationInputRefs.current[packageId] === inputElement) {
          setPackageDestinationAutocompleteError(packageId, "We couldn't load address search right now. Please try again.");
        }
      });
  };

  const getPackageDestinationInputRef = (packageId: string): ((input: HTMLInputElement | null) => void) => {
    if (!packageDestinationInputCallbackRefs.current[packageId]) {
      packageDestinationInputCallbackRefs.current[packageId] = (inputElement: HTMLInputElement | null) => {
        if (inputElement) {
          packageDestinationInputRefs.current[packageId] = inputElement;
          initializePackageDestinationAutocomplete(packageId, inputElement);
          return;
        }

        delete packageDestinationInputRefs.current[packageId];
        delete packageDestinationInputCallbackRefs.current[packageId];
        clearPackageDestinationAutocomplete(packageId);
      };
    }

    return packageDestinationInputCallbackRefs.current[packageId];
  };

  useEffect(() => {
    trackPage('landing');
    trackPage('form');
  }, []);

  useEffect(() => {
    if (route.kind === 'managePickup') {
      trackPage('manage');
    } else if (route.kind === 'signIn') {
      trackPage('sign_in');
    } else if (route.kind === 'accountReturns') {
      trackPage('account_returns');
    } else if (route.kind === 'sellDonate') {
      trackPage('sell_donate');
    } else if (route.kind === 'howItWorks') {
      trackPage('how_it_works');
    } else if (route.kind === 'refundPolicy') {
      trackPage('refund_policy');
    } else if (route.kind === 'privacyPolicy') {
      trackPage('privacy_policy');
    }
  }, [route.kind]);

  useEffect(() => {
    trackRouteView(getAnalyticsPath(route));
  }, [route]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      setAccountMenuOpen(false);
    }
  }, [auth.isAuthenticated]);

  useEffect(() => {
    if (hasCheckedSavedGuestPickupInfoRef.current || auth.isLoading) {
      return;
    }

    hasCheckedSavedGuestPickupInfoRef.current = true;

    const savedPickupInfo = readGuestPickupInfoSnapshot();
    if (!savedPickupInfo) {
      return;
    }

    setRememberPickupInfo(true);
    if (initialIntakeProgressRef.current) {
      return;
    }

    setForm((previous) => {
      const hasPickupAddress = Boolean(
        previous.addressSearch || previous.line1 || previous.city || previous.state || previous.postalCode,
      );

      const withSavedAddress = hasPickupAddress ? previous : applyGuestPickupInfoSnapshot(previous, savedPickupInfo);
      return {
        ...withSavedAddress,
        email: previous.email.trim() ? previous.email : savedPickupInfo.email,
        phone: previous.phone.trim() ? previous.phone : savedPickupInfo.phone,
      };
    });
  }, [auth.isLoading]);

  useEffect(() => {
    if (!isAccountMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent): void => {
      if (!accountMenuRef.current?.contains(event.target as Node)) {
        setAccountMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setAccountMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAccountMenuOpen]);

  useEffect(() => {
    if (!form.packageCards.length) {
      return;
    }

    if (expandedPackageId && !form.packageCards.some((packageCard) => packageCard.id === expandedPackageId)) {
      setExpandedPackageId(form.packageCards[form.packageCards.length - 1].id);
    }
  }, [expandedPackageId, form.packageCards]);

  useEffect(() => {
    if (route.kind !== 'checkout' || checkoutStep !== 'form') {
      return;
    }

    saveIntakeProgressSnapshot(form, intakeStep, expandedPackageId);
  }, [checkoutStep, expandedPackageId, form, intakeStep, route.kind]);

  useEffect(() => {
    if (route.kind !== 'checkout' || hasInitializedCheckoutHistoryRef.current) {
      return;
    }

    hasInitializedCheckoutHistoryRef.current = true;
    const historyStep = initialCheckoutFlowHistoryStepRef.current;
    const restoredStep = initialIntakeProgressRef.current?.intakeStep ?? 1;

    if (historyStep === 'pickup') {
      setCheckoutStep('form');
      setIntakeStep(1);
      return;
    }

    if (historyStep === 'packages' && initialIntakeProgressRef.current) {
      setCheckoutStep('form');
      setIntakeStep(2);
      return;
    }

    if (historyStep === 'review') {
      const activeQuoteId = initialActiveQuoteIdRef.current;
      if (!activeQuoteId) {
        setCheckoutStep('form');
        setIntakeStep(restoredStep);
        window.history.back();
        return;
      }

      let cancelled = false;
      setIsBusy(true);
      void getPickupQuoteById(activeQuoteId)
        .then((snapshot) => {
          if (cancelled) {
            return;
          }
          if (snapshot.quote.quoteId !== activeQuoteId) {
            throw new Error("We couldn't restore that quote. Please get a new quote.");
          }
          const restoredForm = formFromStoredQuote(snapshot, form);
          setForm(restoredForm);
          setConfirmationSnapshot(restoredForm);
          setCheckoutStartData(null);
          if (getQuoteTotalAmount(snapshot.quote) <= 0) {
            setZeroDollarQuote(snapshot.quote);
            setRestoredPaidQuote(null);
          } else {
            setZeroDollarQuote(null);
            setRestoredPaidQuote(snapshot.quote);
          }
          setCheckoutStep('review');
          setIntakeStep(2);
          trackPage('review');
        })
        .catch((error) => {
          if (cancelled) {
            return;
          }
          clearActiveQuoteId();
          replaceCheckoutFlowHistoryStep('packages');
          setCheckoutStep('form');
          setIntakeStep(2);
          setGlobalError(
            error instanceof ApiError && error.status === 404
              ? "We couldn't restore that quote. Please get a new quote."
              : toErrorMessage(error),
          );
        })
        .finally(() => {
          if (!cancelled) {
            setIsBusy(false);
          }
        });

      return () => {
        cancelled = true;
      };
    }

    if (restoredStep === 2) {
      replaceCheckoutFlowHistoryStep('pickup');
      pushCheckoutFlowHistoryStep('packages');
      setCheckoutStep('form');
      setIntakeStep(2);
      return;
    }

    replaceCheckoutFlowHistoryStep('pickup');
    setCheckoutStep('form');
    setIntakeStep(1);
  }, [form, route.kind]);

  useEffect(() => {
    if (route.kind === 'confirmation') {
      clearIntakeProgressSnapshot();
      clearActiveQuoteId();
      setRestoredPaidQuote(null);
    }
  }, [route.kind]);

  useEffect(() => {
    if (route.kind !== 'confirmation') {
      setConfirmationSnapshot(null);
      return;
    }

    setConfirmationSnapshot(readConfirmationSnapshot(route.returnId));
  }, [route]);

  useEffect(() => {
    const previousAuthFailureHandler = (window as any).gm_authFailure;
    (window as any).gm_authFailure = () => {
      setAddressAutocompleteError("We couldn't load address search right now. Please try again.");
      if (typeof previousAuthFailureHandler === 'function') {
        previousAuthFailureHandler();
      }
    };

    return () => {
      if (typeof previousAuthFailureHandler === 'function') {
        (window as any).gm_authFailure = previousAuthFailureHandler;
      } else {
        delete (window as any).gm_authFailure;
      }
    };
  }, []);

  useEffect(() => {
    if (route.kind !== 'checkout' || checkoutStep !== 'form' || !showingPickupBasics) {
      return;
    }

    if (!googleMapsApiKey) {
      setAddressAutocompleteError("Address search isn't available right now.");
      return;
    }

    const inputElement = addressInputRef.current;
    if (!inputElement) {
      return;
    }

    let cancelled = false;
    setAddressAutocompleteError('');

    void loadGooglePlacesScript(googleMapsApiKey)
      .then(() => {
        if (cancelled) {
          return;
        }

        const googleMaps = (window as any).google?.maps;
        if (!googleMaps?.places?.Autocomplete) {
          setAddressAutocompleteError("Address search isn't available right now.");
          return;
        }

        const autocomplete = new googleMaps.places.Autocomplete(inputElement, {
          componentRestrictions: { country: 'us' },
          types: ['address'],
          fields: ['address_components', 'formatted_address'],
        });

        addressAutocompleteRef.current = autocomplete;

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace?.();
          const parsed = parseGooglePlaceAddress(place);

          if (!parsed.line1 || !parsed.city || !parsed.state || !parsed.postalCode) {
            setAddressAutocompleteError('Please choose your full address from the list.');
            return;
          }

          setAddressAutocompleteError('');
          // Selecting a suggestion can trigger a subsequent input change event.
          // Ignore that one so we do not clear parsed address fields.
          ignoreNextAddressInputChangeRef.current = true;
          setForm((previous) => ({
            ...previous,
            addressSearch: parsed.formattedAddress || previous.addressSearch,
            line1: parsed.line1,
            line2: previous.line2 || parsed.line2,
            city: parsed.city,
            state: parsed.state,
            postalCode: parsed.postalCode,
          }));
          setFormErrors((previous) => {
            const next = { ...previous };
            delete next.addressSearch;
            delete next.line1;
            delete next.city;
            delete next.state;
            delete next.postalCode;
            return next;
          });
        });
      })
      .catch(() => {
        if (!cancelled) {
          setAddressAutocompleteError("We couldn't load address search right now. Please try again.");
        }
      });

    return () => {
      cancelled = true;
      const googleMaps = (window as any).google?.maps;
      if (addressAutocompleteRef.current && googleMaps?.event?.clearInstanceListeners) {
        googleMaps.event.clearInstanceListeners(addressAutocompleteRef.current);
      }
      addressAutocompleteRef.current = null;
    };
  }, [checkoutStep, route.kind, showingPickupBasics]);

  useEffect(() => {
    const onPopState = (event: PopStateEvent): void => {
      const nextRoute = parseRoute(window.location.pathname);
      setRoute(nextRoute);

      if (activePickupQuoteAttemptRef.current !== null) {
        activePickupQuoteAttemptRef.current = null;
        pendingPickupQuoteFormRef.current = null;
        setPickupTurnstileActive(false);
        setIsBusy(false);
      }

      if (nextRoute.kind !== 'checkout') {
        return;
      }

      const historyStep = readCheckoutFlowHistoryStep(event.state);
      setGlobalError('');

      if (historyStep === 'review' && (checkoutStartData || zeroDollarQuote || restoredPaidQuote)) {
        setCheckoutStep('review');
        setIntakeStep(2);
        trackPage('review');
      } else if (historyStep === 'packages') {
        setCheckoutStep('form');
        setIntakeStep(2);
        setCheckoutStartData(null);
        setZeroDollarQuote(null);
        setRestoredPaidQuote(null);
        setCheckoutStatus(null);
        clearActiveQuoteId();
        trackPage('form');
      } else {
        if (historyStep === 'review') {
          window.history.back();
        } else if (!historyStep) {
          replaceCheckoutFlowHistoryStep('pickup');
        }
        setCheckoutStep('form');
        setIntakeStep(historyStep === 'review' && initialIntakeProgressRef.current?.intakeStep === 2 ? 2 : 1);
        setCheckoutStartData(null);
        setZeroDollarQuote(null);
        setRestoredPaidQuote(null);
        setCheckoutStatus(null);
        trackPage('form');
      }

      scrollCheckoutStageToTop();
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [checkoutStartData, restoredPaidQuote, zeroDollarQuote]);

  useEffect(() => {
    if (route.kind === 'checkout' && window.location.pathname === '/sell-or-donate') {
      window.history.replaceState({}, '', '/');
    }

    if (route.kind === 'signIn' && /^\/receipt\/[^/]+$/.test(window.location.pathname)) {
      window.history.replaceState({}, '', '/sign-in');
    }
  }, [route.kind]);

  useEffect(() => {
    if (route.kind !== 'confirmation') {
      return;
    }

    trackPage('confirmation');
    setCheckoutStatus(null);
    setConfirmationTick(0);
    setConfirmationConnectionIssue(false);
    setConfirmationDelayed(false);
    setGlobalError('');

    const confirmationReturnId = route.returnId;
    const waitStartedAt = readOrCreateConfirmationWaitStartedAt(confirmationReturnId);
    let trackedTerminalState: BookingState | null = null;
    let stopped = false;
    let pollInFlight = false;
    let consecutiveErrors = 0;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let delayedTimer: ReturnType<typeof setTimeout> | null = null;

    const stopStatusUpdates = (): void => {
      stopped = true;
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
      if (delayedTimer) {
        clearTimeout(delayedTimer);
        delayedTimer = null;
      }
    };

    const handleStatus = (status: CheckoutStatusResponse): void => {
      setCheckoutStatus(status);
      setConfirmationConnectionIssue(false);
      setGlobalError('');

      if (isTerminalState(status.state) && trackedTerminalState !== status.state) {
        trackedTerminalState = status.state;
        clearConfirmationWaitStartedAt(confirmationReturnId);
        setConfirmationDelayed(false);
        if (isSuccessState(status.state)) {
          trackEvent('scheduled_success', { returnId: resolveReturnId(status), state: status.state });
        } else if (status.state === 'FAILED_SCHEDULING') {
          trackEvent('scheduled_fail', { returnId: resolveReturnId(status), state: status.state });
        } else if (status.state === 'CANCELLED_PICKUP') {
          trackEvent('pickup_cancelled', { returnId: resolveReturnId(status), state: status.state });
        } else {
          trackEvent('payment_fail', { returnId: resolveReturnId(status), state: status.state });
        }
        stopStatusUpdates();
      }
    };

    const updateDelayedState = (): number => {
      const elapsed = Math.max(0, Date.now() - waitStartedAt);
      setConfirmationDelayed(elapsed >= ACTIVE_CONFIRMATION_WAIT_MS);
      return elapsed;
    };

    const nextPollDelay = (elapsed: number): number => {
      if (document.visibilityState === 'hidden') {
        return CONFIRMATION_HIDDEN_POLL_MS;
      }
      if (consecutiveErrors > 0) {
        return Math.min(
          CONFIRMATION_INITIAL_POLL_MS * 2 ** consecutiveErrors,
          CONFIRMATION_MAX_ERROR_POLL_MS,
        );
      }
      if (elapsed >= ACTIVE_CONFIRMATION_WAIT_MS) {
        return CONFIRMATION_DELAYED_POLL_MS;
      }
      return elapsed >= 15_000 ? CONFIRMATION_STANDARD_POLL_MS : CONFIRMATION_INITIAL_POLL_MS;
    };

    const scheduleNextPoll = (): void => {
      if (stopped) {
        return;
      }
      const elapsed = updateDelayedState();
      const baseDelay = nextPollDelay(elapsed);
      const jitteredDelay = Math.round(baseDelay * (0.9 + Math.random() * 0.2));
      pollTimer = setTimeout(() => void pollStatus(), jitteredDelay);
    };

    async function pollStatus(): Promise<void> {
      if (stopped || pollInFlight) {
        return;
      }

      pollInFlight = true;
      try {
        const status = await getCheckoutStatus(confirmationReturnId);
        if (!stopped) {
          consecutiveErrors = 0;
          handleStatus(status);
        }
      } catch (error) {
        if (!stopped && error instanceof ApiError && (error.status === 400 || error.status === 404)) {
          stopStatusUpdates();
          clearConfirmationWaitStartedAt(confirmationReturnId);
          setConfirmationConnectionIssue(false);
          setGlobalError("We couldn't find this return. Check the link or sign in with the email used for your return.");
          return;
        }
        if (!stopped) {
          consecutiveErrors += 1;
          setConfirmationConnectionIssue(true);
        }
      } finally {
        pollInFlight = false;
        if (!stopped) {
          scheduleNextPoll();
        }
      }
    }

    const remainingActiveWait = Math.max(0, ACTIVE_CONFIRMATION_WAIT_MS - (Date.now() - waitStartedAt));
    delayedTimer = setTimeout(() => setConfirmationDelayed(true), remainingActiveWait);
    const onVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible' || stopped || pollInFlight) {
        return;
      }
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = null;
      }
      void pollStatus();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    void pollStatus();

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stopStatusUpdates();
    };
  }, [route]);

  useEffect(() => {
    if (route.kind !== 'signIn' || auth.isLoading || !auth.isAuthenticated) {
      return;
    }

    setSignInStage('email');
    setSignInCode('');
    setSignInError('');
    setSignInBusy(false);
    navigate('/returns');
  }, [auth.isAuthenticated, auth.isLoading, route.kind]);

  useEffect(() => {
    if (route.kind !== 'accountReturns' || auth.isLoading || !auth.isAuthenticated) {
      return;
    }

    const pendingReturnId = readPendingReturnDetailsId();
    if (!pendingReturnId) {
      return;
    }

    const nextPath = `/returns/${encodeURIComponent(pendingReturnId)}`;
    window.history.replaceState({}, '', nextPath);
    setRoute(parseRoute(nextPath));
  }, [auth.isAuthenticated, auth.isLoading, route.kind]);

  useEffect(() => {
    if (route.kind !== 'accountReturn') {
      setReturnManagementToken('');
      return;
    }

    setReturnManagementToken(consumeReturnManagementToken(route.returnId));
  }, [route]);

  useEffect(() => {
    if (route.kind !== 'accountReturn' || auth.isLoading) {
      return;
    }

    const hasMagicLinkAccess = Boolean(returnManagementToken);
    if (!auth.isAuthenticated && !hasMagicLinkAccess) {
      savePendingReturnDetailsId(route.returnId);
      return;
    }

    if (resolveReturnId(manageStatus ?? {}) === route.returnId) {
      clearPendingReturnDetailsId();
      return;
    }

    let cancelled = false;
    setManageStatus(null);
    setAccountReturnDetailsBusyId(route.returnId);
    setAccountReturnsError('');

    const loadReturnDetails = async (): Promise<void> => {
      try {
        const response = hasMagicLinkAccess
          ? await getReturnWithManagementToken(route.returnId, returnManagementToken)
          : isSupportReturnsView
            ? await getSupportReturn(route.returnId, auth.getAccessToken)
            : await getCustomerReturn(route.returnId, auth.getAccessToken);
        if (!cancelled) {
          setManageStatus(response);
          setManageSnapshot(readConfirmationSnapshot(route.returnId));
          setManageCancelBusy(false);
          setManageCancelError('');
          setManageCancelNotice('');
          setManageCancelConfirmOpen(false);
          setManageSupportReadOnly(hasMagicLinkAccess ? false : isSupportReturnsView);
          clearPendingReturnDetailsId();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (error) {
        if (!cancelled) {
          if (hasMagicLinkAccess && error instanceof ApiError && error.status === 401) {
            clearReturnManagementToken(route.returnId);
            setReturnManagementToken('');
            savePendingReturnDetailsId(route.returnId);
            if (!auth.isAuthenticated) {
              setSignInError(
                error.payload?.message ??
                  'This secure return link is invalid or expired. Sign in with your email to continue.',
              );
            }
            return;
          }
          setAccountReturnsError(
            error instanceof ApiError && error.status === 404
              ? "We couldn't open that return."
              : toErrorMessage(error),
          );
        }
      } finally {
        if (!cancelled) {
          setAccountReturnDetailsBusyId('');
        }
      }
    };

    void loadReturnDetails();

    return () => {
      cancelled = true;
    };
  }, [
    auth.getAccessToken,
    auth.isAuthenticated,
    auth.isLoading,
    isSupportReturnsView,
    manageStatus,
    returnManagementToken,
    route,
  ]);

  useEffect(() => {
    if (route.kind !== 'accountReturns') {
      return;
    }

    if (auth.isLoading) {
      return;
    }

    if (!auth.isAuthenticated) {
      setAccountReturns([]);
      setAccountReturnsLoading(false);
      setAccountReturnsError('');
      return;
    }

    if (readPendingReturnDetailsId()) {
      return;
    }

    let cancelled = false;

    const loadAccountReturns = async (): Promise<void> => {
      setAccountReturnsLoading(true);
      setAccountReturnsError('');

      try {
        const response = isSupportReturnsView
          ? await getSupportReturns(auth.getAccessToken, SUPPORT_RETURNS_LIMIT)
          : await getCustomerReturns(auth.getAccessToken);
        if (!cancelled) {
          const visibleReturns = isSupportReturnsView
            ? response.returns
            : response.returns.filter((returnSummary) => isCustomerReturnsListState(returnSummary.state));
          setAccountReturns(visibleReturns);
        }
      } catch (error) {
        if (!cancelled) {
          setAccountReturns([]);
          setAccountReturnsError(toErrorMessage(error));
        }
      } finally {
        if (!cancelled) {
          setAccountReturnsLoading(false);
        }
      }
    };

    void loadAccountReturns();

    return () => {
      cancelled = true;
    };
  }, [auth.isAuthenticated, auth.isLoading, auth.getAccessToken, isSupportReturnsView, route.kind]);

  useEffect(() => {
    if (route.kind !== 'managePickup' || manageStatus || auth.isLoading) {
      return;
    }

    const nextPath = auth.isAuthenticated ? '/returns' : '/sign-in';
    window.history.replaceState({}, '', nextPath);
    setRoute(parseRoute(nextPath));
  }, [auth.isAuthenticated, auth.isLoading, manageStatus, route.kind]);

  useEffect(() => {
    const isPendingConfirmation =
      route.kind === 'confirmation' && (!checkoutStatus || !isTerminalState(checkoutStatus.state));

    if (!isPendingConfirmation) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setConfirmationTick((previous) => previous + 1);
    }, 2200);

    return () => {
      clearInterval(intervalId);
    };
  }, [route.kind, checkoutStatus]);

  const confirmationProgressMessage = useMemo(() => {
    if (!checkoutStatus) {
      return CONFIRMATION_GENERIC_MESSAGES[confirmationTick % CONFIRMATION_GENERIC_MESSAGES.length];
    }

    if (checkoutStatus.state === 'PAYMENT_AUTH_PENDING') {
      return 'Checking your payment details...';
    }

    if (checkoutStatus.state === 'PAYMENT_AUTHORIZED') {
      return 'Payment confirmed. Booking your pickup now...';
    }

    if (checkoutStatus.state === 'SCHEDULING_IN_PROGRESS') {
      const schedulingMessages = [
        'Matching your request with the carrier...',
        'Reserving your pickup slot...',
        'Almost there, wrapping things up...',
      ];
      return schedulingMessages[confirmationTick % schedulingMessages.length];
    }

    if (checkoutStatus.state === 'QUOTED') {
      return 'Preparing your booking request...';
    }

    return CONFIRMATION_GENERIC_MESSAGES[confirmationTick % CONFIRMATION_GENERIC_MESSAGES.length];
  }, [checkoutStatus, confirmationTick]);

  const reviewQuote = checkoutStartData?.quote ?? zeroDollarQuote ?? restoredPaidQuote;
  const reviewQuoteExpired = reviewQuote ? isQuoteExpired(reviewQuote, quoteClock) : false;
  useEffect(() => {
    if (!reviewQuote?.expiresAt) {
      return;
    }
    const expiresAt = Date.parse(reviewQuote.expiresAt);
    if (!Number.isFinite(expiresAt)) {
      return;
    }
    const delay = Math.max(0, expiresAt - Date.now());
    const timer = window.setTimeout(() => setQuoteClock(Date.now()), delay);
    return () => window.clearTimeout(timer);
  }, [reviewQuote?.expiresAt]);
  useEffect(() => {
    if (reviewQuoteExpired) {
      clearActiveQuoteId();
    }
  }, [reviewQuoteExpired]);
  const quoteLabel = useMemo(() => {
    if (!reviewQuote) {
      return null;
    }

    return formatCurrency(getQuoteTotalAmount(reviewQuote), reviewQuote.currency);
  }, [reviewQuote]);
  const reviewChargeRows = useMemo(() => {
    if (!reviewQuote) {
      return [];
    }

    return getItemizedChargeRows(reviewQuote);
  }, [reviewQuote]);
  const completeBookingLabel = quoteLabel ? `Complete booking — ${quoteLabel}` : 'Complete booking';
  const confirmationQuote = checkoutStatus?.quote ?? checkoutStartData?.quote ?? null;
  const confirmationCarrierLabel = getQuoteCarrierSummaryLabel(confirmationQuote, checkoutStatus?.carrier ?? form.carrier);
  const confirmationForm = confirmationSnapshot ?? form;
  const confirmationChargeRows = useMemo(() => {
    if (!confirmationQuote) {
      return [];
    }

    return [
      ...getItemizedChargeRows(confirmationQuote),
      {
        key: 'total',
        label: 'Total paid',
        amount: getQuoteTotalAmount(confirmationQuote),
        currency: confirmationQuote.currency,
      },
    ];
  }, [confirmationQuote]);
  const confirmationPackageCards = useMemo(
    () =>
      confirmationForm.packageCards.filter(
        (packageCard) =>
          Boolean(packageCard.packageNote.trim()) ||
          packageCard.hasPrepaidLabel ||
          Boolean(packageCard.weightLbs || packageCard.lengthIn || packageCard.widthIn || packageCard.heightIn) ||
          Boolean(packageCard.destinationLine1 || packageCard.destinationCity || packageCard.destinationPostalCode),
      ),
    [confirmationForm.packageCards],
  );
  const hasRealConfirmationEmail = Boolean(confirmationForm.email.trim()) && !isSystemCheckoutEmail(confirmationForm.email);
  const hasConfirmationPhone = Boolean(confirmationForm.phone.trim());
  const confirmationContactPrimary = hasRealConfirmationEmail
    ? confirmationForm.email.trim()
    : hasConfirmationPhone
      ? confirmationForm.phone.trim()
      : 'Return confirmation on file';
  const confirmationContactSecondary = hasRealConfirmationEmail && hasConfirmationPhone
    ? confirmationForm.phone.trim()
    : !hasRealConfirmationEmail && !hasConfirmationPhone
      ? 'Use your Return ID to manage this return.'
      : '';
  const confirmationEmailMessage = hasRealConfirmationEmail
    ? `Confirmation sent to ${confirmationForm.email.trim()}`
    : 'Save your Return ID below to manage this return anytime.';
  const confirmationNeedsGeneratedLabel = confirmationPackageCards.some((packageCard) => !packageCard.hasPrepaidLabel);
  const confirmationNextSteps = useMemo(() => {
    if (confirmationNeedsGeneratedLabel) {
      return [
        {
          title: 'Check your email',
          description: 'Your label will be attached to your confirmation email.',
          glyph: 'mail' as const,
        },
        {
          title: 'Print and attach',
          description: 'Securely attach the printed shipping label to your package.',
          glyph: 'print' as const,
        },
        {
          title: 'Ready for pickup',
          description: 'Have each package ready and accessible at the pickup address.',
          glyph: 'package' as const,
        },
        {
          title: 'View return details',
          description: 'Sign in to see your return details and current booking status.',
          glyph: 'track' as const,
        },
      ];
    }

    return [
      {
        title: 'Attach your labels',
        description: 'Attach each prepaid return label securely before pickup.',
        glyph: 'print' as const,
      },
      {
        title: 'Ready for pickup',
        description: 'Have each package ready and accessible at the pickup address.',
        glyph: 'package' as const,
      },
      {
        title: 'View return details',
        description: 'Sign in to see your return details and current booking status.',
        glyph: 'track' as const,
      },
    ];
  }, [confirmationNeedsGeneratedLabel]);
  const manageFormSource = useMemo(() => {
    if (!manageStatus) {
      return null;
    }

    if (manageSnapshot) {
      return manageSnapshot;
    }

    if (resolveReturnId(checkoutStatus ?? {}) === resolveReturnId(manageStatus)) {
      return confirmationSnapshot ?? form;
    }

    return null;
  }, [checkoutStatus, confirmationSnapshot, form, manageSnapshot, manageStatus]);
  const managePackageCards = useMemo(
    () =>
      manageFormSource?.packageCards.filter(
        (packageCard) =>
          Boolean(packageCard.packageNote.trim()) ||
          packageCard.hasPrepaidLabel ||
          Boolean(packageCard.weightLbs || packageCard.lengthIn || packageCard.widthIn || packageCard.heightIn) ||
          Boolean(packageCard.destinationLine1 || packageCard.destinationCity || packageCard.destinationPostalCode),
      ) ?? [],
    [manageFormSource],
  );
  const managePrimaryPackage = managePackageCards[0] ?? null;
  const managePackageInfoFromForm = (() => {
    if (!managePrimaryPackage) {
      return null;
    }

    const packageInfo: PackageInfo = {
      weightLbs: Number(managePrimaryPackage.weightLbs),
      lengthIn: Number(managePrimaryPackage.lengthIn),
      widthIn: Number(managePrimaryPackage.widthIn),
      heightIn: Number(managePrimaryPackage.heightIn),
    };

    return Object.values(packageInfo).every((value) => Number.isFinite(value) && value > 0) ? packageInfo : null;
  })();
  const manageLineItemsIncludePackageInfo =
    manageStatus?.lineItems?.some((lineItem) => lineItem.type === 'PACKAGE' && Boolean(lineItem.packageInfo)) ?? false;
  const manageStatusPackageInfo =
    manageStatus?.lineItems === undefined || manageLineItemsIncludePackageInfo ? manageStatus?.packageInfo ?? null : null;
  const managePackageInfo = manageStatusPackageInfo ?? managePackageInfoFromForm;
  const manageStatusShippingLabelLineItem = manageStatus?.lineItems?.find((lineItem) => lineItem.type === 'SHIPPING_LABEL');
  const manageStatusShippingLabelDestination =
    manageStatusShippingLabelLineItem?.type === 'SHIPPING_LABEL' ? manageStatusShippingLabelLineItem.destinationAddress : null;
  const manageUsesPrepaidLabel =
    manageStatus?.lineItems !== undefined
      ? !manageStatus.lineItems.some((lineItem) => lineItem.type === 'SHIPPING_LABEL')
      : Boolean(managePrimaryPackage?.hasPrepaidLabel);
  const manageStatusTone = manageStatus
    ? isSuccessState(manageStatus.state)
      ? 'success'
      : manageStatus.state === 'CANCELLED_PICKUP'
        ? 'cancelled'
        : manageStatus.state === 'FAILED_PAYMENT' ||
          manageStatus.state === 'FAILED_SCHEDULING' ||
          manageStatus.state === 'CANCELLED_AUTH'
        ? 'error'
        : 'pending'
    : 'success';
  const manageStatusHeadline = (() => {
    if (!manageStatus) {
      return '';
    }

    if (isSuccessState(manageStatus.state)) {
      return 'Your pickup is scheduled';
    }

    if (manageStatus.state === 'CANCELLED_PICKUP') {
      return 'This pickup has been cancelled';
    }

    if (manageStatus.state === 'FAILED_PAYMENT') {
      return 'Payment needs attention';
    }

    if (manageStatus.state === 'FAILED_SCHEDULING') {
      return 'There was an issue booking your pickup';
    }

    if (manageStatus.state === 'CANCELLED_AUTH') {
      return 'Booking issue';
    }

    return 'Your pickup is being processed';
  })();
  const manageStatusMessage = (() => {
    if (!manageStatus) {
      return '';
    }

    if (isSuccessState(manageStatus.state)) {
      return `Your pickup is scheduled for ${formatLongDate(manageStatus.pickupDate)} between ${formatClockTime(
        manageStatus.window.readyTime,
      )} - ${formatClockTime(manageStatus.window.closeTime)} local time.`;
    }

    if (manageStatus.state === 'CANCELLED_PICKUP') {
      return "If you'd like to schedule a new pickup, please return to the home page.";
    }

    if (manageStatus.state === 'FAILED_PAYMENT') {
      return 'The payment for this booking did not complete. Please contact support if you still need this pickup.';
    }

    if (manageStatus.state === 'FAILED_SCHEDULING') {
      return 'The carrier could not confirm this pickup. Please contact support or schedule a new pickup.';
    }

    if (manageStatus.state === 'CANCELLED_AUTH') {
      return 'This booking did not complete. Please contact support or schedule a new pickup.';
    }

    return 'We are still working on this booking and will keep the latest status available here.';
  })();
  const manageStatusPillLabel = (() => {
    if (!manageStatus) {
      return '';
    }

    if (isSuccessState(manageStatus.state)) {
      return 'Pickup Scheduled';
    }

    if (manageStatus.state === 'CANCELLED_PICKUP') {
      return 'Cancelled';
    }

    if (manageStatus.state === 'FAILED_PAYMENT' || manageStatus.state === 'FAILED_SCHEDULING') {
      return 'Action Needed';
    }

    if (manageStatus.state === 'CANCELLED_AUTH') {
      return 'Booking Issue';
    }

    return 'Booking Pending';
  })();
  const manageStatusPickupAddressLines = formatAddressLines(manageStatus?.shipperAddress);
  const manageFormPickupAddressLines = manageFormSource
    ? formatAddressLines({
        line1: manageFormSource.line1,
        line2: manageFormSource.line2,
        city: manageFormSource.city,
        state: manageFormSource.state,
        postalCode: manageFormSource.postalCode,
        countryCode: 'US',
      })
    : [];
  const managePickupAddressLines =
    manageStatusPickupAddressLines.length > 0
      ? manageStatusPickupAddressLines
      : manageFormPickupAddressLines.length > 0
        ? manageFormPickupAddressLines
        : ['Pickup address on file'];
  const manageStatusReturnAddressLines = formatAddressLines(manageStatusShippingLabelDestination);
  const manageFormReturnAddressLines = managePrimaryPackage
    ? [
        managePrimaryPackage.destinationLine1.trim(),
        managePrimaryPackage.destinationLine2.trim(),
        formatCityStatePostal(
          managePrimaryPackage.destinationCity,
          managePrimaryPackage.destinationState,
          managePrimaryPackage.destinationPostalCode,
        ),
      ].filter((line): line is string => Boolean(line))
    : [];
  const manageResolvedReturnAddressLines =
    manageStatusReturnAddressLines.length > 0
      ? manageStatusReturnAddressLines
      : manageFormReturnAddressLines.length > 0
        ? manageFormReturnAddressLines
        : manageUsesPrepaidLabel
        ? ['Address on label']
        : ['Return address on file'];
  const manageFallbackPackageCopy = manageUsesPrepaidLabel ? 'Prepaid label' : 'Package details on file';
  const managePackageDimensions = managePackageInfo
    ? `${formatMeasurement(managePackageInfo.lengthIn)} x ${formatMeasurement(managePackageInfo.widthIn)} x ${formatMeasurement(
        managePackageInfo.heightIn,
      )} in`
    : manageFallbackPackageCopy;
  const managePackageWeight = managePackageInfo ? `${formatMeasurement(managePackageInfo.weightLbs)} lbs` : manageFallbackPackageCopy;
  const manageTotalPaid = manageStatus
    ? formatCurrency(getQuoteTotalAmount(manageStatus.quote), manageStatus.quote.currency)
    : null;
  const canManageCancelPickup = Boolean(manageStatus && !manageSupportReadOnly && canRequestCancellation(manageStatus));

  const navigate = (path: string): void => {
    const nextRoute = parseRoute(path);
    window.history.pushState(
      nextRoute.kind === 'checkout' ? createCheckoutFlowHistoryState('pickup') : {},
      '',
      path,
    );
    setRoute(nextRoute);
    if (nextRoute.kind === 'checkout') {
      setCheckoutStep('form');
      setIntakeStep(1);
    }
  };

  const handleSignInEmailChange = (value: string): void => {
    setSignInEmail(value);
    if (signInError) {
      setSignInError('');
    }
  };

  const handleSignInCodeChange = (value: string): void => {
    setSignInCode(value.replace(/[^\dA-Za-z]/g, '').slice(0, 12));
    if (signInError) {
      setSignInError('');
    }
  };

  const handleSignInBackToEmail = (): void => {
    setSignInStage('email');
    setSignInCode('');
    setSignInError('');
    setSignInBusy(false);
  };

  const handleSignInSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const email = signInStage === 'code' ? signInSentEmail || signInEmail.trim() : signInEmail.trim();

    if (!email) {
      setSignInError('Enter your email address.');
      return;
    }

    if (!auth.isConfigured) {
      setSignInError("Sign in isn't available right now.");
      return;
    }

    setSignInBusy(true);
    setSignInError('');

    try {
      if (signInStage === 'code') {
        const code = signInCode.trim();

        if (!code) {
          setSignInError('Enter the code from your email.');
          setSignInBusy(false);
          return;
        }

        await auth.verifyEmailCode(signInSentEmail || email, code);
        return;
      }

      await auth.startEmailLogin(email);
      setSignInSentEmail(email);
      setSignInCode('');
      setSignInStage('code');
      setSignInBusy(false);
    } catch (error) {
      setSignInBusy(false);
      setSignInError(toErrorMessage(error));
    }
  };

  const openAccountReturnDetails = async (returnSummary: AccountReturnSummary): Promise<void> => {
    if (accountReturnDetailsBusyId) {
      return;
    }

    setAccountReturnDetailsBusyId(returnSummary.returnId);
    setAccountReturnsError('');

    try {
      const response = isSupportReturnsView
        ? await getSupportReturn(returnSummary.returnId, auth.getAccessToken)
        : await getCustomerReturn(returnSummary.returnId, auth.getAccessToken);
      openManagePickup(returnSummary.returnId, response, isSupportReturnsView);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        setAccountReturnsError("We couldn't open that return.");
      } else {
        setAccountReturnsError(toErrorMessage(error));
      }
    } finally {
      setAccountReturnDetailsBusyId('');
    }
  };

  const clearManageLookupState = (): void => {
    setManageStatus(null);
    setManageSnapshot(null);
    setManageCancelBusy(false);
    setManageCancelError('');
    setManageCancelNotice('');
    setManageCancelConfirmOpen(false);
    setManageSupportReadOnly(false);
  };

  const navigateHome = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    navigateToPath(event, '/');
  };

  const navigateToPath = (event: React.MouseEvent<HTMLAnchorElement>, path: string): void => {
    event.preventDefault();
    const nextRoute = parseRoute(path);
    setGlobalError('');
    if (activePickupQuoteAttemptRef.current !== null) {
      activePickupQuoteAttemptRef.current = null;
      pendingPickupQuoteFormRef.current = null;
      setPickupTurnstileActive(false);
      setIsBusy(false);
    }
    if (nextRoute.kind !== 'accountReturn' && nextRoute.kind !== 'signIn') {
      clearPendingReturnDetailsId();
    }
    window.history.pushState(
      nextRoute.kind === 'checkout' ? createCheckoutFlowHistoryState('pickup') : {},
      '',
      path,
    );
    setRoute(nextRoute);
    if (nextRoute.kind === 'checkout') {
      clearManageLookupState();
      setCheckoutStep('form');
      setIntakeStep(1);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openManagePickup = (
    returnId?: string,
    existingStatus?: CheckoutStatusResponse | null,
    supportReadOnly = false,
  ): void => {
    const snapshot =
      returnId
        ? (resolveReturnId(checkoutStatus ?? {}) === returnId
            ? confirmationSnapshot ?? readConfirmationSnapshot(returnId) ?? form
            : readConfirmationSnapshot(returnId))
        : null;
    setManageStatus(existingStatus ?? null);
    setManageSnapshot(snapshot);
    setManageCancelBusy(false);
    setManageCancelError('');
    setManageCancelNotice('');
    setManageCancelConfirmOpen(false);
    setManageSupportReadOnly(supportReadOnly);
    navigate(returnId ? `/returns/${encodeURIComponent(returnId)}` : '/manage-return');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleManageCancellation = async (): Promise<void> => {
    if (!manageStatus || manageSupportReadOnly || !canRequestCancellation(manageStatus) || manageCancelBusy) {
      return;
    }

    setManageCancelBusy(true);
    setManageCancelError('');
    setManageCancelNotice('');

    try {
      const activeMagicToken = route.kind === 'accountReturn' ? returnManagementToken : '';
      if (!auth.isAuthenticated && !activeMagicToken) {
        navigate('/sign-in');
        return;
      }

      const returnId = resolveReturnId(manageStatus);
      const response = activeMagicToken
        ? await cancelReturnPickupWithManagementToken(returnId, activeMagicToken)
        : await cancelCustomerReturnPickup(returnId, auth.getAccessToken);
      setManageStatus(response);
      setManageCancelConfirmOpen(false);
      setManageCancelNotice(response.state === 'CANCELLED_PICKUP' ? '' : BOOKING_STATE_SUMMARY[response.state]);
      trackEvent('pickup_cancelled', { returnId: resolveReturnId(response), state: response.state });
    } catch (error) {
      const activeMagicToken = route.kind === 'accountReturn' ? returnManagementToken : '';
      const activeMagicReturnId = route.kind === 'accountReturn' ? route.returnId : '';
      if (activeMagicToken && activeMagicReturnId && error instanceof ApiError && error.status === 401) {
        clearReturnManagementToken(activeMagicReturnId);
        setReturnManagementToken('');
        savePendingReturnDetailsId(activeMagicReturnId);
        setSignInError(
          error.payload?.message ??
            'This secure return link is invalid or expired. Sign in with your email to continue.',
        );
        setManageCancelConfirmOpen(false);
      } else if (error instanceof ApiError && error.status === 422) {
        setManageCancelError(error.payload?.message ?? 'This pickup can no longer be canceled online.');
      } else if (error instanceof ApiError && error.status === 404) {
        setManageCancelError("We couldn't find this pickup anymore. Please look it up again.");
      } else {
        setManageCancelError(toErrorMessage(error));
      }
    } finally {
      setManageCancelBusy(false);
    }
  };

  const resetManageLookup = (): void => {
    clearManageLookupState();
    navigate(auth.isAuthenticated ? '/returns' : '/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAccountSignOut = async (): Promise<void> => {
    setAccountMenuOpen(false);
    await auth.logout();
  };

  const handleReturnMagicSignIn = (): void => {
    if (route.kind !== 'accountReturn') {
      return;
    }
    clearReturnManagementToken(route.returnId);
    setReturnManagementToken('');
    savePendingReturnDetailsId(route.returnId);
    setSignInError(accountReturnsError || 'Sign in with the email used for this return.');
  };

  const handleHelpFormChange = (field: keyof HelpFormState, value: string): void => {
    setHelpForm((previous) => ({
      ...previous,
      [field]: value,
    }));
    if (helpNotice) {
      setHelpNotice('');
    }
    if (helpError) {
      setHelpError('');
    }
  };

  const handleHelpSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setHelpNotice('');
    setHelpError('');

    if (!turnstileToken) {
      setHelpError("Please verify that you're human.");
      return;
    }

    setHelpSubmitting(true);

    try {
      const response = await sendSupportMessage({
        name: helpForm.name.trim(),
        email: helpForm.email.trim(),
        subject: helpForm.subject.trim(),
        message: helpForm.message.trim(),
        turnstileToken,
      });
      setHelpNotice(response.message || 'Thanks, we got your message.');
      setHelpForm({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
    } catch {
      setHelpError("We couldn't send your message. Please try again.");
    } finally {
      setHelpSubmitting(false);
      setTurnstileToken('');
      setTurnstileResetSignal((current) => current + 1);
    }
  };

  const clearFormErrors = (matcher: (key: string) => boolean): void => {
    setFormErrors((previous) => {
      const nextEntries = Object.entries(previous).filter(([key]) => !matcher(key));
      if (nextEntries.length === Object.keys(previous).length) {
        return previous;
      }
      return Object.fromEntries(nextEntries);
    });
  };

  const persistGuestPickupInfoPreference = (): void => {
    if (!canRememberGuestPickupInfo) {
      return;
    }

    if (rememberPickupInfo) {
      saveGuestPickupInfoSnapshot(form);
      return;
    }

    clearGuestPickupInfoSnapshot();
  };

  const handleRememberPickupInfoChange = (rememberInfo: boolean): void => {
    setRememberPickupInfo(rememberInfo);

    if (!rememberInfo) {
      clearGuestPickupInfoSnapshot();
      return;
    }

    saveGuestPickupInfoSnapshot(form);
  };

  const onFormChange = (field: Exclude<keyof FormState, 'packageCards'>, value: string | boolean): void => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));

    if (formErrors[field]) {
      setFormErrors((previous) => {
        const next = { ...previous };
        delete next[field];
        return next;
      });
    }

    if (field === 'pickupDate' && globalError === USPS_WEEKEND_PICKUP_WARNING) {
      setGlobalError('');
    }
  };

  const updatePackageCard = (packageId: string, update: (packageCard: PackageCardState) => PackageCardState): void => {
    setForm((previous) => ({
      ...previous,
      packageCards: previous.packageCards.map((packageCard) =>
        packageCard.id === packageId ? update(packageCard) : packageCard,
      ),
    }));
  };

  const handlePackageFieldChange = (
    packageId: string,
    field: keyof Pick<
      PackageCardState,
      | 'packageNote'
      | 'weightLbs'
      | 'lengthIn'
      | 'widthIn'
      | 'heightIn'
      | 'destinationLine1'
      | 'destinationLine2'
      | 'destinationCity'
      | 'destinationState'
      | 'destinationPostalCode'
    >,
    value: string,
  ): void => {
    updatePackageCard(packageId, (packageCard) => ({
      ...packageCard,
      [field]: value,
    }));
    clearFormErrors((key) => key === getPackageFieldErrorKey(packageId, field));
  };

  const handlePackageCarrierChange = (packageId: string, carrier: CarrierType): void => {
    updatePackageCard(packageId, (packageCard) => ({
      ...packageCard,
      carrier,
    }));
    if (globalError === USPS_WEEKEND_PICKUP_WARNING) {
      setGlobalError('');
    }
  };

  const handlePackageLabelChange = (packageId: string, hasPrepaidLabel: boolean): void => {
    updatePackageCard(packageId, (packageCard) => ({
      ...packageCard,
      hasPrepaidLabel,
      ...(!hasPrepaidLabel
        ? {}
        : {
            destinationAddressSearch: '',
            destinationLine1: '',
            destinationLine2: '',
            destinationCity: '',
            destinationState: '',
            destinationPostalCode: '',
          }),
    }));

    if (hasPrepaidLabel) {
      setPackageDestinationAutocompleteError(packageId, '');
      clearPackageDestinationAutocomplete(packageId);
      clearFormErrors((key) => key.startsWith(`package:${packageId}:destination`) || key.startsWith(`package:${packageId}:`));
    }
    if (globalError === USPS_WEEKEND_PICKUP_WARNING) {
      setGlobalError('');
    }
  };

  const addAnotherPackageCard = (): void => {
    const nextPackageCard = createPackageCardState(form.packageCards.length + 1, form.carrier);
    setForm((previous) => ({
      ...previous,
      packageCards: [...previous.packageCards, nextPackageCard],
    }));
    setExpandedPackageId(nextPackageCard.id);
    clearFormErrors((key) => key === 'packageCards');
  };

  const removePackageCard = (packageId: string): void => {
    setForm((previous) => ({
      ...previous,
      packageCards:
        previous.packageCards.length > 1
          ? previous.packageCards.filter((packageCard) => packageCard.id !== packageId)
          : previous.packageCards,
    }));
    setPackageDestinationAutocompleteError(packageId, '');
    clearPackageDestinationAutocomplete(packageId);
    delete packageDestinationInputRefs.current[packageId];
    delete packageDestinationInputCallbackRefs.current[packageId];
    clearFormErrors((key) => key.startsWith(`package:${packageId}:`) || key === 'packageCards');
  };

  const togglePackageCard = (packageId: string): void => {
    setExpandedPackageId((current) => (current === packageId ? '' : packageId));
  };

  const handleAddressSearchInput = (value: string): void => {
    setAddressAutocompleteError('');
    if (ignoreNextAddressInputChangeRef.current) {
      ignoreNextAddressInputChangeRef.current = false;
      setForm((previous) => ({
        ...previous,
        addressSearch: value,
      }));
      setFormErrors((previous) => {
        const next = { ...previous };
        delete next.addressSearch;
        return next;
      });
      return;
    }

    setForm((previous) => ({
      ...previous,
      addressSearch: value,
      line1: '',
      line2: '',
      city: '',
      state: '',
      postalCode: '',
    }));
    setFormErrors((previous) => {
      const next = { ...previous };
      delete next.addressSearch;
      delete next.line1;
      delete next.line2;
      delete next.city;
      delete next.state;
      delete next.postalCode;
      return next;
    });
  };

  const handlePackageDestinationSearchInput = (packageId: string, value: string): void => {
    setPackageDestinationAutocompleteError(packageId, '');

    if (ignoreNextPackageDestinationInputChangeRef.current[packageId]) {
      delete ignoreNextPackageDestinationInputChangeRef.current[packageId];
      updatePackageCard(packageId, (packageCard) => ({
        ...packageCard,
        destinationAddressSearch: value,
      }));
      clearFormErrors((key) => key === getPackageFieldErrorKey(packageId, 'destinationLine1'));
      return;
    }

    updatePackageCard(packageId, (packageCard) => ({
      ...packageCard,
      destinationAddressSearch: value,
      destinationLine1: '',
      destinationCity: '',
      destinationState: '',
      destinationPostalCode: '',
    }));
    clearFormErrors(
      (key) =>
        key === getPackageFieldErrorKey(packageId, 'destinationLine1') ||
        key === getPackageFieldErrorKey(packageId, 'destinationCity') ||
        key === getPackageFieldErrorKey(packageId, 'destinationState') ||
        key === getPackageFieldErrorKey(packageId, 'destinationPostalCode'),
    );
  };

  const requestPickupQuote = async (
    checkoutForm: FormState,
    pickupTurnstileToken: string,
    attemptId: number,
  ): Promise<void> => {
    const isCurrentAttempt = (): boolean => activePickupQuoteAttemptRef.current === attemptId;

    try {
      const quoteResponse = await getPickupQuote(buildPickupQuoteRequest(checkoutForm, pickupTurnstileToken));
      if (!isCurrentAttempt()) {
        return;
      }
      saveActiveQuoteId(quoteResponse.quoteId);
      const quoteWarning = getUspsWeekendPickupWarning(checkoutForm, quoteResponse);
      if (quoteWarning) {
        clearActiveQuoteId();
        setFormErrors((previous) => ({ ...previous, pickupDate: quoteWarning }));
        setGlobalError(quoteWarning);
        return;
      }
      setConfirmationSnapshot(checkoutForm);
      if (getQuoteTotalAmount(quoteResponse) <= 0) {
        setCheckoutStartData(null);
        setZeroDollarQuote(quoteResponse);
        setRestoredPaidQuote(null);
      } else {
        setRestoredPaidQuote(quoteResponse);
        setZeroDollarQuote(null);
        try {
          const response = await startCheckout(buildCheckoutStartRequest(quoteResponse.quoteId));
          if (!isCurrentAttempt()) {
            return;
          }
          saveConfirmationSnapshot(resolveReturnId(response), checkoutForm);
          setCheckoutStartData(response);
          setRestoredPaidQuote(null);
          trackEvent('checkout_start', { returnId: resolveReturnId(response), carrier: checkoutForm.carrier });
        } catch (error) {
          if (!isCurrentAttempt()) {
            return;
          }
          setCheckoutStartData(null);
          setGlobalError(`${toErrorMessage(error)} Your quote is saved; retry secure checkout below.`);
        }
      }
      setCheckoutStatus(null);
      setCheckoutStep('review');
      pushCheckoutFlowHistoryStep('review');
      scrollCheckoutStageToTop();
      trackPage('review');
    } catch (error) {
      if (isCurrentAttempt()) {
        setGlobalError(toErrorMessage(error));
      }
    } finally {
      if (isCurrentAttempt()) {
        activePickupQuoteAttemptRef.current = null;
        setIsBusy(false);
      }
    }
  };

  const handlePickupTurnstileToken = (token: string): void => {
    const pendingAttempt = pendingPickupQuoteFormRef.current;
    if (!token || !pendingAttempt || activePickupQuoteAttemptRef.current !== pendingAttempt.attemptId) {
      return;
    }

    pendingPickupQuoteFormRef.current = null;
    setPickupTurnstileActive(false);
    void requestPickupQuote(pendingAttempt.form, token, pendingAttempt.attemptId);
  };

  const handlePickupTurnstileError = (): void => {
    const pendingAttempt = pendingPickupQuoteFormRef.current;
    if (!pendingAttempt || activePickupQuoteAttemptRef.current !== pendingAttempt.attemptId) {
      return;
    }

    activePickupQuoteAttemptRef.current = null;
    pendingPickupQuoteFormRef.current = null;
    setPickupTurnstileActive(false);
    setIsBusy(false);
    setGlobalError("We couldn't verify this request. Please try again.");
  };

  const handleStartCheckout = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const validationErrors = validateForm(form);
    setFormErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      const pickupBasicErrorKeys = ['addressSearch', 'pickupDate', 'readyTime', 'closeTime', 'email'];
      if (pickupBasicErrorKeys.some((key) => validationErrors[key])) {
        if (readCheckoutFlowHistoryStep() === 'packages') {
          window.history.back();
        } else {
          replaceCheckoutFlowHistoryStep('pickup');
          setIntakeStep(1);
        }
      } else {
        const firstPackageWithError = form.packageCards.find((packageCard) =>
          Object.keys(validationErrors).some((key) => key.startsWith(`package:${packageCard.id}:`)),
        );
        if (firstPackageWithError) {
          setExpandedPackageId(firstPackageWithError.id);
        }
      }
      return;
    }

    persistGuestPickupInfoPreference();
    setIsBusy(true);
    setGlobalError('');
    scrollCheckoutStageToTop();
    const checkoutForm = form.email.trim() ? form : { ...form, email: checkoutEmail };
    const attemptId = pickupQuoteAttemptCounterRef.current + 1;
    pickupQuoteAttemptCounterRef.current = attemptId;
    activePickupQuoteAttemptRef.current = attemptId;
    pendingPickupQuoteFormRef.current = { form: checkoutForm, attemptId };
    setPickupTurnstileActive(true);
  };

  const handleContinueFromPickupBasics = (): void => {
    const validationErrors = validatePickupBasics(form);
    setFormErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    persistGuestPickupInfoPreference();
    setGlobalError('');
    pushCheckoutFlowHistoryStep('packages');
    setIntakeStep(2);
    scrollCheckoutStageToTop();
  };

  const returnToPickupDetails = (): void => {
    clearActiveQuoteId();
    setRestoredPaidQuote(null);
    if (readCheckoutFlowHistoryStep() === 'packages') {
      window.history.back();
      return;
    }

    replaceCheckoutFlowHistoryStep('pickup');
    setCheckoutStep('form');
    setIntakeStep(1);
    setGlobalError('');
    trackPage('form');
    scrollCheckoutStageToTop();
  };

  const returnToPackageDetails = (): void => {
    clearActiveQuoteId();
    setRestoredPaidQuote(null);
    if (readCheckoutFlowHistoryStep() === 'review') {
      window.history.back();
      return;
    }

    replaceCheckoutFlowHistoryStep('packages');
    setCheckoutStep('form');
    setIntakeStep(2);
    setCheckoutStartData(null);
    setZeroDollarQuote(null);
    setRestoredPaidQuote(null);
    setCheckoutStatus(null);
    setGlobalError('');
    trackPage('form');
    scrollCheckoutStageToTop();
  };

  const handleScheduleWithoutPayment = async (): Promise<void> => {
    if (!zeroDollarQuote || isQuoteExpired(zeroDollarQuote)) {
      setQuoteClock(Date.now());
      return;
    }

    setIsBusy(true);
    setGlobalError('');
    try {
      const response = await confirmCheckout({ quoteId: zeroDollarQuote.quoteId });
      const snapshot = confirmationSnapshot ?? form;
      saveConfirmationSnapshot(resolveReturnId(response), snapshot);
      setCheckoutStartData(response);
      setZeroDollarQuote(null);
      trackEvent('checkout_confirm', { returnId: resolveReturnId(response), carrier: form.carrier });
      navigate(`/confirmation/${encodeURIComponent(resolveReturnId(response))}`);
    } catch (error) {
      setGlobalError(toErrorMessage(error));
      scrollCheckoutStageToTop();
    } finally {
      setIsBusy(false);
    }
  };

  const handleResumePaidCheckout = async (): Promise<void> => {
    if (!restoredPaidQuote || isQuoteExpired(restoredPaidQuote)) {
      setQuoteClock(Date.now());
      return;
    }

    setIsBusy(true);
    setGlobalError('');
    try {
      const response = await startCheckout(buildCheckoutStartRequest(restoredPaidQuote.quoteId));
      const snapshot = confirmationSnapshot ?? form;
      saveConfirmationSnapshot(resolveReturnId(response), snapshot);
      setCheckoutStartData(response);
      setRestoredPaidQuote(null);
      trackEvent('checkout_start', { returnId: resolveReturnId(response), carrier: form.carrier });
    } catch (error) {
      setGlobalError(toErrorMessage(error));
      if (error instanceof ApiError && error.status === 409) {
        setQuoteClock(Date.now());
      }
      scrollCheckoutStageToTop();
    } finally {
      setIsBusy(false);
    }
  };

  const handlePaymentConfirmed = (): void => {
    if (!checkoutStartData) {
      return;
    }

    navigate(`/confirmation/${encodeURIComponent(resolveReturnId(checkoutStartData))}`);
  };

  const resetToForm = (): void => {
    const savedPickupInfo = readGuestPickupInfoSnapshot();

    clearIntakeProgressSnapshot();
    clearActiveQuoteId();
    setRoute({ kind: 'checkout' });
    window.history.pushState(createCheckoutFlowHistoryState('pickup'), '', '/');
    setRememberPickupInfo(Boolean(savedPickupInfo));
    setForm(applyGuestPickupInfoSnapshot(createInitialFormState(), savedPickupInfo));
    setFormErrors({});
    setCheckoutStep('form');
    setIntakeStep(1);
    setCheckoutStartData(null);
    setZeroDollarQuote(null);
    setRestoredPaidQuote(null);
    setCheckoutStatus(null);
    setAddressAutocompleteError('');
    setPackageDestinationAutocompleteErrors({});
    Object.keys(packageDestinationAutocompleteRefs.current).forEach(clearPackageDestinationAutocomplete);
    packageDestinationInputRefs.current = {};
    packageDestinationInputCallbackRefs.current = {};
    ignoreNextPackageDestinationInputChangeRef.current = {};
    ignoreNextAddressInputChangeRef.current = false;
    setGlobalError('');
    trackPage('form');
  };

  const showCheckout = route.kind === 'checkout';
  const isDirectReturnLoaded =
    route.kind === 'accountReturn' && resolveReturnId(manageStatus ?? {}) === route.returnId;
  const hasDirectReturnMagicAccess = route.kind === 'accountReturn' && Boolean(returnManagementToken);
  const showManage =
    route.kind === 'managePickup' ||
    (route.kind === 'accountReturn' &&
      (auth.isAuthenticated || hasDirectReturnMagicAccess) &&
      isDirectReturnLoaded);
  const showSignIn =
    route.kind === 'signIn' ||
    (route.kind === 'accountReturn' && !auth.isAuthenticated && !hasDirectReturnMagicAccess);
  const showAccountReturns =
    route.kind === 'accountReturns' ||
    (route.kind === 'accountReturn' && auth.isAuthenticated && !isDirectReturnLoaded);
  const showReturnMagicLink =
    route.kind === 'accountReturn' && hasDirectReturnMagicAccess && !isDirectReturnLoaded;
  const showSellDonate = route.kind === 'sellDonate';
  const showHowItWorks = route.kind === 'howItWorks';
  const showHelp = route.kind === 'help';
  const showRefundPolicy = route.kind === 'refundPolicy';
  const showPrivacyPolicy = route.kind === 'privacyPolicy';
  const showConfirmation = route.kind === 'confirmation';
  const showFlow = showCheckout || showConfirmation;
  const scheduleNavActive = showFlow;
  const accountNavActive = showSignIn || showAccountReturns || showManage || route.kind === 'accountReturn';
  const helpNavActive = route.kind === 'help';
  const reviewLineItemsByPackage = useMemo(
    () => groupReviewLineItemsByPackage(form.packageCards, reviewQuote?.lineItems),
    [form.packageCards, reviewQuote?.lineItems],
  );
  const reviewCarrierLabel = getQuoteCarrierSummaryLabel(reviewQuote, form.carrier);
  const currentProgressStep =
    route.kind === 'confirmation' ? 3 : checkoutStep === 'form' ? 1 : 2;
  const showScheduleStage = showCheckout && checkoutStep === 'form';
  const showStageShell = showScheduleStage;
  const contentShellClassName = showStageShell
    ? `schedule-stage-shell${showScheduleStage ? (showingPickupBasics ? ' is-home-layout' : ' is-detail-layout') : ''}`
    : showFlow
      ? `content-flow${showCheckout && checkoutStep === 'review' ? ' is-review-layout' : ''}${showConfirmation ? ' is-confirmation-layout' : ''}`
      : 'content-static';

  return (
    <div className="app-root">
      <ErrorNavigationManager />
      <header className="site-header">
        <div className="site-header-shell">
          <div className="site-header-inner">
            <a href="/" className="brand-link" onClick={navigateHome} aria-label="ReturnEm home">
              <BrandLockup />
            </a>
            <div className="site-header-actions">
              <nav className="site-nav" aria-label="Primary navigation">
                <a href="/" onClick={navigateHome} className={scheduleNavActive ? 'active' : ''}>
                  Schedule Pickup
                </a>
                <span className="site-nav-coming-soon">
                  <button
                    type="button"
                    className="site-nav-coming-soon-trigger"
                    aria-disabled="true"
                    aria-describedby="sell-donate-coming-soon-tooltip"
                  >
                    <span>Sell or donate</span>
                    <span className="site-nav-coming-soon-sparkle" aria-hidden="true">
                      ✦
                    </span>
                  </button>
                  <span
                    className="site-nav-coming-soon-tooltip"
                    id="sell-donate-coming-soon-tooltip"
                    role="tooltip"
                  >
                    Coming soon
                  </span>
                </span>
                <a
                  href="/help"
                  onClick={(event) => navigateToPath(event, '/help')}
                  className={helpNavActive ? 'active' : ''}
                >
                  Help
                </a>
              </nav>
              {auth.isAuthenticated ? (
                <div className="account-menu-wrap" ref={accountMenuRef}>
                  <button
                    type="button"
                    className={`site-utility-link site-auth-link site-sign-in-link site-profile-button ${accountNavActive ? 'active' : ''}`}
                    aria-label="Account menu"
                    aria-haspopup="menu"
                    aria-expanded={isAccountMenuOpen}
                    onClick={() => setAccountMenuOpen((isOpen) => !isOpen)}
                  >
                    <AccountIcon />
                  </button>
                  {isAccountMenuOpen && (
                    <div className="account-menu" role="menu">
                      <div className="account-menu-email">{auth.userEmail || 'Signed in'}</div>
                      <a
                        href="/returns"
                        className="account-menu-item"
                        role="menuitem"
                        onClick={(event) => {
                          setAccountMenuOpen(false);
                          navigateToPath(event, '/returns');
                        }}
                      >
                        <AccountReturnsIcon />
                        <span>My Returns</span>
                      </a>
                      <button
                        type="button"
                        className="account-menu-item"
                        role="menuitem"
                        onClick={() => void handleAccountSignOut()}
                      >
                        <AccountSignOutIcon />
                        <span>Sign out</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <a
                  href="/sign-in"
                  onClick={(event) => navigateToPath(event, '/sign-in')}
                  className={`site-utility-link site-auth-link site-sign-in-link ${accountNavActive ? 'active' : ''}`}
                  aria-label="Sign In"
                >
                  <AccountIcon />
                </a>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="content">
        <div className={contentShellClassName}>
          {!showScheduleStage && showFlow && !showConfirmation && !(showCheckout && checkoutStep === 'review') && (
            <StepProgress currentStep={currentProgressStep} />
          )}

          {!showScheduleStage && !showConfirmation && globalError && (
            <p className="alert error" role="alert">
              {globalError}
            </p>
          )}

          {showSignIn && (
            <SignInPanel
              stage={signInStage}
              email={signInEmail}
              code={signInCode}
              sentEmail={signInSentEmail}
              error={signInError}
              busy={signInBusy}
              authLoading={auth.isLoading}
              onEmailChange={handleSignInEmailChange}
              onCodeChange={handleSignInCodeChange}
              onBackToEmail={handleSignInBackToEmail}
              onSubmit={handleSignInSubmit}
            />
          )}

          {showAccountReturns && !auth.isAuthenticated && !auth.isLoading && (
            <SignInPanel
              stage={signInStage}
              email={signInEmail}
              code={signInCode}
              sentEmail={signInSentEmail}
              error={signInError}
              busy={signInBusy}
              authLoading={auth.isLoading}
              onEmailChange={handleSignInEmailChange}
              onCodeChange={handleSignInCodeChange}
              onBackToEmail={handleSignInBackToEmail}
              onSubmit={handleSignInSubmit}
            />
          )}

          {showReturnMagicLink && (
            <ReturnMagicLinkPanel
              busy={auth.isLoading || (Boolean(accountReturnDetailsBusyId) && !accountReturnsError)}
              error={accountReturnsError || "We couldn't open that return."}
              onSignIn={handleReturnMagicSignIn}
            />
          )}

          {showAccountReturns && auth.isAuthenticated && (
            <AccountReturnsPage
              returns={accountReturns}
              loading={
                accountReturnsLoading ||
                auth.isLoading ||
                (route.kind === 'accountReturn' && Boolean(accountReturnDetailsBusyId))
              }
              error={accountReturnsError}
              userEmail={auth.userEmail}
              detailsBusyId={accountReturnDetailsBusyId}
              isSupportView={isSupportReturnsView}
              onDetails={(returnSummary) => void openAccountReturnDetails(returnSummary)}
              onNewReturn={isSupportReturnsView ? undefined : resetToForm}
            />
          )}

          {showCheckout && (
            <>
            {checkoutStep === 'form' && (
              <>
              <section className={`schedule-stage ${showingPickupBasics ? 'is-home-step' : 'is-detail-step'}`}>
                {showingPickupBasics && <HomeScheduleHero />}
                <div className={`schedule-stage-form ${showingPickupBasics ? 'is-home-step' : 'is-detail-step'}`}>
                  {globalError && (
                    <p
                      className={`alert ${globalError === USPS_WEEKEND_PICKUP_WARNING ? 'warning' : 'error'}`}
                      role="alert"
                    >
                      {globalError}
                    </p>
                  )}
                {isBusy ? (
                  <section className="card form-card stage-form-card">
                    <IntakeStageHeader intakeStep={2} title="Preparing your quote" />
                    <p className="muted">Checking carrier pricing and pickup availability.</p>
                    {pickupTurnstileActive && (
                      <TurnstileWidget
                        siteKey={TURNSTILE_SITE_KEY}
                        resetSignal={0}
                        action="pickup_quote"
                        className="turnstile-widget pickup-turnstile-widget"
                        onTokenChange={handlePickupTurnstileToken}
                        onError={handlePickupTurnstileError}
                      />
                    )}
                    <SkeletonLines rows={6} />
                  </section>
                ) : (
                  <section className={`card form-card stage-form-card home-intake-card ${showingPickupBasics ? 'is-home-step' : 'is-detail-step'}`}>
                    <IntakeStageHeader
                      intakeStep={showingPickupBasics ? 1 : 2}
                      title={showingPickupBasics ? 'Pickup details' : 'Package details'}
                      subtitle={
                        showingPickupBasics
                          ? 'Tell us where and when to pick up your return.'
                          : "Tell us what we're picking up and if it has a return label."
                      }
                    />
                    <form
                      onSubmit={
                        showingPickupBasics
                          ? (event) => {
                              event.preventDefault();
                              handleContinueFromPickupBasics();
                            }
                          : handleStartCheckout
                      }
                      className="stack intake-form-stack"
                    >
                      {showingPickupBasics && (
                        <div className="address-block intake-block">
                          <div className="address-block-head">
                            <h3>Pickup address</h3>
                            <div className="residential-toggle" role="group" aria-label="Pickup location type">
                              <button
                                type="button"
                                className={`toggle-option ${form.residential ? 'is-active' : ''}`}
                                onClick={() => onFormChange('residential', true)}
                              >
                                Residential
                              </button>
                              <button
                                type="button"
                                className={`toggle-option ${form.residential ? '' : 'is-active'}`}
                                onClick={() => onFormChange('residential', false)}
                              >
                                Commercial
                              </button>
                            </div>
                          </div>
                          <label className="field address-input-field">
                            <input
                              ref={addressInputRef}
                              name="pickup_search"
                              type="text"
                              aria-label="Pickup address"
                              value={form.addressSearch}
                              onChange={(event) => handleAddressSearchInput(event.target.value)}
                              autoComplete="new-password"
                              autoCorrect="off"
                              autoCapitalize="off"
                              spellCheck={false}
                              placeholder="Start typing your pickup address"
                            />
                            {(formErrors.addressSearch || addressAutocompleteError) && (
                              <small className="field-error" role="alert">
                                {formErrors.addressSearch || addressAutocompleteError}
                              </small>
                            )}
                          </label>
                        </div>
                      )}

                  {showingPickupBasics && (
                    <section className="contact-section intake-block">
                      <h3>Contact information</h3>
                      <div className="contact-grid">
                        <label className="field">
                          <span>Email</span>
                          <input
                            type="email"
                            value={form.email}
                            onChange={(event) => onFormChange('email', event.target.value)}
                            autoComplete="email"
                            placeholder="you@example.com"
                          />
                          {formErrors.email && (
                            <small className="field-error" role="alert">
                              {formErrors.email}
                            </small>
                          )}
                        </label>
                        <label className="field">
                          <span>Phone</span>
                          <input
                            type="tel"
                            value={form.phone}
                            onChange={(event) => onFormChange('phone', event.target.value)}
                            autoComplete="tel"
                            placeholder="(555) 000-0000"
                          />
                        </label>
                      </div>
                      {canRememberGuestPickupInfo && (
                        <label className="checkbox-row saved-info-checkbox">
                          <input
                            type="checkbox"
                            checked={rememberPickupInfo}
                            onChange={(event) => handleRememberPickupInfoChange(event.target.checked)}
                          />
                          <span>Remember my info on this device</span>
                        </label>
                      )}
                    </section>
                  )}

                  {showingPickupBasics && (
                    <section className="timing-section intake-block">
                      <div className="timing-section-head">
                        <h3>Pickup date</h3>
                        <span className="timing-window-pill">
                          Pickups occur between 9:00 AM and 5:00 PM in the pickup address&apos;s local time.
                        </span>
                      </div>
                      <label className="field">
                        <span>Date</span>
                        <input
                          type="date"
                          min={getNextEligiblePickupDate()}
                          value={form.pickupDate}
                          onChange={(event) => onFormChange('pickupDate', event.target.value)}
                        />
                        {formErrors.pickupDate && (
                          <small className="field-error" role="alert">
                            {formErrors.pickupDate}
                          </small>
                        )}
                      </label>
                    </section>
                  )}

                  {!showingPickupBasics && (
                  <section className="package-card-stack">
                    <div className="package-card-stack-head">
                      <h3>Packages and labels</h3>
                      <p className="muted package-stack-note">Enter dimensions and weight.</p>
                    </div>

                    {uspsWeekendPickupWarning && (
                      <p className="alert warning" role="alert">
                        {uspsWeekendPickupWarning}
                      </p>
                    )}

                    {formErrors.packageCards && (
                      <p className="alert error" role="alert">
                        {formErrors.packageCards}
                      </p>
                    )}

                    {form.packageCards.map((packageCard, index) => (
                      <article
                        className={`package-card-frame ${expandedPackageId === packageCard.id ? 'is-expanded' : 'is-collapsed'}`}
                        key={packageCard.id}
                      >
                        <div className="package-card-frame-head">
                          <div className="package-section-head">
                            <span className="package-frame-badge" aria-hidden="true">
                              <PackageCubeIcon />
                            </span>
                            <label className="package-note-field">
                              <span className="sr-only">Package note</span>
                              <input
                                type="text"
                                value={packageCard.packageNote}
                                placeholder={getPackageNotePlaceholder(index)}
                                maxLength={80}
                                onChange={(event) =>
                                  handlePackageFieldChange(packageCard.id, 'packageNote', event.target.value)
                                }
                              />
                            </label>
                          </div>
                          <div className="package-frame-head-actions">
                            {form.packageCards.length > 1 && (
                              <button
                                type="button"
                                className="button ghost package-remove-button"
                                onClick={() => removePackageCard(packageCard.id)}
                              >
                                Remove
                              </button>
                            )}
                            <button
                              type="button"
                              className="package-frame-toggle"
                              aria-expanded={expandedPackageId === packageCard.id}
                              aria-label={`${expandedPackageId === packageCard.id ? 'Collapse' : 'Expand'} package ${index + 1}`}
                              onClick={() => togglePackageCard(packageCard.id)}
                            >
                              <ChevronUpSmallIcon />
                            </button>
                          </div>
                        </div>

                        <div className="package-card-frame-body">
                          <div className="package-layout">
                            <aside className="package-preview" aria-label={`Package ${index + 1} size preview`}>
                              <h4>Package preview</h4>
                              <PackagePreviewIllustration />
                            </aside>

                            <div className="package-form-panel">
                              <div className="package-form-panel-head">
                                <span className="package-form-panel-icon" aria-hidden="true">
                                  <PackageCubeIcon className="package-form-panel-icon-svg" />
                                </span>
                                <h4>Size &amp; Weight</h4>
                              </div>
                              {packageCard.hasPrepaidLabel ? (
                                <div className="package-size-note-card">
                                  <p>
                                    Max weight for packages with prepaid labels is <strong>70 lbs</strong>.
                                  </p>
                                </div>
                              ) : (
                                <div className="package-input-card">
                                  <fieldset className="package-input-card package-dimensions-group">
                                    <legend>Dimensions (inches)</legend>
                                    <div className="grid three package-dimensions-grid">
                                      <label className="field">
                                        <span>Length</span>
                                        <input
                                          type="number"
                                          inputMode="decimal"
                                          min={1}
                                          step={0.1}
                                          value={packageCard.lengthIn}
                                          placeholder="L"
                                          onChange={(event) => handlePackageFieldChange(packageCard.id, 'lengthIn', event.target.value)}
                                        />
                                        {formErrors[getPackageFieldErrorKey(packageCard.id, 'lengthIn')] && (
                                          <small className="field-error" role="alert">
                                            {formErrors[getPackageFieldErrorKey(packageCard.id, 'lengthIn')]}
                                          </small>
                                        )}
                                      </label>

                                      <label className="field">
                                        <span>Width</span>
                                        <input
                                          type="number"
                                          inputMode="decimal"
                                          min={1}
                                          step={0.1}
                                          value={packageCard.widthIn}
                                          placeholder="W"
                                          onChange={(event) => handlePackageFieldChange(packageCard.id, 'widthIn', event.target.value)}
                                        />
                                        {formErrors[getPackageFieldErrorKey(packageCard.id, 'widthIn')] && (
                                          <small className="field-error" role="alert">
                                            {formErrors[getPackageFieldErrorKey(packageCard.id, 'widthIn')]}
                                          </small>
                                        )}
                                      </label>

                                      <label className="field">
                                        <span>Height</span>
                                        <input
                                          type="number"
                                          inputMode="decimal"
                                          min={1}
                                          step={0.1}
                                          value={packageCard.heightIn}
                                          placeholder="H"
                                          onChange={(event) => handlePackageFieldChange(packageCard.id, 'heightIn', event.target.value)}
                                        />
                                        {formErrors[getPackageFieldErrorKey(packageCard.id, 'heightIn')] && (
                                          <small className="field-error" role="alert">
                                            {formErrors[getPackageFieldErrorKey(packageCard.id, 'heightIn')]}
                                          </small>
                                        )}
                                      </label>
                                    </div>
                                  </fieldset>
                                  <div className="package-input-card package-weight-panel">
                                    <label className="field package-weight-field">
                                      <span>Weight</span>
                                      <div className="input-unit-wrap">
                                        <input
                                          type="number"
                                          inputMode="decimal"
                                          min={0.1}
                                          step={0.1}
                                          value={packageCard.weightLbs}
                                          placeholder="e.g. 5.5"
                                          onChange={(event) => handlePackageFieldChange(packageCard.id, 'weightLbs', event.target.value)}
                                        />
                                        <span className="input-unit-suffix">LBS</span>
                                      </div>
                                      {formErrors[getPackageFieldErrorKey(packageCard.id, 'weightLbs')] && (
                                        <small className="field-error" role="alert">
                                          {formErrors[getPackageFieldErrorKey(packageCard.id, 'weightLbs')]}
                                        </small>
                                      )}
                                    </label>
                                  </div>
                                </div>
                              )}
                          </div>
                          </div>

                          <section className="package-label-area">
                            <div className="label-panel-header">
                              <h4>Do you have a prepaid return label?</h4>
                            </div>
                            <div className="label-choice-group" role="group" aria-label="Prepaid return label">
                              <button
                                type="button"
                                className={`label-choice-button ${packageCard.hasPrepaidLabel ? 'is-active' : ''}`}
                                onClick={() => handlePackageLabelChange(packageCard.id, true)}
                              >
                                <span className="label-choice-indicator" aria-hidden="true">
                                  <span />
                                </span>
                                <span>Yes, I have one</span>
                              </button>
                              <button
                                type="button"
                                className={`label-choice-button ${packageCard.hasPrepaidLabel ? '' : 'is-active'}`}
                                onClick={() => handlePackageLabelChange(packageCard.id, false)}
                              >
                                <span className="label-choice-indicator" aria-hidden="true">
                                  <span />
                                </span>
                                <span>No, I need a label</span>
                              </button>
                            </div>

                            <div className={`label-preview-card ${packageCard.hasPrepaidLabel ? '' : 'is-destination-mode'}`}>
                              <div className="label-preview-topbar">
                                <span>Return</span>
                                <div className="label-preview-topbar-fill" aria-hidden="true" />
                              </div>
                              <div className="label-preview-header">
                                <div className="label-preview-address">
                                  <span>From: Customer</span>
                                  <strong>{form.line1.trim() || '123 Return Address'}</strong>
                                  <strong>{form.city && form.state && form.postalCode ? `${form.city}, ${form.state} ${form.postalCode}` : 'City, ST 12345'}</strong>
                                </div>
                                <div className="label-preview-service">
                                  <strong>RETURN LABEL</strong>
                                  <span>
                                    {packageCard.hasPrepaidLabel
                                      ? 'Use your prepaid label'
                                      : 'Emailed with your confirmation'}
                                  </span>
                                </div>
                              </div>
                              <div className="label-preview-body">
                                {packageCard.hasPrepaidLabel ? (
                                  <div className="embedded-carrier-section">
                                    <p className="embedded-carrier-title">Which carrier is on the label?</p>
                                    <div className="carrier-grid embedded-carrier-grid" role="radiogroup" aria-label="Carrier">
                                      {CARRIERS.map((carrier) => (
                                        <button
                                          type="button"
                                          key={`${packageCard.id}-${carrier}`}
                                          className={`carrier-chip ${CARRIER_STYLE_CLASS[carrier]} ${packageCard.carrier === carrier ? 'active' : ''}`}
                                          onClick={() => handlePackageCarrierChange(packageCard.id, carrier)}
                                        >
                                          <span>{CARRIER_DISPLAY_LABEL[carrier]}</span>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="label-preview-destination">
                                    <label className="field">
                                      <span>Return destination</span>
                                      <input
                                        ref={getPackageDestinationInputRef(packageCard.id)}
                                        type="text"
                                        value={packageCard.destinationAddressSearch}
                                        onChange={(event) => handlePackageDestinationSearchInput(packageCard.id, event.target.value)}
                                        autoComplete="new-password"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck={false}
                                        placeholder="Start typing the retailer's return address"
                                      />
                                      {(formErrors[getPackageFieldErrorKey(packageCard.id, 'destinationLine1')] ||
                                        packageDestinationAutocompleteErrors[packageCard.id]) && (
                                        <small className="field-error" role="alert">
                                          {formErrors[getPackageFieldErrorKey(packageCard.id, 'destinationLine1')] ||
                                            packageDestinationAutocompleteErrors[packageCard.id]}
                                        </small>
                                      )}
                                    </label>
                                  </div>
                                )}
                              </div>
                              <div className="label-preview-barcode">
                                <div className="label-preview-bars" aria-hidden="true">
                                  <span />
                                  <span />
                                  <span />
                                  <span />
                                  <span />
                                </div>
                                <span>Label preview</span>
                              </div>
                            </div>
                          </section>
                        </div>
                      </article>
                    ))}

                    <button type="button" className="button ghost package-add-button" onClick={addAnotherPackageCard}>
                      Add another package
                    </button>
                  </section>
                  )}

                  {showingPickupBasics ? (
                    <button type="submit" className="button primary intake-next-button">
                      <span>NEXT STEP</span>
                      <ArrowRightIcon />
                    </button>
                  ) : (
                    <div className="stage-action-row">
                      <button type="button" className="button ghost stage-back-button" onClick={returnToPickupDetails}>
                        <ArrowLeftIcon />
                        <span>Back</span>
                      </button>
                      <button type="submit" className="button primary" disabled={isBusy}>
                        <span>Continue to payment</span>
                        <ArrowRightIcon />
                      </button>
                    </div>
                  )}
                    </form>
                  </section>
                )}
                </div>
              </section>
              {showingPickupBasics && <HomeValueSection />}
              </>
            )}

            {checkoutStep === 'review' && reviewQuote && (
              <section className="review-screen">
                <div className="review-stage-card">
                  <div className="review-stage-head">
                    <p className="review-stage-kicker">Schedule pickup</p>
                    <span className="review-stage-step-count">Step 2 of 3</span>
                  </div>
                  <h2>Review and schedule</h2>
                  <p className="review-intro">
                    {getQuoteTotalAmount(reviewQuote) > 0
                      ? 'Check your pickup details and finish booking.'
                      : 'Check your pickup details and schedule when ready.'}
                  </p>
                  <ReviewStageProgress currentStep={2} />
                </div>

                {globalError && !reviewQuoteExpired && (
                  <section className="alert error" role="alert">
                    {globalError}
                  </section>
                )}

                {reviewQuoteExpired && (
                  <section className="alert warning review-expired-alert" role="alert">
                    <strong>This quote has expired.</strong>
                    <span>Get a new quote before completing payment or scheduling your pickup.</span>
                    <button type="button" className="button primary" onClick={returnToPackageDetails}>
                      Get a new quote
                    </button>
                  </section>
                )}

                <div className="review-main-column">
                  <section className="review-block review-pickup-card">
                    <div className="review-panel-head">
                      <h3>Pickup details</h3>
                    </div>
                    <div className="review-pickup-top-grid">
                      <article className="review-pickup-item">
                        <span>Carrier</span>
                        <strong>{reviewCarrierLabel}</strong>
                      </article>
                      <article className="review-pickup-item">
                        <span>Date</span>
                        <strong>{formatDate(form.pickupDate)}</strong>
                      </article>
                      <article className="review-pickup-item">
                        <span>Window</span>
                        <strong>{form.readyTime}-{form.closeTime}</strong>
                      </article>
                    </div>
                    <div className="review-pickup-bottom-grid">
                      <article className="review-pickup-item review-pickup-address">
                        <span>Address</span>
                        <strong>
                          {form.line1}
                          {form.line2 ? `, ${form.line2}` : ''}
                        </strong>
                        <p>
                          {form.city}, {form.state} {form.postalCode}
                        </p>
                      </article>
                      <article className="review-pickup-item">
                        <span>Location type</span>
                        <strong>{form.residential ? 'Residential' : 'Commercial'}</strong>
                      </article>
                    </div>
                  </section>

                  <section className="review-package-stack">
                    {form.packageCards.map((packageCard, index) => {
                      const pricedLineItems = reviewLineItemsByPackage[index] ?? [];
                      const packageChargeRows = pricedLineItems
                        .filter((lineItem) => lineItem.type === 'SHIPPING_LABEL')
                        .map((lineItem, lineItemIndex) => ({
                          key: `${packageCard.id}-${lineItem.type}-${lineItem.description}-${lineItemIndex}`,
                          label: lineItem.description || `${CARRIER_DISPLAY_LABEL[lineItem.carrier ?? form.carrier]} Label`,
                          amount: getQuoteLineItemTotal(lineItem),
                          currency: lineItem.currency,
                        }));
                      const packageTotalAmount = packageChargeRows.reduce((total, chargeRow) => total + chargeRow.amount, 0);
                      const packageCurrency = packageChargeRows[0]?.currency ?? reviewQuote.currency;
                      const shippingMethodLabel = getReviewShippingMethodLabel(
                        packageCard.hasPrepaidLabel,
                        packageCard.hasPrepaidLabel ? packageCard.carrier : pricedLineItems[0]?.carrier ?? reviewQuote.carrier,
                        packageChargeRows[0]?.label,
                      );

                      return (
                        <article className="review-package-card" key={packageCard.id}>
                          <div className="review-package-card-head">
                              <div className="review-package-card-title">
                                <div className="review-package-heading-row">
                                <h3>{getPackageNote(packageCard, index)}</h3>
                                <span
                                  className={
                                    packageCard.hasPrepaidLabel ? 'review-package-pill is-prepaid' : 'review-package-pill is-label'
                                  }
                                >
                                  {packageCard.hasPrepaidLabel ? 'Using your prepaid label' : 'Label requested'}
                                </span>
                              </div>
                              <p>{getPackageDetailSummary(packageCard)}</p>
                            </div>
                            <div className={packageTotalAmount > 0 ? 'review-package-cost' : 'review-package-cost is-zero'}>
                              <span>Label cost</span>
                              <strong>{formatCurrency(packageTotalAmount, packageCurrency)}</strong>
                            </div>
                          </div>

                          <div className="review-package-layout">
                            <div className="review-package-illustration-frame" aria-hidden="true">
                              <div className="review-package-illustration-shell">
                                <ReviewPackageIllustration showLabel={!packageCard.hasPrepaidLabel} />
                              </div>
                            </div>

                            <div className="review-package-summary-card">
                              {!packageCard.hasPrepaidLabel ? (
                                <div className="review-package-detail-block">
                                  <span className="review-package-detail-icon" aria-hidden="true">
                                    <ReviewDestinationIcon />
                                  </span>
                                  <div className="review-package-destination">
                                    <span>Destination</span>
                                    <strong>{packageCard.destinationLine1}</strong>
                                    <p>
                                      {packageCard.destinationCity}, {packageCard.destinationState.toUpperCase()}{' '}
                                      {packageCard.destinationPostalCode}
                                    </p>
                                  </div>
                                </div>
                              ) : (
                                <div className="review-package-detail-block">
                                  <span className="review-package-detail-icon" aria-hidden="true">
                                    <ReviewDestinationIcon />
                                  </span>
                                  <div className="review-package-destination review-package-destination-attached">
                                    <span>Destination</span>
                                    <p className="review-package-detail-note">Determined by attached label</p>
                                  </div>
                                </div>
                              )}

                              <div className="review-package-detail-block review-package-detail-block-secondary">
                                <span className="review-package-detail-icon" aria-hidden="true">
                                  <ReviewShippingMethodIcon />
                                </span>
                                <div className="review-package-destination">
                                  <span>Shipping method</span>
                                  <strong>{shippingMethodLabel}</strong>
                                  <p>
                                    {packageCard.hasPrepaidLabel
                                      ? 'Use your prepaid label'
                                      : 'Label will be emailed with your confirmation'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </section>

                  {restoredPaidQuote && !checkoutStartData && !reviewQuoteExpired && (
                    <section className="review-checkout-card">
                      <div className="review-checkout-lockup">
                        <span className="review-checkout-icon" aria-hidden="true">
                          <CheckoutLockIcon />
                        </span>
                        <div>
                          <h3>Secure checkout</h3>
                          <p>Your quote is saved. Reload secure payment to continue.</p>
                        </div>
                      </div>
                      <div className="button-row review-actions">
                        <button
                          type="button"
                          className="button primary"
                          onClick={handleResumePaidCheckout}
                          disabled={isBusy}
                        >
                          {isBusy ? 'Loading secure checkout...' : 'Resume secure checkout'}
                        </button>
                        <button type="button" className="button ghost" onClick={returnToPackageDetails}>
                          Back
                        </button>
                      </div>
                    </section>
                  )}

                  {checkoutStartData?.paymentRequired && (
                    <section className="review-checkout-card">
                      <div className="review-checkout-lockup">
                        <span className="review-checkout-icon" aria-hidden="true">
                          <CheckoutLockIcon />
                        </span>
                        <div>
                          <h3>Secure checkout</h3>
                          <p>Powered by Stripe</p>
                        </div>
                      </div>

                      <div className="review-cost-panel">
                        <span className="review-cost-kicker">Order total</span>
                        <strong className="review-cost-total">{quoteLabel}</strong>
                        <div className="review-cost-breakdown">
                          {reviewChargeRows.map((chargeRow) => (
                            <div className="review-cost-row" key={chargeRow.key}>
                              <span>{chargeRow.label}</span>
                              <strong>{formatCurrency(chargeRow.amount, chargeRow.currency)}</strong>
                            </div>
                          ))}
                          <div className="review-cost-divider" aria-hidden="true" />
                          <div className="review-cost-row is-total">
                            <span>Total</span>
                            <strong>{quoteLabel}</strong>
                          </div>
                        </div>
                      </div>

                      <div className="review-payment-panel">
                        <div className="review-payment-copy">
                          <h3>Payment details</h3>
                        </div>
                        {checkoutStartData.stripeClientSecret && stripePromise ? (
                          <Elements
                            stripe={stripePromise}
                            options={{
                              clientSecret: checkoutStartData.stripeClientSecret,
                              appearance: stripeElementsAppearance,
                              fonts: stripeElementsFonts,
                            }}
                          >
                            <PaymentCardForm
                              email={checkoutEmail}
                              phone={form.phone.trim()}
                              returnUrl={`${window.location.origin}/confirmation/${encodeURIComponent(
                                resolveReturnId(checkoutStartData),
                              )}`}
                              onPaymentConfirmed={handlePaymentConfirmed}
                              showTopRow={false}
                              showFieldLabel={false}
                              footnoteText="Your card information is encrypted and securely processed by Stripe."
                              submitLabel={completeBookingLabel}
                              disabled={reviewQuoteExpired}
                            />
                          </Elements>
                        ) : (
                          <p className="alert error" role="alert">
                            Payment is temporarily unavailable. Please try again in a moment.
                          </p>
                        )}
                      </div>

                      <div className="button-row review-actions review-checkout-actions">
                        <button type="button" className="button ghost stage-back-button" onClick={returnToPackageDetails}>
                          <ArrowLeftIcon />
                          <span>Back</span>
                        </button>
                      </div>

                      <p className="review-checkout-footnote">Payments secured by Stripe</p>
                    </section>
                  )}
                </div>

                {zeroDollarQuote && (
                  <div className="review-footer">
                    <div className="review-footer-total">
                      <span>Total</span>
                      <strong>{quoteLabel}</strong>
                    </div>
                    <div className="button-row review-actions">
                      <button
                        type="button"
                        className="button ghost"
                        onClick={returnToPackageDetails}
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        className="button primary"
                        onClick={handleScheduleWithoutPayment}
                        disabled={isBusy || reviewQuoteExpired}
                      >
                        {isBusy ? 'Scheduling...' : 'Schedule Pickup'}
                      </button>
                    </div>
                  </div>
                )}
              </section>
            )}
            </>
          )}

          {showManage && manageStatus && (
            <section className="manage-page is-results-view">
                  <section className="manage-results-overview">
                    <div className="manage-results-head">
                      <div className="manage-results-booking">
                        <span>Return ID</span>
                        <strong>{resolveReturnId(manageStatus)}</strong>
                      </div>
                      <span className={`manage-results-pill is-${manageStatusTone}`}>
                        <ManageStatusClockIcon className="manage-results-pill-icon" />
                        <span>{manageStatusPillLabel}</span>
                      </span>
                    </div>

                    <div className={`manage-results-banner is-${manageStatusTone}`} role="status" aria-live="polite">
                      <span className="manage-results-banner-icon-shell" aria-hidden="true">
                        <ManageStatusClockIcon className="manage-results-banner-icon" />
                      </span>
                      <div className="manage-results-banner-copy">
                        <strong>{manageStatusHeadline}</strong>
                        <p>{manageStatusMessage}</p>
                      </div>
                    </div>

                    <div className="manage-results-section">
                      <div className="manage-results-section-title">
                        <span className="manage-results-section-icon is-date" aria-hidden="true">
                          <ConfirmationGlyphIcon glyph="calendar" className="manage-results-section-icon-svg" />
                        </span>
                        <h2>Pickup Details</h2>
                      </div>
                      <div className="manage-results-info-grid">
                        <article className="manage-results-info-card">
                          <span>Date</span>
                          <strong>{formatDate(manageStatus.pickupDate)}</strong>
                        </article>
                        <article className="manage-results-info-card">
                          <span>Time Window</span>
                          <strong>
                            {formatClockTime(manageStatus.window.readyTime)} - {formatClockTime(manageStatus.window.closeTime)}
                          </strong>
                        </article>
                      </div>
                    </div>

                    <div className="manage-results-section">
                      <div className="manage-results-section-title">
                        <span className="manage-results-section-icon is-package" aria-hidden="true">
                          <ConfirmationGlyphIcon glyph="package" className="manage-results-section-icon-svg" />
                        </span>
                        <h2>Package Information</h2>
                      </div>
                      <div className="manage-results-info-grid">
                        <article className="manage-results-info-card">
                          <span>Dimensions (L × W × H)</span>
                          <strong>{managePackageDimensions}</strong>
                        </article>
                        <article className="manage-results-info-card">
                          <span>Weight</span>
                          <strong>{managePackageWeight}</strong>
                        </article>
                      </div>
                    </div>
                  </section>

                  <div className="manage-results-address-grid">
                    <article className="manage-results-address-card">
                      <div className="manage-results-address-head">
                        <span className="manage-results-address-icon is-pickup" aria-hidden="true">
                          <ConfirmationGlyphIcon glyph="pin" className="manage-results-address-icon-svg" />
                        </span>
                        <h3>Pickup Address</h3>
                      </div>
                      <div className="manage-results-address-lines">
                        {managePickupAddressLines.map((line, index) => (
                          <p
                            key={`pickup-address-line-${index}`}
                            className={`manage-results-address-line${index === 0 ? ' is-primary' : ''}`}
                          >
                            {line}
                          </p>
                        ))}
                      </div>
                    </article>

                    <article className="manage-results-address-card">
                      <div className="manage-results-address-head">
                        <span className="manage-results-address-icon is-return" aria-hidden="true">
                          <ConfirmationGlyphIcon glyph="pin" className="manage-results-address-icon-svg" />
                        </span>
                        <h3>Return Address</h3>
                      </div>
                      <div className="manage-results-address-lines">
                        {manageResolvedReturnAddressLines.map((line, index) => (
                          <p
                            key={`return-address-line-${index}`}
                            className={`manage-results-address-line${index === 0 ? ' is-primary' : ''}`}
                          >
                            {line}
                          </p>
                        ))}
                      </div>
                    </article>
                  </div>

                  <section className="manage-results-total-card">
                    <span>Total Paid</span>
                    <strong>{manageTotalPaid}</strong>
                  </section>

                  <div className="manage-results-actions">
                    <button type="button" className="button ghost manage-results-secondary-action" onClick={resetManageLookup}>
                      {auth.isAuthenticated ? 'Back to Returns' : 'Back Home'}
                    </button>
                    {canManageCancelPickup && !manageCancelConfirmOpen ? (
                      <button
                        type="button"
                        className="button ghost manage-results-danger-action"
                        onClick={() => {
                          setManageCancelConfirmOpen(true);
                          setManageCancelError('');
                          setManageCancelNotice('');
                        }}
                        disabled={manageCancelBusy}
                      >
                        Cancel Pickup
                      </button>
                    ) : null}
                  </div>

                  {canManageCancelPickup && manageCancelConfirmOpen && (
                    <section className="manage-cancel-confirm-card" aria-live="polite">
                      <h2>Cancel this pickup?</h2>
                      <p>You&apos;ll need to schedule a new pickup if you change your mind.</p>
                      <div className="manage-cancel-confirm-actions">
                        <button
                          type="button"
                          className="button manage-cancel-confirm-primary"
                          onClick={handleManageCancellation}
                          disabled={manageCancelBusy}
                        >
                          {manageCancelBusy ? 'Canceling...' : 'Cancel pickup'}
                        </button>
                        <button
                          type="button"
                          className="button ghost manage-cancel-confirm-secondary"
                          onClick={() => {
                            setManageCancelConfirmOpen(false);
                            setManageCancelError('');
                            setManageCancelNotice('');
                          }}
                          disabled={manageCancelBusy}
                        >
                          Keep pickup
                        </button>
                      </div>
                    </section>
                  )}

                  {manageCancelError && (
                    <p className="alert error manage-alert" role="alert">
                      {manageCancelError}
                    </p>
                  )}
                  {manageCancelNotice && (
                    <p className="alert success manage-alert" role="status">
                      {manageCancelNotice}
                    </p>
                  )}
            </section>
          )}

          {showHowItWorks && <HowItWorksPage onScheduleClick={resetToForm} />}

          {showRefundPolicy && (
            <LegalPolicyPage title="Refund Policy" updatedAt="April 14, 2026" sections={REFUND_POLICY_SECTIONS} />
          )}

          {showPrivacyPolicy && (
            <LegalPolicyPage title="Privacy Policy" updatedAt="August 24, 2026" sections={PRIVACY_POLICY_SECTIONS} />
          )}

          {showSellDonate && (
            <section className="sell-donate-page">
              <section className="sell-donate-hero">
                <span className="sell-donate-hero-icon-shell" aria-hidden="true">
                  <SellDonateSparkIcon />
                </span>
                <h1>Can&apos;t Return It?</h1>
                <p>
                  Not all items can be returned. But that doesn&apos;t mean they have to go to waste. We&apos;re building
                  tools to help you donate or resell unwanted items with ease.
                </p>
              </section>

              <section className="sell-donate-banner" aria-label="Coming soon">
                <strong>
                  <span className="sell-donate-banner-icon" aria-hidden="true">
                    <SellDonateSparkIcon />
                  </span>
                  Coming Soon
                </strong>
                <p>We&apos;re working hard to bring these features to life. Check back soon for updates!</p>
              </section>

              <div className="sell-donate-options-grid">
                <article className="sell-donate-option-card">
                  <span className="sell-donate-option-badge">In Development</span>
                  <span className="sell-donate-option-icon is-donate" aria-hidden="true">
                    <SellDonateOptionIcon option="donate" />
                  </span>
                  <h2>Donate Your Items</h2>
                  <p className="sell-donate-option-copy">
                    Schedule a pickup for items you&apos;d like to donate to charity. We&apos;ll connect you with local
                    organizations and handle the logistics.
                  </p>
                  <ul className="sell-donate-feature-list is-donate">
                    <li>Partner with trusted charities in your area</li>
                    <li>Get a tax-deductible receipt automatically</li>
                    <li>Schedule convenient pickup times</li>
                  </ul>
                  <div className="sell-donate-perfect-for">
                    <span>Perfect for:</span> Clothing, household items, books, electronics, and more
                  </div>
                </article>

                <article className="sell-donate-option-card">
                  <span className="sell-donate-option-badge is-blue">In Development</span>
                  <span className="sell-donate-option-icon is-resell" aria-hidden="true">
                    <SellDonateOptionIcon option="resell" />
                  </span>
                  <h2>Resell with Ease</h2>
                  <p className="sell-donate-option-copy">
                    List your items on popular marketplaces and we&apos;ll handle the shipping when they sell. Get your
                    money back hassle-free.
                  </p>
                  <ul className="sell-donate-feature-list is-resell">
                    <li>Integrate with eBay, Poshmark, and more</li>
                    <li>Pre-filled shipping labels when items sell</li>
                    <li>Schedule pickup or drop-off options</li>
                  </ul>
                  <div className="sell-donate-perfect-for">
                    <span>Perfect for:</span> Designer items, electronics, collectibles, furniture, and more
                  </div>
                </article>
              </div>

              <section className="sell-donate-process-card">
                <h2>How it will work</h2>
                <div className="sell-donate-steps-grid">
                  <article className="sell-donate-step-card">
                    <span className="sell-donate-step-number">1</span>
                    <h3>Choose your option</h3>
                    <p>Decide whether to donate or resell your item</p>
                  </article>
                  <article className="sell-donate-step-card">
                    <span className="sell-donate-step-number">2</span>
                    <h3>Provide details</h3>
                    <p>Tell us about your item and preferences</p>
                  </article>
                  <article className="sell-donate-step-card">
                    <span className="sell-donate-step-number">3</span>
                    <h3>We handle the rest</h3>
                    <p>From pickup to delivery, we&apos;ve got you covered</p>
                  </article>
                </div>
              </section>
            </section>
          )}

          {showHelp && (
            <section className="help-page">
              <section className="help-page-hero">
                <span className="help-page-hero-icon-shell" aria-hidden="true">
                  <HelpHeroIcon />
                </span>
                <h1>Help &amp; Support</h1>
                <p>Find answers below or send us a message. We reply within 24 business hours.</p>
              </section>

              <div className="help-support-grid">
                <article className="help-support-card">
                  <span className="help-support-card-icon is-orange" aria-hidden="true">
                    <HelpSupportCardIcon kind="email" />
                  </span>
                  <div className="help-support-card-copy">
                    <h2>Email Support</h2>
                    <p>Get help via email within 24 business hours</p>
                    <strong>{supportEmail}</strong>
                  </div>
                </article>

                <article className="help-support-card">
                  <span className="help-support-card-icon is-purple" aria-hidden="true">
                    <HelpSupportCardIcon kind="hours" />
                  </span>
                  <div className="help-support-card-copy">
                    <h2>Support Hours</h2>
                    <p>
                      <strong>Mon - Fri:</strong> {HELP_SUPPORT_HOURS}
                    </p>
                  </div>
                </article>
              </div>

              <div className="help-main-grid">
                <section className="help-contact-card">
                  <h2>Send us a message</h2>
                  <p className="help-contact-copy">Send us a message and we&apos;ll reply within 24 business hours.</p>
                  {helpNotice && (
                    <p className="alert success help-contact-notice" role="status">
                      {helpNotice}
                    </p>
                  )}
                  {helpError && (
                    <p className="alert error help-contact-notice" role="alert">
                      {helpError}
                    </p>
                  )}
                  <form className="help-contact-form" onSubmit={handleHelpSubmit}>
                    <label className="help-field">
                      <span>Name</span>
                      <input
                        type="text"
                        value={helpForm.name}
                        onChange={(event) => handleHelpFormChange('name', event.target.value)}
                        autoComplete="name"
                        required
                      />
                    </label>

                    <label className="help-field">
                      <span>Email</span>
                      <input
                        type="email"
                        value={helpForm.email}
                        onChange={(event) => handleHelpFormChange('email', event.target.value)}
                        autoComplete="email"
                        required
                      />
                    </label>

                    <label className="help-field">
                      <span>Subject</span>
                      <select
                        value={helpForm.subject}
                        onChange={(event) => handleHelpFormChange('subject', event.target.value)}
                        required
                      >
                        <option value="">Select a topic</option>
                        {HELP_SUBJECT_OPTIONS.map((subject) => (
                          <option key={subject} value={subject}>
                            {subject}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="help-field">
                      <span>Message</span>
                      <textarea
                        value={helpForm.message}
                        onChange={(event) => handleHelpFormChange('message', event.target.value)}
                        required
                      />
                    </label>

                    <TurnstileWidget
                      siteKey={TURNSTILE_SITE_KEY}
                      resetSignal={turnstileResetSignal}
                      onTokenChange={(token) => {
                        setTurnstileToken(token);
                        if (token) {
                          setHelpError('');
                        }
                      }}
                      onError={() => setHelpError("We couldn't verify you're human. Please try again.")}
                    />

                    <button
                      type="submit"
                      className="button primary help-contact-submit"
                      disabled={helpSubmitting || !turnstileToken}
                    >
                      <HelpSendIcon />
                      <span>{helpSubmitting ? 'Sending...' : 'Send message'}</span>
                    </button>
                  </form>
                </section>
              </div>

              <section className="help-faq-card">
                <h2>Frequently Asked Questions</h2>
                <div className="help-faq-list">
                  {HELP_FAQS.map((faq) => {
                    const isOpen = expandedHelpFaqId === faq.id;

                    return (
                      <article className={`help-faq-item ${isOpen ? 'is-open' : ''}`} key={faq.id}>
                        <button
                          type="button"
                          className="help-faq-trigger"
                          onClick={() => setExpandedHelpFaqId((current) => (current === faq.id ? null : faq.id))}
                          aria-expanded={isOpen}
                        >
                          <span className="help-faq-trigger-main">
                            <span className={`help-topic-icon-shell help-faq-topic-icon is-${faq.topic}`} aria-hidden="true">
                              <HelpTopicIcon topic={faq.topic} />
                            </span>
                            <span>{faq.question}</span>
                          </span>
                          <HelpChevronIcon isOpen={isOpen} />
                        </button>
                        {isOpen && <p className="help-faq-answer">{faq.answer}</p>}
                      </article>
                    );
                  })}
                </div>
              </section>
            </section>
          )}

          {showConfirmation && (
            <section className="confirmation-screen">
              <div className="confirmation-progress-card">
                <ConfirmationStageProgress />
              </div>

              {globalError && (
                <section className="confirmation-status-card" role="alert">
                  <div className="confirmation-status-copy confirmation-status-copy-error">
                    <span className="confirmation-status-kicker">Return not found</span>
                    <h1>We couldn&apos;t find this return</h1>
                    <p>{globalError}</p>
                  </div>

                  <div className="button-row confirmation-action-row">
                    <button
                      type="button"
                      className="button primary"
                      onClick={() => {
                        navigate('/sign-in');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      Sign in
                    </button>
                    <button type="button" className="button ghost" onClick={resetToForm}>
                      Schedule a pickup
                    </button>
                  </div>
                </section>
              )}

              {!globalError && (!checkoutStatus || !isTerminalState(checkoutStatus.state)) && (
                <section
                  className={`confirmation-status-card is-pending${confirmationDelayed ? ' is-delayed' : ''}`}
                  role="status"
                  aria-live="polite"
                >
                  <div className="confirmation-status-icon-shell" aria-hidden="true">
                    <div className="confirmation-status-icon-ring" />
                    <div className="confirmation-status-icon-core">
                      <ConfirmationClockIcon />
                    </div>
                  </div>
                  <div className="confirmation-status-copy">
                    <h1>{confirmationDelayed ? 'Your booking is still processing' : 'Confirming your pickup'}</h1>
                    <p>
                      {confirmationDelayed
                        ? 'This is taking longer than expected. You can safely leave this page and check your return later.'
                        : 'Almost done - thanks for hanging tight.'}
                    </p>
                    <span className="confirmation-status-note">
                      {confirmationDelayed
                        ? 'We will keep checking at a lower frequency while this page is open.'
                        : confirmationConnectionIssue
                          ? 'Checking your booking status...'
                          : confirmationProgressMessage}
                    </span>
                  </div>
                  {confirmationDelayed && (
                    <div className="button-row confirmation-action-row">
                      <button
                        type="button"
                        className="button primary"
                        onClick={() => {
                          if (!auth.isAuthenticated) {
                            savePendingReturnDetailsId(route.returnId);
                          }
                          navigate(auth.isAuthenticated ? '/returns' : '/sign-in');
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        View my returns
                      </button>
                    </div>
                  )}
                </section>
              )}

              {checkoutStatus && isSuccessState(checkoutStatus.state) && (
                <section className="confirmation-success-screen">
                  <section className="confirmation-success-hero">
                    <div className="confirmation-success-icon-shell" aria-hidden="true">
                      <ConfirmationSuccessIcon />
                    </div>
                    <h1>Pickup confirmed!</h1>
                    <p>Your booking is complete</p>
                    <span className="confirmation-success-note">{confirmationEmailMessage}</span>
                  </section>

                  <section className="confirmation-booking-card">
                    <div className="confirmation-booking-reference">
                      <span>Return ID</span>
                      <strong>{resolveReturnId(checkoutStatus)}</strong>
                    </div>
                    <div className="confirmation-booking-actions">
                      <button type="button" className="confirmation-inline-action" onClick={() => window.print()}>
                        <ConfirmationGlyphIcon glyph="printer" className="confirmation-inline-action-icon" />
                        <span>Print</span>
                      </button>
                    </div>
                  </section>

                  <section className="confirmation-info-card is-next-steps">
                    <h2>What happens next?</h2>
                    <ol className="confirmation-next-steps">
                      {confirmationNextSteps.map((step, index) => (
                        <li
                          className={`confirmation-next-step ${
                            index === 0 ? 'is-current' : index < 3 ? 'is-active' : 'is-upcoming'
                          }`}
                          key={step.title}
                        >
                          <span
                            className={`confirmation-next-step-number ${
                              index === 0 ? 'is-current' : index < 3 ? 'is-active' : 'is-muted'
                            }`}
                          >
                            {index + 1}
                          </span>
                          <div className="confirmation-next-step-card">
                            <span className="confirmation-next-step-icon" aria-hidden="true">
                              <ConfirmationGlyphIcon glyph={step.glyph} />
                            </span>
                            <div className="confirmation-next-step-copy">
                              <strong>{step.title}</strong>
                              <p>{step.description}</p>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </section>

                  <section className="confirmation-info-card is-pickup-details">
                    <h2>Pickup details</h2>
                    <div className="confirmation-detail-stack">
                      <article className="confirmation-detail-card is-primary-row">
                        <span className="confirmation-detail-icon is-date" aria-hidden="true">
                          <ConfirmationGlyphIcon glyph="calendar" />
                        </span>
                        <div className="confirmation-detail-copy">
                          <span>Pickup date &amp; time</span>
                          <strong>{formatLongDate(checkoutStatus.pickupDate)}</strong>
                          <p>
                            Between {formatClockTime(checkoutStatus.window.readyTime)} -{' '}
                            {formatClockTime(checkoutStatus.window.closeTime)} local time
                          </p>
                          <small>{confirmationQuote?.estimatedServiceLevel ?? confirmationCarrierLabel}</small>
                        </div>
                      </article>

                      {confirmationForm.line1 && (
                        <article className="confirmation-detail-card is-primary-row">
                          <span className="confirmation-detail-icon is-location" aria-hidden="true">
                            <ConfirmationGlyphIcon glyph="pin" />
                          </span>
                          <div className="confirmation-detail-copy">
                            <span>Pickup location</span>
                            <strong>{confirmationForm.line1}</strong>
                            <p>
                              {confirmationForm.city}, {confirmationForm.state.toUpperCase()} {confirmationForm.postalCode}
                            </p>
                            <small>{confirmationForm.residential ? 'Residential' : 'Commercial'}</small>
                          </div>
                        </article>
                      )}

                      <article className="confirmation-detail-card is-compact-row">
                        <span className="confirmation-detail-icon is-contact" aria-hidden="true">
                          <ConfirmationGlyphIcon glyph="contact" />
                        </span>
                        <div className="confirmation-detail-copy">
                          <span>Contact</span>
                          <strong>{confirmationContactPrimary}</strong>
                          {confirmationContactSecondary && <p>{confirmationContactSecondary}</p>}
                        </div>
                      </article>
                    </div>
                  </section>

                  {(confirmationPackageCards.length > 0 || confirmationChargeRows.length > 0) && (
                    <section className="confirmation-info-card is-package-summary">
                      <h2>Package summary</h2>

                      {confirmationPackageCards.length > 0 && (
                        <div className="confirmation-package-list">
                          {confirmationPackageCards.map((packageCard, index) => {
                            const packageLabelText = packageCard.hasPrepaidLabel ? 'Prepaid label' : 'Label sent via email';

                            return (
                              <article className="confirmation-package-item" key={packageCard.id}>
                                <div className="confirmation-package-item-head">
                                  <strong>{getPackageNote(packageCard, index)}</strong>
                                  <span
                                    className={`confirmation-package-pill ${
                                      packageCard.hasPrepaidLabel ? 'is-prepaid' : 'is-generated'
                                    }`}
                                  >
                                    {packageLabelText}
                                  </span>
                                </div>

                                {hasCompletePackageInfo(packageCard) && (
                                  <div className="confirmation-package-meta-grid">
                                    <div className="confirmation-package-meta-item">
                                      <span>Weight</span>
                                      <p className="confirmation-package-value">{packageCard.weightLbs} lbs</p>
                                    </div>
                                    <div className="confirmation-package-meta-item">
                                      <span>Dimensions</span>
                                      <p className="confirmation-package-value">
                                        {packageCard.lengthIn} x {packageCard.widthIn} x {packageCard.heightIn} in
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {!packageCard.hasPrepaidLabel && packageCard.destinationLine1 && (
                                  <div className="confirmation-package-destination">
                                    <span>Destination</span>
                                    <strong>{packageCard.destinationLine1}</strong>
                                    <p className="confirmation-package-secondary-line">
                                      {packageCard.destinationCity}, {packageCard.destinationState.toUpperCase()}{' '}
                                      {packageCard.destinationPostalCode}
                                    </p>
                                  </div>
                                )}

                                {packageCard.hasPrepaidLabel && (
                                  <div className="confirmation-package-destination">
                                    <span>Label status</span>
                                    <strong>Prepaid label</strong>
                                    <p className="confirmation-package-secondary-line">
                                      {CARRIER_DISPLAY_LABEL[packageCard.carrier]} carrier pickup
                                    </p>
                                  </div>
                                )}
                              </article>
                            );
                          })}
                        </div>
                      )}

                      {confirmationChargeRows.length > 0 && (
                        <div className="confirmation-summary-breakdown">
                          {confirmationChargeRows.map((chargeRow) => (
                            <div
                              className={`confirmation-summary-row ${chargeRow.key === 'total' ? 'is-total' : ''}`}
                              key={chargeRow.key}
                            >
                              <span>{chargeRow.label}</span>
                              <strong>{formatCurrency(chargeRow.amount, chargeRow.currency)}</strong>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  )}

                  <div className="button-row confirmation-success-actions">
                    <button type="button" className="button primary confirmation-primary-action" onClick={resetToForm}>
                      Schedule another pickup
                    </button>
                    <button
                      type="button"
                      className="button ghost confirmation-secondary-action"
                      onClick={() => openManagePickup(resolveReturnId(checkoutStatus), checkoutStatus)}
                    >
                      View Return
                    </button>
                  </div>
                </section>
              )}

              {checkoutStatus && checkoutStatus.state === 'CANCELLED_PICKUP' && (
                <section className="confirmation-status-card">
                  <div className="confirmation-status-copy confirmation-status-copy-success">
                    <span className="confirmation-status-kicker">Pickup canceled</span>
                    <h1>Your pickup has been canceled</h1>
                    <p>If you still need a pickup, you can schedule a new one anytime.</p>
                  </div>

                  <div className="confirmation-reference">
                    <span>Return ID</span>
                    <strong>{resolveReturnId(checkoutStatus)}</strong>
                  </div>

                  <div className="button-row confirmation-action-row">
                    <button type="button" className="button primary" onClick={resetToForm}>
                      Schedule another pickup
                    </button>
                  </div>
                </section>
              )}

              {checkoutStatus && checkoutStatus.state === 'FAILED_SCHEDULING' && (
                <section className="confirmation-status-card">
                  <div className="confirmation-status-copy confirmation-status-copy-error">
                    <span className="confirmation-status-kicker">Booking issue</span>
                    <h1>We couldn&apos;t schedule this pickup</h1>
                    <p>You were not charged.</p>
                    <span className="confirmation-status-note">
                      Try another date, confirm your address, or try another carrier. {checkoutStatus.failureMessage ?? ''}
                    </span>
                  </div>

                  <div className="button-row confirmation-action-row">
                    <button type="button" className="button primary" onClick={resetToForm}>
                      Return to form
                    </button>
                  </div>
                </section>
              )}

              {checkoutStatus && checkoutStatus.state === 'QUOTE_EXPIRED' && (
                <section className="confirmation-status-card">
                  <div className="confirmation-status-copy confirmation-status-copy-error">
                    <span className="confirmation-status-kicker">Quote expired</span>
                    <h1>This quote is no longer available</h1>
                    <p>Your pickup was not scheduled. If a payment authorization exists, it is being canceled.</p>
                  </div>

                  <div className="button-row confirmation-action-row">
                    <button type="button" className="button primary" onClick={resetToForm}>
                      Get a new quote
                    </button>
                  </div>
                </section>
              )}

              {checkoutStatus &&
                (checkoutStatus.state === 'FAILED_PAYMENT' || checkoutStatus.state === 'CANCELLED_AUTH') && (
                  <section className="confirmation-status-card">
                    <div className="confirmation-status-copy confirmation-status-copy-error">
                      <span className="confirmation-status-kicker">Payment issue</span>
                      <h1>We couldn&apos;t complete payment</h1>
                      <p>Try another payment method.</p>
                    </div>

                    <div className="button-row confirmation-action-row">
                      <button type="button" className="button primary" onClick={resetToForm}>
                        Try again
                      </button>
                    </div>
                  </section>
                )}
            </section>
          )}

        </div>

      </main>

      <footer className="site-footer">
        <div className="site-footer-inner">
          <div className="footer-meta">
            <p>
              <a href={`mailto:${DEFAULT_SUPPORT_EMAIL}`}>{DEFAULT_SUPPORT_EMAIL}</a>
            </p>
            <p>{DEFAULT_SUPPORT_HOURS}</p>
          </div>
          <div className="footer-links">
            <a href="/how-it-works" onClick={(event) => navigateToPath(event, '/how-it-works')}>
              How it works
            </a>
            <a href="/refund-policy" onClick={(event) => navigateToPath(event, '/refund-policy')}>
              Refund Policy
            </a>
            <a href="/privacy" onClick={(event) => navigateToPath(event, '/privacy')}>
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>

    </div>
  );
}

export default App;
